import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import {
  StyleSheet,
  View,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
  TouchableOpacity,
  Alert,
  Animated,
  StatusBar,
  Dimensions,
  TextInput as RNTextInput,
  Modal,
  PanResponder,
} from 'react-native';
import { GestureDetector, Gesture } from 'react-native-gesture-handler';
import ReanimatedAnimated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import {
  TextInput,
  Text,
  IconButton,
  Avatar,
  Button,
} from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { Audio } from 'expo-av';
import io from 'socket.io-client';
import {
  AVATAR_TYPES,
  normalizeAvatar,
  DEFAULT_AVATAR_OPTION,
} from '../constants/avatars';

const MATERIAL_COMMUNITY_ICONS_TTF = require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf');

const RAILWAY_URL = 'https://radyohub-production.up.railway.app';
const SOCKET_URL = RAILWAY_URL;
const API_BASE_URL = `${RAILWAY_URL}/api`;
const EMOJI_LIST = [
  '😀','😁','😂','🤣','😅','😊','😍','😘','😎','🤩','🥳','🤗',
  '😇','🙂','🙃','😏','😌','😢','😭','😡','🤯','🤔','🤤','🤠',
  '👍','👎','🙏','👏','🙌','🔥','❤️','💙','💚','💛','💜','🖤',
  '🎉','🎶','🎧','🥰','😴','😜','🤪','😬','🤞','🤟','👌','💯'
];
const BATAK_SUITS = ['Maça', 'Kupa', 'Karo', 'Sinek'];

const DEFAULT_USER_PREFIX = 'Kullanıcı';

const normalizeDisplayName = (name) => {
  const trimmed = (name || '').trim();
  return trimmed.length > 0 ? trimmed : DEFAULT_USER_PREFIX;
};

const generateTemporaryGameName = (baseName = 'Oyuncu', existingPlayers = []) => {
  const existing = new Set(
    (existingPlayers || []).map((player) => (player?.name || '').toLowerCase())
  );
  const base = normalizeDisplayName(baseName).replace(/\s+/g, '-');
  for (let i = 0; i < 5; i += 1) {
    const candidate = `${base}-${Math.floor(1000 + Math.random() * 9000)}`;
    if (!existing.has(candidate.toLowerCase())) {
      return candidate;
    }
  }
  return `${base}-${Date.now().toString().slice(-4)}`;
};

const getCardSuitEmoji = (suit) => {
  switch (suit) {
    case 'Kupa': return '♥️';
    case 'Karo': return '♦️';
    case 'Maça': return '♠️';
    case 'Sinek': return '♣️';
    default: return '🃏';
  }
};

const getCardValueDisplay = (value) => {
  switch (value) {
    case 'Vale': return 'J';
    case 'Kız': return 'Q';
    case 'Papaz': return 'K';
    case 'As': return 'A';
    default: return value;
  }
};

const UNO_COLOR_STYLES = {
  red: '#E94560',
  yellow: '#F4B400',
  green: '#0FBF61',
  blue: '#4285F4',
  wild: '#9E9E9E',
};

const UNO_COLOR_LABELS = {
  red: 'Kırmızı',
  yellow: 'Sarı',
  green: 'Yeşil',
  blue: 'Mavi',
};

const getUnoCardText = (card) => {
  if (!card) return '';
  switch (card.value) {
    case 'skip':
      return '⏭️';
    case 'reverse':
      return '🔄';
    case 'draw-two':
      return '+2';
    case 'wild':
      return '🎨';
    case 'wild-draw-four':
      return '+4';
    default:
      return card.value;
  }
};

const getUnoCardSubtext = (card) => {
  if (!card) return '';
  switch (card.value) {
    case 'skip':
      return 'Atla';
    case 'reverse':
      return 'Ters';
    case 'draw-two':
      return '';
    case 'wild':
      return 'Vahşi';
    case 'wild-draw-four':
      return '';
    default:
      return '';
  }
};

// Global ses yönetimi - App.js ile aynı referansı kullan
// React Native'de global objesini kullanarak paylaşılan değişken oluşturuyoruz
if (typeof global.globalSoundInstance === 'undefined') {
  global.globalSoundInstance = null;
}
if (typeof global.isPlayingAudio === 'undefined') {
  global.isPlayingAudio = false; // Ses çalma işlemi devam ediyor mu?
}

// Memoized Batak Bid Input Component - render döngüsünden izole
// Input'u tamamen izole etmek için key prop kullanıyoruz
const BatakBidInput = memo(({ 
  inputRef, 
  defaultValue, 
  disabled, 
  onFocus, 
  onBlur,
  onKeyDown,
  inputKey,
}) => {
  if (Platform.OS !== 'web') {
    return null;
  }
  return (
    <input
      key={inputKey}
      ref={inputRef}
      type="number"
      min="1"
      max="13"
      defaultValue={defaultValue}
      disabled={disabled}
      placeholder="Teklif (1-13)"
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      autoFocus={false}
      style={{
        flex: 1,
        marginRight: 12,
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        padding: 12,
        fontSize: 16,
        backgroundColor: '#fff',
        minHeight: 48,
        borderStyle: 'solid',
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison: sadece disabled ve inputKey değişirse re-render
  return prevProps.disabled === nextProps.disabled &&
         prevProps.defaultValue === nextProps.defaultValue &&
         prevProps.inputKey === nextProps.inputKey;
});

BatakBidInput.displayName = 'BatakBidInput';

// Memoized Batak Cards Component - kartların render'ından input'u izole etmek için
const BatakCards = memo(({ 
  myHand, 
  phase, 
  isMyTurn, 
  isSubmittingBatakMove, 
  isMobile, 
  handlePlayBatakCard,
  currentPlayer,
  getCardValueDisplay,
  getCardSuitEmoji,
}) => {
  if (!myHand || myHand.length === 0) {
    return null;
  }
  
  if (phase !== 'PLAYING' && phase !== 'BIDDING' && phase !== 'CHOOSE_TRUMP') {
    return null;
  }

  return (
    <View style={styles.pistiHandContainer}>
      <Text style={styles.gameHudLabel}>Kartların ({myHand.length})</Text>
      <View style={styles.pistiHandRow}>
        {myHand.map((card, index) => (
          <TouchableOpacity
            key={`${card.suit}-${card.value}-${index}`}
            style={[
              styles.pistiCard,
              (phase === 'PLAYING' && isMyTurn && !isSubmittingBatakMove) ? styles.pistiCardPlayable : styles.pistiCardDisabled,
              Platform.OS === 'web' && isMobile && {
                width: 42,
                height: 59,
                minWidth: 38,
                minHeight: 55,
              },
            ]}
            disabled={phase !== 'PLAYING' || !isMyTurn || isSubmittingBatakMove}
            onPress={() => {
              if (phase === 'PLAYING' && typeof handlePlayBatakCard === 'function') {
                handlePlayBatakCard(card);
              }
            }}
          >
            <Text style={[styles.pistiCardValue, Platform.OS === 'web' && isMobile && { fontSize: 13 }]}>
              {getCardValueDisplay(card.value)}
            </Text>
            <Text style={[styles.pistiCardSuit, Platform.OS === 'web' && isMobile && { fontSize: 17 }]}>
              {getCardSuitEmoji(card.suit)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      {phase === 'PLAYING' && !isMyTurn && (
        <Text style={styles.batakHint}>
          {currentPlayer?.name ? `Hamle ${currentPlayer.name} oyuncusunda` : 'Hamle bekleniyor'}
        </Text>
      )}
      {phase === 'BIDDING' && (
        <Text style={styles.batakHint}>
          Kartlarını görüp teklif verebilirsin
        </Text>
      )}
    </View>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: sadece gerekli prop'lar değişirse re-render
  return (
    prevProps.phase === nextProps.phase &&
    prevProps.isMyTurn === nextProps.isMyTurn &&
    prevProps.isSubmittingBatakMove === nextProps.isSubmittingBatakMove &&
    prevProps.myHand?.length === nextProps.myHand?.length &&
    JSON.stringify(prevProps.myHand) === JSON.stringify(nextProps.myHand)
  );
});

BatakCards.displayName = 'BatakCards';

export default function ChatRoom({
  radio,
  room,
  onClose,
  userName = 'Kullanıcı',
  userAvatar = DEFAULT_AVATAR_OPTION,
  gameIntegration = null,
  initialGameState = null,
}) {
  // Mobil cihaz tespiti (web'de mobil cihazlar için)
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    if (Platform.OS === 'web') {
      const checkMobile = () => {
        const width = window.innerWidth;
        setIsMobile(width <= 768);
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    } else {
      const { width } = Dimensions.get('window');
      setIsMobile(width <= 768);
    }
  }, []);

  
  const [fontsLoaded, fontsError] = useFonts({
    MaterialCommunityIcons: MATERIAL_COMMUNITY_ICONS_TTF,
  });

  useEffect(() => {
    MaterialCommunityIcons.loadFont().catch((error) => {
      if (__DEV__) {
        console.warn('MaterialCommunityIcons font load failed:', error);
      }
    });
  }, []);

  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [recentReactions, setRecentReactions] = useState([]);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [sound, setSound] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameClockTick, setGameClockTick] = useState(Date.now());
  const [gameStateSnapshot, setGameStateSnapshot] = useState(initialGameState);
  const flatListRef = useRef(null);
  const inputRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const currentRadioIdRef = useRef(null); // Hangi radyo çalıyor
  const pistiJoinedRef = useRef(false); // Pişti odasına katıldı mı?
  const pistiStateRef = useRef(null); // Pişti state'inin güncel referansı (closure problemi için)
  const batakJoinedRef = useRef(false);
  const batakStateRef = useRef(null);
  const okeyJoinedRef = useRef(false);
  const okeyStateRef = useRef(null);
  const [myLastDiscardedTile, setMyLastDiscardedTile] = useState(null);
  const [isSubmittingOkeyMove, setIsSubmittingOkeyMove] = useState(false);
  const [selectedTiles, setSelectedTiles] = useState([]); // Seçili taşlar (seri oluşturma için)
  const [tileGroups, setTileGroups] = useState([]); // Taş grupları (seriler)
  const [draggedTileIndex, setDraggedTileIndex] = useState(null); // Sürüklenen taşın index'i
  const [orderedHand, setOrderedHand] = useState([]); // Sıralı taşlar (drag & drop için)
  const lastMyHandRef = useRef([]); // Son myHand referansı
  const dragStartIndexRef = useRef(null); // Drag başlangıç index'i
  const dragHoverIndexRef = useRef(null); // Drag sırasında hedef index (hover)
  const unoJoinedRef = useRef(false);
  const unoStateRef = useRef(null);
  const initialGameStateAppliedRef = useRef(false); // initialGameState sadece bir kez uygulandı mı?
  const [pistiNotification, setPistiNotification] = useState(null); // Pişti geri bildirimi
  const pistiNotificationAnim = useRef(new Animated.Value(0)).current;
  const pistiNotificationScale = pistiNotificationAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.8, 1.1, 1],
  });
  const newWordAnim = useRef(new Animated.Value(0)).current;
  const wordPulseScale = newWordAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.06],
  });
  const wordPulseOpacity = newWordAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.85],
  });
  const questionPulseOpacity = newWordAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.9],
  });
  const [sessionUserName, setSessionUserName] = useState(userName);
  const sessionUserNameRef = useRef(userName);
  const currentUserNameRef = useRef(userName);
  const batakBidInputRef = useRef(null);
  const isInputFocusedRef = useRef(false);
  const pendingGameStateRef = useRef(null);
  const [isGameIdentityReady, setIsGameIdentityReady] = useState(true);
  const [batakBidValue, setBatakBidValue] = useState('4');
  const [batakTrumpSuit, setBatakTrumpSuit] = useState('Maça');
  const [isSubmittingBatakMove, setIsSubmittingBatakMove] = useState(false);
  const [pendingWildCard, setPendingWildCard] = useState(null);
  const [isSubmittingUnoMove, setIsSubmittingUnoMove] = useState(false);
  const [showAiQuestionModal, setShowAiQuestionModal] = useState(false);
  const [hasAskedAiQuestion, setHasAskedAiQuestion] = useState(false);
  const [isAddingAiPlayer, setIsAddingAiPlayer] = useState(false);
  const hasAskedOkeyAiQuestionRef = useRef(false); // OKEY için özel AI question kontrolü
  const pistiAiPlayerAddedRef = useRef(false); // Pişti için AI oyuncu eklenme kontrolü
  const pistiModalOpenedRef = useRef(false); // Pişti için modal açılma kontrolü (race condition önlemek için)

  useEffect(() => {
    sessionUserNameRef.current = sessionUserName;
  }, [sessionUserName]);
  // Sadece geçerli bir room varsa odaya katıl, yoksa dinleyici odasına katılma
  // Dinleyici odası (radio-${radio._id}) sadece App.js'teki dinleyici kaydı için kullanılmalı
  const activeRoomId = room?.roomId || null;
  const normalizedUserAvatar = useMemo(() => normalizeAvatar(userAvatar), [userAvatar]);
  
  // Debug: gameStateSnapshot'ı logla (sadece Pişti için) - gameStateSnapshot tanımlandıktan SONRA
  const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
  const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
  const isBatak = gameType === 'batak' || radio._id === 'game-batak' || room?.roomId?.startsWith('batak-');
  const isUno = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
  const isOkey = false; // Okey oyunu kaldırıldı
  const isGameContext = isPisti || isBatak || isUno;
  


  // Handler fonksiyonlarını renderBatakGame'den önce tanımla (hoisting sorununu önlemek için)
  const handleStartBatakGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('Batak', 'Oda bilgisi bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/games/batak/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.roomId }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Oyun başlatılamadı' }));
        Alert.alert('Batak', error.error || 'Oyun başlatılamadı');
        return;
      }
      const stateResponse = await fetch(`${API_BASE_URL}/games/batak/state/${room.roomId}?playerName=${encodeURIComponent(currentUserNameRef.current || '')}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        setGameStateSnapshot(state);
        batakStateRef.current = state;
        initialGameStateAppliedRef.current = true;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('Batak oyun başlatma hatası:', error);
      Alert.alert('Batak', error.message || 'Oyun başlatılamadı');
    }
  }, [room?.roomId, gameIntegration]);

  const handleBatakInputFocus = useCallback(() => {
    isInputFocusedRef.current = true;
  }, []);

  const handleBatakInputBlur = useCallback(() => {
    // Blur'u geciktir - eğer gönder butonuna tıklanıyorsa blur'u engelle
    setTimeout(() => {
      // Eğer gönder butonuna focus geçiyorsa blur'u iptal et
      if (Platform.OS === 'web') {
        const activeElement = document.activeElement;
        if (activeElement && (activeElement.tagName === 'BUTTON' || activeElement.getAttribute('data-batak-submit-button'))) {
          return; // Gönder butonuna focus geçiyor, blur'u iptal et
        }
      }
      isInputFocusedRef.current = false;
      // Blur olduğunda queue'daki state'i uygula
      if (pendingGameStateRef.current) {
        if (Platform.OS === 'web') {
          console.log('[Web ChatRoom] Applying queued Batak state update');
        }
        setGameStateSnapshot(pendingGameStateRef.current);
        pendingGameStateRef.current = null;
      }
    }, 100);
  }, []);

  const handleBatakInputKeyDown = useCallback((e) => {
    // Enter tuşuna basıldığında gönder butonuna focus geç
    if (e.key === 'Enter') {
      e.preventDefault();
      // Gönder butonunu bul ve tıkla
      if (Platform.OS === 'web') {
        const submitButton = document.querySelector('[data-batak-submit-button]');
        if (submitButton) {
          submitButton.focus();
          setTimeout(() => {
            submitButton.click();
          }, 50);
        }
      }
    }
  }, []);

  const handleSubmitBatakBid = useCallback(async () => {
    if (!room?.roomId) {
      return;
    }
    // Input'tan değeri direkt al (uncontrolled)
    const inputValue = batakBidInputRef.current?.value || batakBidValue;
    const amount = Number(inputValue);
    if (!Number.isInteger(amount) || amount < 1 || amount > 13) {
      Alert.alert('Batak', 'Lütfen 1 ile 13 arasında bir değer girin.');
      return;
    }
    setIsSubmittingBatakMove(true);
    try {
      const response = await fetch(`${API_BASE_URL}/games/batak/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: currentUserNameRef.current || 'Anonim',
          move: { type: 'bid', amount },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Teklif gönderilemedi' }));
        Alert.alert('Batak', error.error || 'Teklif gönderilemedi');
      } else {
        // Başarılı olduysa input'u temizle
        if (batakBidInputRef.current) {
          batakBidInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Batak teklif hatası:', error);
      Alert.alert('Batak', error.message || 'Teklif gönderilemedi');
    } finally {
      setIsSubmittingBatakMove(false);
    }
  }, [room?.roomId, batakBidValue]);

  const handleSelectBatakTrump = useCallback(async () => {
    if (!room?.roomId) {
      return;
    }
    setIsSubmittingBatakMove(true);
    try {
      const response = await fetch(`${API_BASE_URL}/games/batak/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: currentUserNameRef.current || 'Anonim',
          move: { type: 'set-trump', suit: batakTrumpSuit },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Koz seçilemedi' }));
        Alert.alert('Batak', error.error || 'Koz seçilemedi');
      }
    } catch (error) {
      console.error('Batak koz seçimi hatası:', error);
      Alert.alert('Batak', error.message || 'Koz seçilemedi');
    } finally {
      setIsSubmittingBatakMove(false);
    }
  }, [room?.roomId, batakTrumpSuit]);

  const handlePlayBatakCard = useCallback(async (card) => {
    if (!room?.roomId || !card) {
      return;
    }
    setIsSubmittingBatakMove(true);
    try {
      const response = await fetch(`${API_BASE_URL}/games/batak/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: currentUserNameRef.current || 'Anonim',
          move: { type: 'play-card', card },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Hamle yapılamadı' }));
        Alert.alert('Batak', error.error || 'Hamle yapılamadı');
      }
    } catch (error) {
      console.error('Batak hamle hatası:', error);
      Alert.alert('Batak', error.message || 'Hamle yapılamadı');
    } finally {
      setIsSubmittingBatakMove(false);
    }
  }, [room?.roomId]);

  const renderBatakGame = useMemo(() => {
    if (!isBatak) {
      return null;
    }

    const normalizedBaseUserNameValue = normalizeDisplayName(userName);
    const currentUserNameValue = isGameContext ? sessionUserName : normalizedBaseUserNameValue;
    const players = gameStateSnapshot?.players || [];
    const status = gameStateSnapshot?.status || 'WAITING';
    const phase = gameStateSnapshot?.currentPhase || 'WAITING';
    const round = gameStateSnapshot?.round || 1;
    const trumpSuit = gameStateSnapshot?.trumpSuit || 'Maça';
    const leadingSuit = gameStateSnapshot?.leadingSuit || null;
    const currentTrick = gameStateSnapshot?.currentTrick || [];
    const winningBid = gameStateSnapshot?.winningBid || null;
    const winningBidderIndex = typeof gameStateSnapshot?.winningBidderIndex === 'number'
      ? gameStateSnapshot.winningBidderIndex
      : null;
    const currentPlayerIndex = typeof gameStateSnapshot?.currentPlayerIndex === 'number'
      ? gameStateSnapshot.currentPlayerIndex
      : null;

    const matchName = (a = '', b = '') => (
      a?.toLowerCase() === b?.toLowerCase() ||
      a?.toLowerCase().startsWith(b?.toLowerCase()) ||
      b?.toLowerCase().startsWith(a?.toLowerCase())
    );

    const currentPlayer = typeof currentPlayerIndex === 'number' ? players[currentPlayerIndex] : null;
    const myPlayer = players.find((player) => matchName(player.name, currentUserNameValue));
    const myHand = myPlayer?.hand || [];
    const isMyTurn = currentPlayer ? matchName(currentPlayer.name, currentUserNameValue) : false;
    const minPlayers = gameStateSnapshot?.minPlayers || 4;

    // İhale kazanan oyuncuyu kontrol et
    const isWinningBidder = typeof winningBidderIndex === 'number' && 
      players[winningBidderIndex] &&
      matchName(players[winningBidderIndex].name, currentUserNameValue);

    const trickInfo = currentTrick.length > 0
      ? currentTrick.map((entry) => `${entry.playerName}: ${getCardValueDisplay(entry.card?.value)} ${getCardSuitEmoji(entry.card?.suit)}`).join(' • ')
      : 'Henüz kart çıkılmadı';
    const trumpDisplay = gameStateSnapshot?.trumpSuit || 'Belirlenmedi';
    const isChoosingTrump = phase === 'CHOOSE_TRUMP';
    // CHOOSE_TRUMP fazında winningBidderIndex ile kontrol et, diğer fazlarda isMyTurn kullan
    const canSelectTrump = isChoosingTrump && (isWinningBidder || isMyTurn);
    // HUD çerçevesi için: CHOOSE_TRUMP fazında winningBidderIndex, diğer fazlarda isMyTurn
    const shouldHighlightBorder = (phase === 'PLAYING' || phase === 'BIDDING') ? isMyTurn :
      (phase === 'CHOOSE_TRUMP' ? isWinningBidder : false);

    const isMobileDevice = (Platform.OS === 'web' && isMobile) || (Platform.OS !== 'web');

    return (
      <View style={[
        styles.gameHudContainer, 
        shouldHighlightBorder && { borderColor: '#0FBF61' },
        isMobileDevice && styles.gameHudContainerMobile,
      ]}>
        <View style={styles.gameHudRow}>
          <View style={styles.gameHudColumn}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Durum</Text>
            <Text style={[
              styles.gameHudValue,
              isMobileDevice && styles.gameHudValueMobile,
            ]}>
              {status === 'WAITING'
                ? 'Oyuncu bekleniyor'
                : phase === 'BIDDING'
                ? 'İhale'
                : phase === 'PLAYING'
                ? 'Oyun devam ediyor'
                : status}
            </Text>
          </View>
          <View style={styles.gameHudColumn}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Tur</Text>
            <Text style={[
              styles.gameHudValue,
              isMobileDevice && styles.gameHudValueMobile,
            ]}>#{round}</Text>
          </View>
          <View style={styles.gameHudColumn}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Koz</Text>
            <Text style={[
              styles.gameHudValue,
              isMobileDevice && styles.gameHudValueMobile,
            ]}>
              {trumpDisplay}
            </Text>
          </View>
        </View>

        <View style={styles.gameHudRow}>
          <View style={[styles.gameHudColumn, { flex: 2 }]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Sıra</Text>
            <Text style={[
              styles.gameHudValue, 
              isMyTurn && { color: '#E94560', fontWeight: 'bold' },
              isMobileDevice && styles.gameHudValueMobile,
            ]}>
              {isMyTurn ? 'Sende' : (currentPlayer?.name || '—')}
            </Text>
          </View>
          <View style={styles.gameHudColumn}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Önce çıkan</Text>
            <Text style={[
              styles.gameHudValue,
              isMobileDevice && styles.gameHudValueMobile,
            ]}>{leadingSuit || '—'}</Text>
          </View>
        </View>

        {players.length > 0 && (
          <View style={[
            styles.gameLeaderboard,
            isMobileDevice && styles.gameLeaderboardMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Oyuncular</Text>
            {players.map((player, index) => (
              <View key={player.name || index} style={[
                styles.gameLeaderboardItem,
                isMobileDevice && styles.gameLeaderboardItemMobile,
              ]}>
                <Text style={[
                  styles.gameLeaderboardName,
                  isMobileDevice && styles.gameLeaderboardNameMobile,
                ]} numberOfLines={1}>
                  {player.name}
                  {index === currentPlayerIndex && phase !== 'WAITING' ? ' ▶️' : ''}
                  {index === winningBidderIndex && phase !== 'WAITING' && winningBid ? ' 🎯' : ''}
                </Text>
                <Text style={[
                  styles.gameLeaderboardScore,
                  isMobileDevice && styles.gameLeaderboardScoreMobile,
                ]}>
                  {player.score || 0} puan • Bid: {typeof player.bid === 'number' ? player.bid : '-'} • El: {player.tricksWon || 0}
                </Text>
              </View>
            ))}
          </View>
        )}

        <View style={[
          styles.batakTable,
          isMobileDevice && styles.batakTableMobile,
        ]}>
          <View style={styles.batakTableHeader}>
            <Text style={[
              styles.batakTableTitle,
              isMobileDevice && styles.batakTableTitleMobile,
            ]}>❄️ Yer</Text>
            <Text style={[
              styles.batakTableStatus,
              isMobileDevice && styles.batakTableStatusMobile,
            ]}>
              {currentTrick.length ? `${currentTrick.length}/${players.length}` : 'Boş'}
                </Text>
          </View>
          {currentTrick.length > 0 ? (
            <View style={[
              styles.batakTableCards,
              isMobileDevice && styles.batakTableCardsMobile,
            ]}>
              {currentTrick.map((entry) => (
                <View
                  key={`${entry.playerName}-${entry.card?.suit}-${entry.card?.value}`}
                  style={[
                    styles.batakTableCard,
                    isMobileDevice && styles.batakTableCardMobile,
                  ]}
                >
                  <Text style={[
                    styles.batakTablePlayer,
                    isMobileDevice && styles.batakTablePlayerMobile,
                  ]} numberOfLines={1}>
                    {entry.playerName}
                  </Text>
                  <Text style={[
                    styles.batakTableCardValue,
                    isMobileDevice && styles.batakTableCardValueMobile,
                  ]}>
                    {getCardValueDisplay(entry.card?.value)}
                  </Text>
                  <Text style={[
                    styles.batakTableCardSuit,
                    isMobileDevice && styles.batakTableCardSuitMobile,
                  ]}>
                    {getCardSuitEmoji(entry.card?.suit)}
                </Text>
              </View>
            ))}
          </View>
          ) : (
            <Text style={[
              styles.batakHint,
              isMobileDevice && styles.batakHintMobile,
            ]}>Yer boş</Text>
          )}
        </View>

        {winningBid && (
          <View style={[
            styles.gameAnnouncement,
            isMobileDevice && styles.gameAnnouncementMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>İhale</Text>
            <Text style={[
              styles.gameAnnouncementText,
              isMobileDevice && styles.gameAnnouncementTextMobile,
            ]}>
              {winningBid} el - {winningBidderIndex !== null ? players[winningBidderIndex]?.name : 'Belirsiz'}
            </Text>
          </View>
        )}

        {phase === 'CHOOSE_TRUMP' && (
          <View style={[
            styles.gameAnnouncement,
            isMobileDevice && styles.gameAnnouncementMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>
              {canSelectTrump ? 'Koz seçimi sende' : 'İhale sahibi koz seçiyor'}
            </Text>
            <View style={[
              styles.batakTrumpOptions,
              isMobileDevice && styles.batakTrumpOptionsMobile,
            ]}>
              {BATAK_SUITS.map((suit) => {
                const active = batakTrumpSuit === suit;
                return (
                  <TouchableOpacity
                    key={suit}
                    style={[
                      styles.batakTrumpOption,
                      active && styles.batakTrumpOptionActive,
                      isMobileDevice && styles.batakTrumpOptionMobile,
                    ]}
                    onPress={() => setBatakTrumpSuit(suit)}
                    disabled={!canSelectTrump || isSubmittingBatakMove}
                  >
                    <Text
                      style={[
                        styles.batakTrumpOptionText,
                        active && styles.batakTrumpOptionTextActive,
                        isMobileDevice && styles.batakTrumpOptionTextMobile,
                      ]}
                    >
                      {suit}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {canSelectTrump ? (
                <Button
                  mode="contained"
                onPress={handleSelectBatakTrump}
                  disabled={isSubmittingBatakMove}
                  buttonColor="#E94560"
                style={styles.startGameButton}
                >
                Kozu Onayla
                </Button>
            ) : (
              <Text style={styles.batakHint}>Koz seçimi bekleniyor...</Text>
            )}
          </View>
        )}

        {phase === 'BIDDING' && (
          <View style={[
            styles.gameAnnouncement,
            isMobileDevice && styles.gameAnnouncementMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>
              {isMyTurn
                ? 'Teklif sırası sende'
                : `Teklif sırası ${currentPlayer?.name || '—'} isimli oyuncuda`}
                  </Text>
            {!isMyTurn && (
              <Text style={[
                styles.batakHint,
                isMobileDevice && styles.batakHintMobile,
              ]}>İhaleyi bekle...</Text>
            )}
          </View>
        )}

        <BatakCards
          myHand={myHand}
          phase={phase}
          isMyTurn={isMyTurn}
          isSubmittingBatakMove={isSubmittingBatakMove}
          isMobile={isMobile}
          handlePlayBatakCard={handlePlayBatakCard}
          currentPlayer={currentPlayer}
          getCardValueDisplay={getCardValueDisplay}
          getCardSuitEmoji={getCardSuitEmoji}
        />

        {phase === 'WAITING' && (
          <View style={[
            styles.gameAnnouncement,
            isMobileDevice && styles.gameAnnouncementMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>
              {players.length >= minPlayers
                ? `Oyun başlamaya hazır (${players.length}/${minPlayers})`
                : `Başlamak için ${minPlayers} oyuncu gerekiyor (${players.length}/${minPlayers})`}
            </Text>
            {players.length >= minPlayers && (
              <Button
                mode="contained"
                onPress={() => {
                  if (typeof handleStartBatakGame === 'function') {
                    handleStartBatakGame();
                  }
                }}
                buttonColor="#E94560"
              style={isMobileDevice && styles.startGameButtonMobile}
              >
                Oyunu Başlat
              </Button>
            )}
          </View>
        )}
      </View>
    );
  }, [isBatak, gameStateSnapshot, isGameContext, sessionUserName, userName, batakTrumpSuit, isSubmittingBatakMove, isMobile, handleSelectBatakTrump, handleSubmitBatakBid, handlePlayBatakCard, handleStartBatakGame]);
  const normalizedBaseUserName = normalizeDisplayName(userName);
  const currentUserName = isGameContext ? sessionUserName : normalizedBaseUserName;

  useEffect(() => {
    currentUserNameRef.current = currentUserName;
  }, [currentUserName]);

  useEffect(() => {
    if (!isGameContext) {
      if (sessionUserNameRef.current !== normalizedBaseUserName) {
        setSessionUserName(normalizedBaseUserName);
      }
      setIsGameIdentityReady(true);
    }
  }, [isGameContext, normalizedBaseUserName]);

  useEffect(() => {
    if (room?.roomId) {
      setBatakBidValue('4');
      setBatakTrumpSuit('Maça');
    }
  }, [room?.roomId]);

  useEffect(() => {
    if (gameStateSnapshot?.trumpSuit) {
      setBatakTrumpSuit(gameStateSnapshot.trumpSuit);
    } else if (gameStateSnapshot?.currentPhase === 'CHOOSE_TRUMP') {
      setBatakTrumpSuit('Maça');
    }
  }, [gameStateSnapshot?.trumpSuit, gameStateSnapshot?.currentPhase]);

  if (Platform.OS === 'web' && (isPisti || isBatak)) {
    console.log('[Web ChatRoom] Component rendering with:', {
      radio: radio?.name,
      room: room?.roomId,
      roomName: room?.roomName,
      userName: currentUserName,
      gameStateSnapshot: gameStateSnapshot ? {
        status: gameStateSnapshot.status,
        playersCount: gameStateSnapshot.players?.length || 0,
        playerNames: gameStateSnapshot.players?.map(p => p.name) || [],
      } : null,
    });
  }

  useEffect(() => {
    if (!isGameContext || !room?.roomId) {
      setIsGameIdentityReady(true);
      return;
    }

    let cancelled = false;
    setIsGameIdentityReady(false);

    const ensureUniqueGameIdentity = async () => {
      try {
        const playerParam = `?playerName=${encodeURIComponent(normalizedBaseUserName)}`;
        let endpoint = null;

        if (isPisti) {
          endpoint = `${API_BASE_URL}/games/pisti/state/${room.roomId}${playerParam}`;
        } else if (isBatak) {
          endpoint = `${API_BASE_URL}/games/batak/state/${room.roomId}${playerParam}`;
        } else if (isUno) {
          endpoint = `${API_BASE_URL}/games/uno/state/${room.roomId}${playerParam}`;
        }

        if (!endpoint) {
          if (!cancelled) {
            setSessionUserName(normalizedBaseUserName);
            setIsGameIdentityReady(true);
          }
          return;
        }

        const response = await fetch(endpoint);

        if (!response.ok) {
          if (!cancelled) {
            setSessionUserName(normalizedBaseUserName);
          }
          return;
        }

        const state = await response.json();
        const players = state.players || [];
        const lowerBaseName = normalizedBaseUserName.toLowerCase();
        const nameTaken = players.some((player) => (player?.name || '').toLowerCase() === lowerBaseName);
        const isDefaultName = lowerBaseName === DEFAULT_USER_PREFIX.toLowerCase();

        if (!cancelled) {
          if (isDefaultName || nameTaken) {
            const alias = generateTemporaryGameName(normalizedBaseUserName, players);
            setSessionUserName(alias);
          } else {
            setSessionUserName(normalizedBaseUserName);
          }
        }
      } catch (error) {
        console.error('[Web ChatRoom] Game identity generation error:', error);
        if (!cancelled) {
          setSessionUserName(normalizedBaseUserName);
        }
      } finally {
        if (!cancelled) {
          setIsGameIdentityReady(true);
        }
      }
    };

    ensureUniqueGameIdentity();

    return () => {
      cancelled = true;
    };
  }, [isGameContext, isPisti, isBatak, isUno, room?.roomId, normalizedBaseUserName]);
  const isGameRoom = isPisti || isBatak || Boolean(gameIntegration?.isActive);

  // Initial game state effect - sadece ilk yüklemede kullan (prop değişikliklerini ignore et)
  // Bu, Socket.IO'dan gelen güncellemelerin prop'lardan gelen eski verilerle ezilmesini önler
  useEffect(() => {
    // Sadece ilk yüklemede ve eğer henüz uygulanmamışsa initialGameState'i kullan
    if (initialGameState && !initialGameStateAppliedRef.current) {
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] Initial game state uygulanıyor (sadece ilk yükleme):', {
          status: initialGameState?.status,
          playersCount: initialGameState?.players?.length || 0,
        });
      }
      if (initialGameState) {
        setGameStateSnapshot(initialGameState);
        initialGameStateAppliedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Boş dependency array - sadece mount'ta çalışır, prop değişikliklerini ignore et

  // New word animation effect - moved after isGameRoom, gameStateSnapshot, newWordAnim definitions
  useEffect(() => {
    if (!isGameRoom) {
      return;
    }
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isMysteryWordHunt = gameType === 'mystery-word-hunt' || radio._id === 'game-mystery-word-hunt';
    if (!isMysteryWordHunt) {
      return;
    }
    if (!gameStateSnapshot?.roundId) {
      return;
    }
    newWordAnim.stopAnimation();
    newWordAnim.setValue(0);
    Animated.sequence([
      Animated.timing(newWordAnim, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.spring(newWordAnim, {
        toValue: 0,
        friction: 6,
        useNativeDriver: true,
      }),
    ]).start();
  }, [isGameRoom, radio._id, room?.gameType, gameStateSnapshot?.roundId, newWordAnim]);

  // Game clock tick effect - moved after isGameRoom definition
  useEffect(() => {
    if (!isGameRoom) {
      return undefined;
    }
    // Gizemli Kelime Avı için: Soru gelene kadar sayacı başlatma
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isMysteryWordHunt = gameType === 'mystery-word-hunt' || radio._id === 'game-mystery-word-hunt';
    if (isMysteryWordHunt) {
      if (!gameStateSnapshot?.currentQuestion) {
        return undefined;
      }
    }
    const interval = setInterval(() => {
      setGameClockTick(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [isGameRoom, radio._id, room?.gameType, gameStateSnapshot?.currentQuestion]);

  // Game clock initialization effect - moved after isGameRoom definition
  useEffect(() => {
    if (!isGameRoom) {
      return;
    }
    // Gizemli Kelime Avı için: Soru gelene kadar sayacı başlatma
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isMysteryWordHunt = gameType === 'mystery-word-hunt' || radio._id === 'game-mystery-word-hunt';
    if (isMysteryWordHunt) {
      if (gameStateSnapshot?.roundEndsAt && gameStateSnapshot?.currentQuestion) {
        setGameClockTick(Date.now());
      }
    } else {
      if (gameStateSnapshot?.roundEndsAt) {
        setGameClockTick(Date.now());
      }
    }
  }, [isGameRoom, radio._id, room?.gameType, gameStateSnapshot?.roundEndsAt, gameStateSnapshot?.currentQuestion]);

  // Icon to emoji mapping (App.js ile aynı)
  const iconToEmoji = {
    'headphones': '🎧',
    'microphone-variant': '🎤',
    'record-player': '💿',
    'radio': '📻',
    'album': '💿',
    'guitar-electric': '🎸',
    'drum': '🥁',
    'music-note': '🎵',
    'music-circle': '🎶',
    'music-clef-treble': '🎼',
    'music-rest-whole': '🎵',
    'disc': '💿',
    'equalizer': '🎚️',
    'metronome': '⏱️',
    'cassette': '📼',
    'boombox': '📻',
    'tune-vertical': '🎛️',
    'rocket-launch': '🚀',
    'star-four-points': '⭐',
    'fire': '🔥',
    'turntable': '💿',
    'lightning-bolt': '⚡',
    'waveform': '〰️',
    'music-box': '🎵',
    'play-circle': '▶️',
    'music-note-plus': '🎵',
    'music-note-off': '🎵',
    'music-note-eighth': '🎵',
    'music-note-sixteenth': '🎵',
    'music-note-half': '🎵',
    'music-note-quarter': '🎵',
    'music-note-whole': '🎵',
    'music-note-multiple': '🎵',
    'music-circle-outline': '🎶',
    'karate': '🥋',
    'emoticon-cool-outline': '😎',
    'gamepad-variant': '🎮',
    'controller-classic': '🎮',
    'virtual-reality': '🥽',
    'robot-excited-outline': '🤖',
    'rocket-launch-outline': '🚀',
    'piano': '🎹',
    'violin': '🎻',
  };

  const getEmojiForIcon = (iconName) => {
    return iconToEmoji[iconName] || '🎵';
  };

  const renderAvatarBadge = (
    avatarData,
    size = 32,
    extraStyle = {},
    extraLabelStyle = {},
    fallbackLabel = '🙂'
  ) => {
    const normalized = normalizeAvatar(avatarData);

    if (normalized.type === AVATAR_TYPES.ICON) {
      const emoji = getEmojiForIcon(normalized.icon);
      return (
        <View
          style={[
            {
              width: size,
              height: size,
              borderRadius: size / 2,
              backgroundColor: normalized.backgroundColor,
              justifyContent: 'center',
              alignItems: 'center',
            },
            extraStyle,
          ]}
        >
          <Text style={{ fontSize: size * 0.5 }}>{emoji}</Text>
        </View>
      );
    }

    return (
      <Avatar.Text
        size={size}
        label={normalized.value || fallbackLabel}
        style={[{ backgroundColor: '#FFFFFF' }, extraStyle]}
        labelStyle={extraLabelStyle}
      />
    );
  };

  // Radyoyu çal - component mount olduğunda
  useEffect(() => {
    // Oyun odası ama gerçek radyo çalıyorsa (game- ile başlamıyorsa), radyo çalmaya devam et
    const isGameOnlyRadio = radio._id?.startsWith('game-');
    if (isGameRoom && isGameOnlyRadio) {
      return () => {
        if (sound) {
          sound.unloadAsync().catch(() => {});
          setSound(null);
        }
        setIsPlaying(false);
        currentRadioIdRef.current = null;
      };
    }

    const timer = setTimeout(() => {
      playRadio();
    }, 100);
    
    return () => {
      clearTimeout(timer);
      const cleanup = async () => {
        if (global.globalSoundInstance) {
          try {
            const status = await global.globalSoundInstance.getStatusAsync();
            if (status.isLoaded) {
              await global.globalSoundInstance.stopAsync();
              await global.globalSoundInstance.unloadAsync();
            }
          } catch (e) {
            console.error('Cleanup global ses hatası:', e);
          }
          global.globalSoundInstance = null;
        }
        if (sound) {
          try {
            const status = await sound.getStatusAsync();
            if (status.isLoaded) {
              await sound.stopAsync();
              await sound.unloadAsync();
            }
          } catch (e) {
            console.error('Cleanup local ses hatası:', e);
          }
          setSound(null);
        }
        setIsPlaying(false);
        currentRadioIdRef.current = null; // Radyo ID'sini temizle
      };
      cleanup();
    };
  }, [radio._id, isGameRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const playRadio = async () => {
    // Oyun odası ama gerçek radyo çalıyorsa (game- ile başlamıyorsa), radyo çalmaya devam et
    const isGameOnlyRadio = radio._id?.startsWith('game-');
    if (isGameRoom && isGameOnlyRadio) {
      return;
    }
    try {
      // Eğer başka bir ses çalma işlemi devam ediyorsa bekle
      while (global.isPlayingAudio) {
        console.log('Başka bir ses çalma işlemi devam ediyor (ChatRoom), bekleniyor...');
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Ses çalma işlemini başlat
      global.isPlayingAudio = true;
      
      // Aynı radyo zaten çalıyorsa hiçbir şey yapma
      if (currentRadioIdRef.current === radio._id && sound) {
        try {
          const status = await sound.getStatusAsync();
          if (status.isLoaded && status.isPlaying) {
            console.log('Aynı radyo zaten çalıyor (ChatRoom), tekrar başlatılmıyor:', radio.name);
            global.isPlayingAudio = false;
            return;
          }
        } catch (e) {
          // Status alınamazsa devam et
        }
      }
      
      // ÖNCE TÜM SESLERİ DURDUR - önemli: await ile bekle
      // Bu App.js'deki sesleri de durduracak
      console.log('Tüm sesler durduruluyor (ChatRoom)...');
      if (global.globalSoundInstance) {
        try {
          const status = await global.globalSoundInstance.getStatusAsync();
          if (status.isLoaded) {
            await global.globalSoundInstance.stopAsync();
            await global.globalSoundInstance.unloadAsync();
          }
        } catch (e) {
          console.error('Global ses durdurma hatası (ChatRoom):', e);
        }
        global.globalSoundInstance = null;
      }
      if (sound) {
        try {
          const status = await sound.getStatusAsync();
          if (status.isLoaded) {
            await sound.stopAsync();
            await sound.unloadAsync();
          }
        } catch (e) {
          console.error('Local ses durdurma hatası (ChatRoom):', e);
        }
        setSound(null);
      }
      
      // Ses tamamen durduktan sonra bekle - daha uzun bekleme
      await new Promise(resolve => setTimeout(resolve, 300));

      let streamUrl = radio.streamUrl?.trim();
      if (!streamUrl) {
        global.isPlayingAudio = false; // Flag'i temizle
        return;
      }

      // Web'de HTTP stream URL'lerini backend proxy üzerinden serve et (Mixed Content sorununu önlemek için)
      if (Platform.OS === 'web' && streamUrl.startsWith('http://')) {
        // Backend proxy üzerinden stream'i serve et (HLS ve non-HLS için)
        const encodedUrl = encodeURIComponent(streamUrl);
        streamUrl = `${RAILWAY_URL}/api/stream-proxy?url=${encodedUrl}`;
        console.log('[Web ChatRoom] HTTP stream URL proxied through backend:', streamUrl);
      }

      const { sound: newSound } = await Audio.Sound.createAsync(
        { uri: streamUrl },
        { shouldPlay: true }
      );
      
      // Global ve local ses referanslarını güncelle
      global.globalSoundInstance = newSound;
      setSound(newSound);
      setIsPlaying(true);
      currentRadioIdRef.current = radio._id; // Hangi radyo çalıyor kaydet
      
      // Ses çalma işlemi tamamlandı
      global.isPlayingAudio = false;
      console.log('Radyo başarıyla çalıyor (ChatRoom):', radio.name);
    } catch (error) {
      console.error('Radyo çalma hatası (ChatRoom):', error);
      global.isPlayingAudio = false; // Hata durumunda da flag'i temizle
    }
  };

  const stopRadio = async () => {
      // TÜM sesleri durdur
      if (global.globalSoundInstance) {
        try {
          await global.globalSoundInstance.unloadAsync();
        } catch (e) {
          console.error('Global ses durdurma hatası (ChatRoom):', e);
        }
        global.globalSoundInstance = null;
      }
    if (sound) {
      try {
        await sound.unloadAsync();
      } catch (e) {
        console.error('Local ses durdurma hatası (ChatRoom):', e);
      }
      setSound(null);
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    // Socket.IO bağlantısı
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });

    let joinRetryTimer = null;

    newSocket.on('connect', () => {
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] Socket.IO bağlandı, activeRoomId:', activeRoomId);
      } else {
        console.log('Socket.IO bağlandı');
      }
      setIsConnected(true);
      
      const attemptJoinRoom = () => {
        if (!activeRoomId) {
          if (Platform.OS === 'web') {
            console.error('[Web ChatRoom] Geçerli bir roomId yok, odaya katılamıyor');
          } else {
            console.warn('ChatRoom: Geçerli bir roomId yok, odaya katılamıyor');
          }
          setIsConnected(false);
          newSocket.disconnect();
          return;
        }

        if (isGameContext && (!isGameIdentityReady || !currentUserNameRef.current)) {
          if (Platform.OS === 'web') {
            console.log('[Web ChatRoom] Oyun kimliği hazırlanıyor, katılım ertelendi...');
          }
          joinRetryTimer = setTimeout(attemptJoinRoom, 150);
          return;
        }

        if (Platform.OS === 'web') {
          console.log('[Web ChatRoom] Joining room:', activeRoomId, 'radioId:', radio._id, 'as', currentUserNameRef.current);
        }

        newSocket.emit('join-room', {
          roomId: activeRoomId,
          radioId: radio._id,
          userName: currentUserNameRef.current || 'Anonim',
          avatar: normalizedUserAvatar,
        });
      };

      attemptJoinRoom();
    });

    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;

    newSocket.on('disconnect', (reason) => {
      console.log('Socket.IO bağlantısı kesildi:', reason);
      setIsConnected(false);
      reconnectAttempts = 0;
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('Socket.IO yeniden bağlandı, deneme:', attemptNumber);
      reconnectAttempts = attemptNumber;
      setIsConnected(true);
      // Reconnect olduğunda odaya tekrar katıl
      if (activeRoomId && currentUserNameRef.current && reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          newSocket.emit('join-room', {
            roomId: activeRoomId,
            radioId: radio._id,
            userName: currentUserNameRef.current || 'Anonim',
            avatar: normalizedUserAvatar,
          });
        }, 100);
      }
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Socket.IO yeniden bağlanma denemesi:', attemptNumber);
      if (attemptNumber > MAX_RECONNECT_ATTEMPTS) {
        console.warn('Socket.IO maksimum yeniden bağlanma denemesi aşıldı');
        newSocket.disconnect();
      }
    });

    newSocket.on('room-error', (error) => {
      console.error('[ChatRoom] Room error:', error);
      if (error.error === 'GAME_ALREADY_STARTED') {
        Alert.alert('Oyun Başlamış', error.message || 'Bu oyun başlamış, yeni oyuncu kabul edilmiyor.');
        // Odayı kapat ve geri dön
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      } else {
        Alert.alert('Hata', error.message || 'Odaya katılamadı');
      }
    });

    // ÖNEMLİ: Backend'den atanan yeni ismi al ve güncelle
    newSocket.on('player-name-assigned', ({ originalName, assignedName, roomId }) => {
      if (Platform.OS === 'web') {
        console.log(`[Web ChatRoom] Backend'den isim atandı: ${originalName} -> ${assignedName}`);
      }
      // Eğer bu odaya ait ise ve mevcut kullanıcının ismi ise, güncelle
      if (roomId === activeRoomId && (originalName === currentUserNameRef.current || originalName === userName)) {
        setSessionUserName(assignedName);
        currentUserNameRef.current = assignedName;
        if (Platform.OS === 'web') {
          console.log(`[Web ChatRoom] currentUserName güncellendi: ${assignedName}`);
        }
      }
    });

    newSocket.on('room-joined', (data) => {
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] Odaya katıldı, messages:', data.messages?.length || 0, 'participants:', data.participants?.length || 0);
      } else {
        console.log('Odaya katıldı:', data);
      }
      setMessages(data.messages || []);
      setParticipants(data.participants || []);
      
      // Pişti oyun odası ise, state'i hemen yükle
      const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
      const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
      const isBatak = gameType === 'batak' || radio._id === 'game-batak';
      const isUnoGame = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
      const isOkeyGame = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
      
      if (Platform.OS === 'web') {
        console.log('[OKEY] room-joined event kontrolü:', {
          dataRoomId: data.roomId,
          roomRoomId: room?.roomId,
          radioId: radio._id,
          gameType,
          isOkeyGame,
          roomType: room?.gameType,
        });
      }
      
      if (isPisti && data.roomId) {
        // Socket.IO join işlemi tamamlandı, şimdi Pişti state'ini yükle
        // Backend'de zaten Pişti join işlemi yapıldı, sadece state'i yükle
        setTimeout(() => {
          const loadPistiState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${data.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                if (Platform.OS === 'web') {
                  console.log('[Web ChatRoom] Pişti state yüklendi (room-joined):', {
                    roomId: data.roomId,
                    players: state.players?.length || 0,
                    playerNames: state.players?.map(p => p.name) || [],
                    status: state.status,
                  });
                }
                setGameStateSnapshot(state);
                // Ref'i güncelle
                pistiStateRef.current = state;
                // initialGameState artık uygulandı, prop değişikliklerini ignore et
                initialGameStateAppliedRef.current = true;
                
                // AI oyuncu kontrolü - ref'i güncelle
                // ÖNEMLİ: isAi kontrolünü sıkı yap (undefined, null, false kontrolü)
                const aiPlayers = state.players?.filter(p => p.isAi === true) || [];
                if (aiPlayers.length > 0) {
                  console.log(`[Pişti] AI oyuncu tespit edildi (room-joined): ${aiPlayers.length} AI oyuncu, ref güncellendi`);
                  pistiAiPlayerAddedRef.current = true;
                }
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (Pişti için)
                // ÖNEMLİ: Modal zaten açık mı kontrol et (ref ile race condition önle)
                if (state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedAiQuestion &&
                    !pistiAiPlayerAddedRef.current &&
                    !pistiModalOpenedRef.current &&
                    !showAiQuestionModal &&
                    state.players[0]?.name === currentUserName) {
                  console.log('[Pişti] Modal açılma koşulları sağlandı (room-joined), modal açılıyor');
                  pistiModalOpenedRef.current = true; // Ref'i hemen işaretle (race condition önlemek için)
                  setShowAiQuestionModal(true);
                } else {
                  console.log('[Pişti] Modal açılma koşulları sağlanmadı (room-joined):', {
                    status: state.status,
                    playersLength: state.players?.length,
                    hasAskedAiQuestion,
                    pistiAiPlayerAddedRef: pistiAiPlayerAddedRef.current,
                    pistiModalOpenedRef: pistiModalOpenedRef.current,
                    showAiQuestionModal,
                    firstPlayerName: state.players[0]?.name,
                    currentUserName
                  });
                }
                
                // Periyodik yükleme kaldırıldı - sadece Socket.IO event'lerine güveniyoruz
                // App.js'e bildirme - room-joined sadece ilk yükleme, kritik değil
              } else {
                console.error('[Web ChatRoom] Pişti state yükleme hatası - response not ok:', stateResponse.status);
              }
            } catch (error) {
              console.error('Pişti state yükleme hatası (room-joined):', error);
            }
          };
          loadPistiState();
        }, 300);
      }
      

      if (isBatak && data.roomId) {
          setTimeout(() => {
          const loadBatakState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/batak/state/${data.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                batakStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (Batak için)
                if (state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedAiQuestion &&
                    state.players[0]?.name === currentUserName) {
                  setShowAiQuestionModal(true);
                }
              }
            } catch (error) {
              console.error('Batak state yükleme hatası (room-joined):', error);
            }
          };
          loadBatakState();
        }, 300);
      }

      if (isUnoGame && data.roomId) {
        setTimeout(() => {
          const loadUnoState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${data.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                unoStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (UNO için)
                if (state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedAiQuestion &&
                    state.players[0]?.name === currentUserName) {
                  setShowAiQuestionModal(true);
                }
              }
            } catch (error) {
              console.error('UNO state yükleme hatası (room-joined):', error);
            }
          };
          loadUnoState();
        }, 300);
      }

      if (isOkeyGame && data.roomId) {
        // Yeni oda için AI question ref'ini sıfırla
        hasAskedOkeyAiQuestionRef.current = false;
        if (Platform.OS === 'web') {
          console.log('[OKEY] Yeni oda için AI question ref sıfırlandı:', data.roomId);
        }
        setTimeout(() => {
          const loadOkeyState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${data.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (OKEY için)
                // Creator otomatik eklendiği için, creator tek başına olduğunda modal göster
                const playerNameToCheck = currentUserNameRef.current || currentUserName || '';
                if (Platform.OS === 'web') {
                  console.log('[OKEY] AI question modal kontrolü (room-joined):', {
                    status: state.status,
                    playersCount: state.players?.length || 0,
                    creator: state.creator,
                    playerNameToCheck,
                    hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                    players: state.players?.map(p => p.name) || [],
                    playerInState: state.players[0]?.name || '',
                  });
                }
                const isCreator = state.creator && (
                  state.creator.toLowerCase() === playerNameToCheck.toLowerCase() ||
                  state.creator.toLowerCase().startsWith(playerNameToCheck.toLowerCase()) ||
                  playerNameToCheck.toLowerCase().startsWith(state.creator.toLowerCase())
                );
                const isFirstPlayer = state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedOkeyAiQuestionRef.current &&
                    isCreator;
                if (Platform.OS === 'web') {
                  console.log('[OKEY] AI question modal kontrolü sonucu (room-joined):', {
                    isFirstPlayer,
                    isCreator,
                    status: state.status,
                    playersCount: state.players?.length || 0,
                    hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                    creatorCheck: {
                      creator: state.creator,
                      playerNameToCheck,
                      creatorLower: state.creator?.toLowerCase(),
                      playerNameToCheckLower: playerNameToCheck.toLowerCase(),
                      exactMatch: state.creator?.toLowerCase() === playerNameToCheck.toLowerCase(),
                      creatorStartsWith: state.creator?.toLowerCase().startsWith(playerNameToCheck.toLowerCase()),
                      playerStartsWith: playerNameToCheck.toLowerCase().startsWith(state.creator?.toLowerCase()),
                    },
                  });
                }
                if (isFirstPlayer) {
                  // İsim eşleşmesi kontrolü (esnek)
                  const playerName = state.players[0]?.name || '';
                  const nameMatches = playerName.toLowerCase() === playerNameToCheck.toLowerCase() ||
                    playerName.toLowerCase().startsWith(playerNameToCheck.toLowerCase()) ||
                    playerNameToCheck.toLowerCase().startsWith(playerName.toLowerCase());
                  if (Platform.OS === 'web') {
                    console.log('[OKEY] isFirstPlayer=true, nameMatches kontrolü:', {
                      playerName,
                      playerNameToCheck,
                      nameMatches,
                      playerNameLower: playerName.toLowerCase(),
                      playerNameToCheckLower: playerNameToCheck.toLowerCase(),
                    });
                  }
                  if (nameMatches) {
                    if (Platform.OS === 'web') {
                      console.log('[OKEY] AI question modal gösteriliyor (room-joined):', {
                        creator: state.creator,
                        playerName,
                        playerNameToCheck,
                        playersCount: state.players.length,
                      });
                    }
                    // Ref'i modal gösterilmeden önce true yapma, modal gerçekten gösterildiğinde yapılacak
                    setShowAiQuestionModal(true);
                    if (Platform.OS === 'web') {
                      console.log('[OKEY] setShowAiQuestionModal(true) çağrıldı (room-joined)');
                    }
                  } else {
                    if (Platform.OS === 'web') {
                      console.log('[OKEY] AI question modal gösterilmedi - isim eşleşmedi (room-joined):', {
                        playerName,
                        playerNameToCheck,
                      });
                    }
                  }
                } else {
                  if (Platform.OS === 'web') {
                    console.log('[OKEY] AI question modal gösterilmedi - isFirstPlayer=false:', {
                      status: state.status,
                      playersCount: state.players?.length || 0,
                      hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                      isCreator,
                    });
                  }
                }
              }
            } catch (error) {
              console.error('OKEY state yükleme hatası (room-joined):', error);
            }
          };
          loadOkeyState();
        }, 300);
      }
    });

    newSocket.on('new-message', (message) => {
      setMessages(prev => [...prev, message]);
      // Mesaj listesini en alta kaydır
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    });

    newSocket.on('new-reaction', (reaction) => {
      addReaction(reaction);
    });

    newSocket.on('user-joined', (user) => {
      setParticipants(prev => {
        if (prev.some(p => p.socketId === user.socketId)) {
          return prev;
        }
        return [...prev, user];
      });
      
      // Pişti oyun odası ise, yeni kullanıcı katıldığında state'i güncelle
      const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
      const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
      const isBatak = gameType === 'batak' || radio._id === 'game-batak';
      const isUnoGame = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
      const isOkeyGame = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
      
      if (isPisti && activeRoomId) {
        setTimeout(() => {
          const loadPistiState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                if (Platform.OS === 'web') {
                  console.log('[Web ChatRoom] Pişti state yüklendi (user-joined):', {
                    roomId: activeRoomId,
                    players: state.players?.length || 0,
                    playerNames: state.players?.map(p => p.name) || [],
                    newUser: user.userName,
                  });
                }
                setGameStateSnapshot(state);
                // Ref'i güncelle
                pistiStateRef.current = state;
                // initialGameState artık uygulandı, prop değişikliklerini ignore et
                initialGameStateAppliedRef.current = true;
                
                // Periyodik yükleme kaldırıldı - sadece Socket.IO event'lerine güveniyoruz
                // App.js'e bildirme - user-joined sadece state yükleme, kritik değil
              }
            } catch (error) {
              console.error('Pişti state yükleme hatası (user-joined):', error);
            }
          };
          loadPistiState();
        }, 300);
      }
      
      if (isBatak && activeRoomId) {
        setTimeout(() => {
          const loadBatakState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/batak/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                batakStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (Batak için)
                if (state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedAiQuestion &&
                    state.players[0]?.name === currentUserName) {
                  setShowAiQuestionModal(true);
                }
              }
            } catch (error) {
              console.error('Batak state yükleme hatası (user-joined):', error);
            }
          };
          loadBatakState();
        }, 300);
      }

      if (isUnoGame && activeRoomId) {
        setTimeout(() => {
          const loadUnoState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                unoStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
              }
            } catch (error) {
              console.error('UNO state yükleme hatası (user-joined):', error);
            }
          };
          loadUnoState();
        }, 300);
      }

      if (isOkeyGame && activeRoomId) {
        setTimeout(() => {
          const loadOkeyState = async () => {
            try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                setGameStateSnapshot(state);
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
                
                // İlk oyuncu kontrolü - yapay zeka sorusu (OKEY için)
                // Creator otomatik eklendiği için, creator tek başına olduğunda modal göster
                const playerNameToCheck = currentUserNameRef.current || currentUserName || '';
                const isCreator = state.creator && (
                  state.creator.toLowerCase() === playerNameToCheck.toLowerCase() ||
                  state.creator.toLowerCase().startsWith(playerNameToCheck.toLowerCase()) ||
                  playerNameToCheck.toLowerCase().startsWith(state.creator.toLowerCase())
                );
                const isFirstPlayer = state.status === 'WAITING' && 
                    state.players?.length === 1 && 
                    !hasAskedOkeyAiQuestionRef.current &&
                    isCreator;
                if (isFirstPlayer) {
                  // İsim eşleşmesi kontrolü (esnek)
                  const playerName = state.players[0]?.name || '';
                  const nameMatches = playerName.toLowerCase() === playerNameToCheck.toLowerCase() ||
                    playerName.toLowerCase().startsWith(playerNameToCheck.toLowerCase()) ||
                    playerNameToCheck.toLowerCase().startsWith(playerName.toLowerCase());
                  if (nameMatches) {
                    if (Platform.OS === 'web') {
                      console.log('[OKEY] AI question modal gösteriliyor (user-joined):', {
                        creator: state.creator,
                        playerName,
                        playerNameToCheck,
                        playersCount: state.players.length,
                      });
                    }
                    setShowAiQuestionModal(true);
                  }
                }
              }
            } catch (error) {
              console.error('OKEY state yükleme hatası (user-joined):', error);
            }
          };
          loadOkeyState();
        }, 300);
      }
    });

    newSocket.on('user-left', (user) => {
      setParticipants(prev => prev.filter(p => p.socketId !== user.socketId));
      const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
      const isBatak = gameType === 'batak' || radio._id === 'game-batak';
      const isUnoGame = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
      if (isBatak && activeRoomId) {
        setTimeout(() => {
          fetch(`${API_BASE_URL}/games/batak/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((state) => {
              if (state) {
                setGameStateSnapshot(state);
                batakStateRef.current = state;
                initialGameStateAppliedRef.current = true;
              }
            })
            .catch((error) => {
              console.error('Batak state yükleme hatası (user-left):', error);
            });
        }, 300);
      }

      if (isUnoGame && activeRoomId) {
        setTimeout(() => {
          fetch(`${API_BASE_URL}/games/uno/state/${activeRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((state) => {
              if (state) {
                setGameStateSnapshot(state);
                unoStateRef.current = state;
                initialGameStateAppliedRef.current = true;
              }
            })
            .catch((error) => {
              console.error('UNO state yükleme hatası (user-left):', error);
            });
        }, 300);
      }
    });

    newSocket.on('clear-messages', (data) => {
      if (data.roomId === activeRoomId) {
        setMessages([]);
        console.log('Mesajlar temizlendi:', data.roomId);
      }
    });

    const handleGameUpdate = async (payload) => {
      // Oyun tipini belirle
      const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
      const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
      const isBatak = gameType === 'batak' || radio._id === 'game-batak';
      const isUnoGame = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
      const isOkeyGame = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
      
      // Input focus olduğunda Batak state güncellemelerini ignore et (render'ı engelle)
      if (isBatak && isInputFocusedRef.current) {
      if (Platform.OS === 'web') {
          console.log('[Web ChatRoom] Batak update ignored - input is focused');
        }
        return;
      }
      
      if (Platform.OS === 'web') {
        const gameName = isPisti ? 'Pişti' : isBatak ? 'Batak' : isUnoGame ? 'UNO' : isOkeyGame ? 'OKEY' : 'Game';
        console.log(`[Web ChatRoom] ${gameName} game update received:`, {
          reason: payload?.reason,
          players: payload?.players?.length || 0,
          playerNames: payload?.players?.map(p => p.name) || [],
          status: payload?.status,
          roomId: payload?.roomId,
          fullPayload: payload,
        });
      }
      
      // Pişti oyunu için, state güncellemesi geldiğinde kartları içeren tam state'i yükle
      
      if (isPisti && payload?.roomId) {
        // ÖNEMLİ: Oyuncu sayısı değişikliğini kontrol et
        const currentState = pistiStateRef.current;
        const newPlayerCount = payload?.players?.length || 0;
        const currentPlayerCount = currentState?.players?.length || 0;
        
        if (Platform.OS === 'web') {
          console.log('[Web ChatRoom] Oyuncu sayısı kontrolü:', {
            currentPlayerCount,
            newPlayerCount,
            reason: payload?.reason,
            currentPlayers: currentState?.players?.map(p => p.name) || [],
            newPlayers: payload?.players?.map(p => p.name) || [],
          });
        }
        // Pişti yapıldı mı kontrol et
        if (payload?.reason === 'PISTI_MADE') {
          const pistiPlayerName = payload?.playerName || payload?.message?.split(' ')[0];
          const isMyPisti = pistiPlayerName === currentUserName || 
                           (pistiPlayerName?.toLowerCase() === currentUserName?.toLowerCase()) ||
                           (pistiPlayerName?.toLowerCase().startsWith(currentUserName?.toLowerCase())) ||
                           (currentUserName?.toLowerCase().startsWith(pistiPlayerName?.toLowerCase()));
          
          // Vale ile Pişti mi yoksa normal Pişti mi?
          const isValePisti = payload?.pistiType === 'VALE_PISTI';
          
          let message;
          if (isValePisti) {
            message = isMyPisti ? 'Vale ile Piştiiii! 🎉🎉' : `${pistiPlayerName} Vale ile Pişti yaptı! 🎉🎉`;
          } else {
            message = isMyPisti ? 'Piştiiii! 🎉' : `${pistiPlayerName} Pişti yaptı! 🎉`;
          }
          
          // Animasyonlu geri bildirim göster
          setPistiNotification({ message, isMyPisti });
          pistiNotificationAnim.setValue(0);
          Animated.sequence([
            Animated.timing(pistiNotificationAnim, {
              toValue: 1,
              duration: 300,
              useNativeDriver: true,
            }),
            Animated.delay(2500), // Vale ile Pişti için biraz daha uzun göster
            Animated.timing(pistiNotificationAnim, {
              toValue: 0,
              duration: 300,
              useNativeDriver: true,
            }),
          ]).start(() => {
            setPistiNotification(null);
          });
        }
        
        // Yeri kesti bildirimi
        if (payload?.reason === 'CARDS_COLLECTED') {
          const playerName = payload?.playerName || payload?.message?.split(' ')[0];
          const isMyMove = playerName === currentUserName || 
                          (playerName?.toLowerCase() === currentUserName?.toLowerCase()) ||
                          (playerName?.toLowerCase().startsWith(currentUserName?.toLowerCase())) ||
                          (currentUserName?.toLowerCase().startsWith(playerName?.toLowerCase()));
          
          const message = payload?.message || `${playerName} yeri kesti!`;
          
          // Animasyonlu geri bildirim göster
          setPistiNotification({ message, isMyPisti: isMyMove });
          pistiNotificationAnim.setValue(0);
          Animated.sequence([
            Animated.timing(pistiNotificationAnim, {
              toValue: 1,
              duration: 300,
              useNativeDriver: true,
            }),
            Animated.delay(2000),
            Animated.timing(pistiNotificationAnim, {
              toValue: 0,
              duration: 300,
              useNativeDriver: true,
            }),
          ]).start(() => {
            setPistiNotification(null);
          });
        }
        
        // Pişti state güncellemesi geldi - playerName ile tam state'i yükle
        // Socket.IO event'i sadece sanitized state gönderiyor, kartları görmek için API'den yükle
        // Periyodik yükleme kaldırıldı - sadece Socket.IO event'lerine güveniyoruz
        const loadPistiStateFromEvent = async () => {
          try {
              const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${payload.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
            if (stateResponse.ok) {
              const state = await stateResponse.json();
              const currentState = pistiStateRef.current;
              
              // ÖNEMLİ: Oyuncu sayısı değişikliğini kontrol et
              const currentPlayerCount = currentState?.players?.length || 0;
              const newPlayerCount = state.players?.length || 0;
              const playersChanged = currentPlayerCount !== newPlayerCount;
              
              // Oyun durumu geriye dönüşünü engelle - AMA oyuncu sayısı değiştiyse veya PLAYER_JOINED/PLAYER_LEFT geldiyse güncelle
              if (currentState?.status === 'PLAYING' && state.status === 'WAITING' && !playersChanged && payload?.reason !== 'PLAYER_JOINED' && payload?.reason !== 'PLAYER_LEFT') {
                console.warn('[Web ChatRoom] Oyun durumu yanlış güncelleniyor - PLAYING -> WAITING (oyuncu sayısı değişmedi ve PLAYER_JOINED/PLAYER_LEFT değil), eski state korunuyor');
                return;
              }
              
              // State'i güncelle - kartların kapanıp açılmasını önlemek için akıllı güncelleme
              const currentHand = currentState?.players?.find(p => p.name === currentUserName)?.hand;
              const newHand = state.players?.find(p => p.name === currentUserName)?.hand;
              const handChanged = JSON.stringify(currentHand) !== JSON.stringify(newHand);
              
              // ÖNEMLİ: Eğer oyuncu sayısı değiştiyse, her zaman tam güncelleme yap
              if (playersChanged) {
                // Oyuncu sayısı değişti, tam güncelleme yap
                setGameStateSnapshot(state);
                pistiStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
                if (Platform.OS === 'web') {
                  console.log('[Web ChatRoom] Pişti oyuncu sayısı değişti, state güncellendi:', {
                    currentPlayerCount,
                    newPlayerCount,
                    currentPlayers: currentState?.players?.map(p => p.name) || [],
                    newPlayers: state.players?.map(p => p.name) || [],
                  });
                }
              } else if (!handChanged && state.status === 'PLAYING' && currentState?.status === 'PLAYING') {
                // Eğer kartlar değişmediyse ve oyun oynanıyorsa, sadece kritik değişiklikleri güncelle
                // Sadece sıra, merkez kart, oyuncu skorları gibi değişiklikleri güncelle
                const needsUpdate = state.currentPlayerIndex !== currentState.currentPlayerIndex || 
                    JSON.stringify(state.centerPile) !== JSON.stringify(currentState.centerPile) ||
                    state.players.some((p, i) => p.score !== currentState.players?.[i]?.score);
                
                if (needsUpdate) {
                  // Kartları koru, sadece diğer bilgileri güncelle
                  setGameStateSnapshot(prev => {
                    const updatedState = {
                      ...state,
                      players: state.players.map(newPlayer => {
                        const prevPlayer = prev.players?.find(p => p.name === newPlayer.name);
                        if (prevPlayer && newPlayer.name === currentUserName && prevPlayer.hand) {
                          // Kendi kartlarımızı koru
                          return { ...newPlayer, hand: prevPlayer.hand };
                        }
                        return newPlayer;
                      }),
                    };
                    // Ref'i güncelle
                    pistiStateRef.current = updatedState;
                    // initialGameState artık uygulandı, prop değişikliklerini ignore et
                    initialGameStateAppliedRef.current = true;
                    // AI oyuncu kontrolü - ref'i güncelle
                    const aiPlayers = updatedState.players?.filter(p => p.isAi) || [];
                    if (aiPlayers.length > 0) {
                      pistiAiPlayerAddedRef.current = true;
                    }
                    // App.js'e bildir (sadece kritik güncellemeler için)
                    if (typeof gameIntegration?.onStateUpdate === 'function') {
                      // Sadece gerçekten önemli değişiklikler için bildir
                      gameIntegration.onStateUpdate(updatedState);
                    }
                    return updatedState;
                  });
                }
              } else {
                // Kartlar değişti veya oyun durumu değişti, tam güncelleme yap
                // ÖNEMLİ: Oyuncu sayısı değişikliğini kontrol et
                const currentPlayerCount = currentState?.players?.length || 0;
                const newPlayerCount = state.players?.length || 0;
                const playersChanged = currentPlayerCount !== newPlayerCount;
                
                // Sadece gerçekten değiştiyse güncelle (gereksiz render'ı önlemek için)
                // Ama oyuncu sayısı değiştiyse her zaman güncelle
                const stateChanged = !currentState || JSON.stringify(currentState) !== JSON.stringify(state) || playersChanged;
                
                if (Platform.OS === 'web' && playersChanged) {
                  console.log('[Web ChatRoom] Oyuncu sayısı değişti, state güncelleniyor:', {
                    currentPlayerCount,
                    newPlayerCount,
                    currentPlayers: currentState?.players?.map(p => p.name) || [],
                    newPlayers: state.players?.map(p => p.name) || [],
                  });
                }
                
                if (stateChanged) {
                  setGameStateSnapshot(state);
                  // Ref'i güncelle
                  pistiStateRef.current = state;
                  // initialGameState artık uygulandı, prop değişikliklerini ignore et
                  initialGameStateAppliedRef.current = true;
                  
                  // AI oyuncu kontrolü - ref'i güncelle (birden fazla bot eklenmesini önlemek için)
                  // ÖNEMLİ: isAi kontrolünü sıkı yap (undefined, null, false kontrolü)
                  const aiPlayers = state.players?.filter(p => p.isAi === true) || [];
                  if (aiPlayers.length > 0) {
                    pistiAiPlayerAddedRef.current = true;
                    if (Platform.OS === 'web') {
                      console.log('[Web ChatRoom] AI oyuncu tespit edildi, ref güncellendi:', {
                        aiPlayers: aiPlayers.map(p => p.name),
                        aiPlayerCount: aiPlayers.length,
                      });
                    }
                  }
                  
                  // İlk oyuncu kontrolü - yapay zeka sorusu (Pişti için)
                  // ÖNEMLİ: Modal zaten açık mı kontrol et (ref ile race condition önle)
                  if (state.status === 'WAITING' && 
                      state.players?.length === 1 && 
                      !hasAskedAiQuestion &&
                      !pistiAiPlayerAddedRef.current &&
                      !pistiModalOpenedRef.current &&
                      !showAiQuestionModal &&
                      state.players[0]?.name === currentUserName) {
                    console.log('[Pişti] Modal açılma koşulları sağlandı (pisti:update - state değişti), modal açılıyor');
                    pistiModalOpenedRef.current = true; // Ref'i hemen işaretle (race condition önlemek için)
                    setShowAiQuestionModal(true);
                  } else if (state.status === 'WAITING' && state.players?.length === 1) {
                    console.log('[Pişti] Modal açılma koşulları sağlanmadı (pisti:update - state değişti):', {
                      status: state.status,
                      playersLength: state.players?.length,
                      hasAskedAiQuestion,
                      pistiAiPlayerAddedRef: pistiAiPlayerAddedRef.current,
                      pistiModalOpenedRef: pistiModalOpenedRef.current,
                      showAiQuestionModal,
                      firstPlayerName: state.players[0]?.name,
                      currentUserName
                    });
                  }
                  
                  // Sadece state gerçekten değiştiyse App.js'e bildir (gereksiz render'ı önlemek için)
                  if (typeof gameIntegration?.onStateUpdate === 'function') {
                    gameIntegration.onStateUpdate(state);
                  }
                } else {
                  // State değişmedi, sadece ref'i güncelle
                  pistiStateRef.current = state;
                  // AI oyuncu kontrolü - ref'i güncelle
                  // ÖNEMLİ: isAi kontrolünü sıkı yap (undefined, null, false kontrolü)
                  const aiPlayers = state.players?.filter(p => p.isAi === true) || [];
                  if (aiPlayers.length > 0) {
                    pistiAiPlayerAddedRef.current = true;
                  }
                  
                  // İlk oyuncu kontrolü - yapay zeka sorusu (Pişti için)
                  if (state.status === 'WAITING' && 
                      state.players?.length === 1 && 
                      !hasAskedAiQuestion &&
                      !pistiAiPlayerAddedRef.current &&
                      !pistiModalOpenedRef.current &&
                      !showAiQuestionModal &&
                      state.players[0]?.name === currentUserName) {
                    console.log('[Pişti] Modal açılma koşulları sağlandı (pisti:update - state değişmedi), modal açılıyor');
                    pistiModalOpenedRef.current = true; // Ref'i hemen işaretle (race condition önlemek için)
                    setShowAiQuestionModal(true);
                  }
                }
              }
            }
          } catch (error) {
            console.error('Pişti state yükleme hatası (pisti:update):', error);
          }
        };
        
        // Kısa bir gecikme ile state'i yükle (backend işleminin tamamlanması için)
        setTimeout(() => {
          loadPistiStateFromEvent();
        }, 100);
      } else if (isBatak && payload?.roomId) {
        const loadBatakStateFromEvent = async () => {
          try {
            const stateResponse = await fetch(`${API_BASE_URL}/games/batak/state/${payload.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
            if (stateResponse.ok) {
              const state = await stateResponse.json();
              // Teklif aşamasında ve input focus'ta ise state güncellemesini queue'ya al
              const currentPhase = state?.phase || gameStateSnapshot?.phase;
              if (currentPhase === 'BIDDING' && isInputFocusedRef.current) {
                if (Platform.OS === 'web') {
                  console.log('[Web ChatRoom] Batak BIDDING phase - queueing state update (input focused)');
                }
                pendingGameStateRef.current = state;
                batakStateRef.current = state; // Ref'i güncelle ama state'i set etme
                return; // State güncellemesini durdur
              }
              // Input focus olduğunda state'i queue'ya al, değilse direkt uygula
              if (isInputFocusedRef.current) {
                pendingGameStateRef.current = state;
                if (Platform.OS === 'web') {
                  console.log('[Web ChatRoom] Batak state queued - input is focused');
                }
              } else {
              setGameStateSnapshot(state);
              }
              batakStateRef.current = state;
              initialGameStateAppliedRef.current = true;
              
              // İlk oyuncu kontrolü - yapay zeka sorusu (Batak için)
              if (state.status === 'WAITING' && 
                  state.players?.length === 1 && 
                  !hasAskedAiQuestion &&
                  state.players[0]?.name === currentUserName) {
                setShowAiQuestionModal(true);
              }
              
              if (typeof gameIntegration?.onStateUpdate === 'function') {
                gameIntegration.onStateUpdate(state);
              }
            }
          } catch (error) {
            console.error('Batak state yükleme hatası (batak:update):', error);
          }
        };
        setTimeout(() => {
          loadBatakStateFromEvent();
        }, payload?.reason === 'PLAYER_JOINED' ? 200 : 80);
      } else if (isUnoGame && (payload?.roomId || activeRoomId)) {
        // UNO oyunu için, state güncellemesi geldiğinde kartları içeren tam state'i yükle
        const roomIdToUse = payload?.roomId || activeRoomId;
        const loadUnoStateFromEvent = async () => {
          try {
            // Ref'ten güncel currentUserName değerini al (closure problemi için)
            const playerNameToUse = currentUserNameRef.current || currentUserName || '';
            if (Platform.OS === 'web') {
              console.log('[UNO] handleGameUpdate: Fetching state with playerName:', playerNameToUse, 'roomId:', roomIdToUse);
            }
            const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${roomIdToUse}?playerName=${encodeURIComponent(playerNameToUse)}`);
            if (stateResponse.ok) {
              const state = await stateResponse.json();
              if (Platform.OS === 'web') {
                const myPlayer = state.players?.find(p => {
                  const playerName = p.name || '';
                  const currentName = playerNameToUse || '';
                  const lowerPlayer = playerName.toLowerCase();
                  const lowerCurrent = currentName.toLowerCase();
                  const matches = lowerPlayer === lowerCurrent || 
                                   lowerPlayer.startsWith(lowerCurrent) || 
                                   lowerCurrent.startsWith(lowerPlayer);
                  if (matches) {
                    console.log('[UNO] handleGameUpdate: Found myPlayer:', {
                      playerName,
                      currentName,
                      hasHand: !!p.hand,
                      handLength: p.hand?.length || 0,
                      handType: typeof p.hand,
                      handIsArray: Array.isArray(p.hand),
                    });
                  }
                  return matches;
                });
                console.log('[UNO] handleGameUpdate: State received:', {
                  roomId: roomIdToUse,
                  players: state.players?.map(p => ({ 
                    name: p.name, 
                    handCount: p.handCount, 
                    hasHand: !!p.hand, 
                    handLength: p.hand?.length || 0 
                  })),
                  myPlayer: myPlayer ? {
                    name: myPlayer.name,
                    hasHand: !!myPlayer.hand,
                    handLength: myPlayer.hand?.length || 0,
                  } : null,
                  status: state.status,
                });
              }
                setGameStateSnapshot(state);
              unoStateRef.current = state;
                initialGameStateAppliedRef.current = true;
                if (typeof gameIntegration?.onStateUpdate === 'function') {
                  gameIntegration.onStateUpdate(state);
                }
              } else {
              console.error('UNO state yükleme hatası (uno:update) - response not ok:', stateResponse.status);
            }
          } catch (error) {
            console.error('UNO state yükleme hatası (uno:update):', error);
          }
        };
        // Kısa bir gecikme ile state'i yükle (backend işleminin tamamlanması için)
        setTimeout(() => {
          loadUnoStateFromEvent();
        }, payload?.reason === 'GAME_STARTED' ? 200 : payload?.reason === 'PLAYER_JOINED' ? 200 : 100);
      } else if (isOkeyGame && (payload?.roomId || activeRoomId)) {
        // OKEY oyunu için, state güncellemesi geldiğinde tam state'i yükle
        const roomIdToUse = payload?.roomId || activeRoomId;
        const loadOkeyStateFromEvent = async () => {
          try {
            const playerNameToUse = currentUserNameRef.current || currentUserName || '';
            if (Platform.OS === 'web') {
              console.log('[OKEY] handleGameUpdate: Fetching state with playerName:', playerNameToUse, 'roomId:', roomIdToUse);
            }
            const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${roomIdToUse}?playerName=${encodeURIComponent(playerNameToUse)}`);
            if (stateResponse.ok) {
              const state = await stateResponse.json();
              setGameStateSnapshot(state);
              okeyStateRef.current = state;
              initialGameStateAppliedRef.current = true;
              
              if (Platform.OS === 'web') {
                console.log('[OKEY] handleGameUpdate: State received:', {
                  roomId: roomIdToUse,
                  players: state.players?.map(p => ({ name: p.name, handLength: p.hand?.length || 0 })),
                  status: state.status,
                });
              }
              
              // İlk oyuncu kontrolü - yapay zeka sorusu (OKEY için)
              // Creator otomatik eklendiği için, creator tek başına olduğunda modal göster
              if (Platform.OS === 'web') {
                console.log('[OKEY] AI question modal kontrolü (okey:update):', {
                  status: state.status,
                  playersCount: state.players?.length || 0,
                  creator: state.creator,
                  playerNameToUse,
                  hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                  players: state.players?.map(p => p.name) || [],
                  reason: payload?.reason,
                });
              }
              const isCreator = state.creator && (
                state.creator.toLowerCase() === playerNameToUse.toLowerCase() ||
                state.creator.toLowerCase().startsWith(playerNameToUse.toLowerCase()) ||
                playerNameToUse.toLowerCase().startsWith(state.creator.toLowerCase())
              );
              const isFirstPlayer = state.status === 'WAITING' && 
                  state.players?.length === 1 && 
                  !hasAskedOkeyAiQuestionRef.current &&
                  isCreator;
              if (Platform.OS === 'web') {
                console.log('[OKEY] AI question modal kontrolü sonucu (okey:update):', {
                  isFirstPlayer,
                  isCreator,
                  status: state.status,
                  playersCount: state.players?.length || 0,
                  hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                  creatorCheck: {
                    creator: state.creator,
                    playerNameToUse,
                    creatorLower: state.creator?.toLowerCase(),
                    playerNameToUseLower: playerNameToUse.toLowerCase(),
                    exactMatch: state.creator?.toLowerCase() === playerNameToUse.toLowerCase(),
                    creatorStartsWith: state.creator?.toLowerCase().startsWith(playerNameToUse.toLowerCase()),
                    playerStartsWith: playerNameToUse.toLowerCase().startsWith(state.creator?.toLowerCase()),
                  },
                });
              }
              if (isFirstPlayer && state.status === 'WAITING') {
                // İsim eşleşmesi kontrolü (esnek)
                const playerName = state.players[0]?.name || '';
                const nameMatches = playerName.toLowerCase() === playerNameToUse.toLowerCase() ||
                  playerName.toLowerCase().startsWith(playerNameToUse.toLowerCase()) ||
                  playerNameToUse.toLowerCase().startsWith(playerName.toLowerCase());
                if (Platform.OS === 'web') {
                  console.log('[OKEY] isFirstPlayer=true (okey:update), nameMatches kontrolü:', {
                    playerName,
                    playerNameToUse,
                    nameMatches,
                    playerNameLower: playerName.toLowerCase(),
                    playerNameToUseLower: playerNameToUse.toLowerCase(),
                  });
                }
                if (nameMatches) {
                  if (Platform.OS === 'web') {
                    console.log('[OKEY] AI question modal gösteriliyor (okey:update):', {
                      creator: state.creator,
                      playerName,
                      playerNameToUse,
                      playersCount: state.players.length,
                      status: state.status,
                    });
                  }
                  // Ref'i modal gösterilmeden önce true yapma, modal gerçekten gösterildiğinde yapılacak
                  setShowAiQuestionModal(true);
                  if (Platform.OS === 'web') {
                    console.log('[OKEY] setShowAiQuestionModal(true) çağrıldı (okey:update)');
                  }
                } else {
                  if (Platform.OS === 'web') {
                    console.log('[OKEY] AI question modal gösterilmedi - isim eşleşmedi (okey:update):', {
                      playerName,
                      playerNameToUse,
                    });
                  }
                }
              } else {
                if (Platform.OS === 'web') {
                  console.log('[OKEY] AI question modal gösterilmedi - isFirstPlayer=false veya status!=WAITING (okey:update):', {
                    status: state.status,
                    playersCount: state.players?.length || 0,
                    hasAskedOkeyAiQuestion: hasAskedOkeyAiQuestionRef.current,
                    isCreator,
                    isFirstPlayer,
                  });
                }
              }
              
              if (typeof gameIntegration?.onStateUpdate === 'function') {
                gameIntegration.onStateUpdate(state);
              }
              
              // Oyun başladıysa AI modal'ını kapat
              if (state.status === 'PLAYING' && showAiQuestionModal) {
                setShowAiQuestionModal(false);
                if (Platform.OS === 'web') {
                  console.log('[OKEY] Oyun başladı, AI modal kapatılıyor');
                }
              }
            } else {
              console.error('OKEY state yükleme hatası (okey:update) - response not ok:', stateResponse.status);
            }
          } catch (error) {
            console.error('OKEY state yükleme hatası (okey:update):', error);
          }
        };
        setTimeout(() => {
          loadOkeyStateFromEvent();
        }, payload?.reason === 'GAME_STARTED' ? 200 : payload?.reason === 'PLAYER_JOINED' ? 200 : 100);
      } else {
        // Pişti değilse normal şekilde işle
        setGameStateSnapshot(payload);
        // initialGameState artık uygulandı, prop değişikliklerini ignore et
        initialGameStateAppliedRef.current = true;
        if (payload?.reason === 'ROUND_END' || payload?.phase === 'EVALUATION') {
          setMessages([]);
        }
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(payload);
        }
      }
    };

    const currentGameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const listenPisti = currentGameType === 'pisti' || radio._id === 'game-pisti';
    const listenBatak = currentGameType === 'batak' || radio._id === 'game-batak';
    const listenUno = currentGameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
    const listenOkey = currentGameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
    
    if (listenPisti) {
      newSocket.on('pisti:update', handleGameUpdate);
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] pisti:update event listener kuruldu');
      }
    }
    if (listenBatak) {
      newSocket.on('batak:update', handleGameUpdate);
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] batak:update event listener kuruldu');
      }
    }
    if (listenUno) {
      newSocket.on('uno:update', handleGameUpdate);
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] uno:update event listener kuruldu');
      }
    }
    if (listenOkey) {
      newSocket.on('okey:update', handleGameUpdate);
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] okey:update event listener kuruldu');
      }
    }
    
    if (isGameRoom) {
      newSocket.on('category-battle:update', handleGameUpdate);
      newSocket.on('mystery-word-hunt:update', handleGameUpdate);
    }

    setSocket(newSocket);

    return () => {
      if (joinRetryTimer) {
        clearTimeout(joinRetryTimer);
      }
      // Pişti event listener'ını temizle
      const gameTypeCleanup = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
      const cleanupPisti = gameTypeCleanup === 'pisti' || radio._id === 'game-pisti';
      const cleanupBatak = gameTypeCleanup === 'batak' || radio._id === 'game-batak';
      const cleanupUno = gameTypeCleanup === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
      const cleanupOkey = gameTypeCleanup === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
      if (cleanupPisti) {
        newSocket.off('pisti:update', handleGameUpdate);
      }
      if (cleanupBatak) {
        newSocket.off('batak:update', handleGameUpdate);
      }
      if (cleanupUno) {
        newSocket.off('uno:update', handleGameUpdate);
      }
      if (cleanupOkey) {
        newSocket.off('okey:update', handleGameUpdate);
      }
      
      if (isGameRoom) {
        newSocket.off('category-battle:update', handleGameUpdate);
        newSocket.off('mystery-word-hunt:update', handleGameUpdate);
      }
      newSocket.disconnect();
    };
  // ÖNEMLİ: currentUserName veya gameIntegration dependency'lerini eklemiyoruz çünkü
  // backend'den isim atanırken veya parent her render'da yeni integration objesi gönderirken
  // effect tekrar çalışıp socket bağlantısını gereksiz yere koparabiliyordu.
  // Bunun yerine currentUserNameRef kullanarak güncel ismi okuyoruz.
  }, [radio._id, activeRoomId, userAvatar, isGameRoom]);

  // Pişti oyun state'ini yükle ve odaya katıl
  useEffect(() => {
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
    
    if (!isPisti || !room?.roomId || !socket || !isConnected || !isGameIdentityReady) {
      pistiJoinedRef.current = false; // Reset when leaving room
      return;
    }

    // Oda değiştiğinde reset
    const currentRoomId = room.roomId;
    if (pistiJoinedRef.current && pistiJoinedRef.current !== currentRoomId) {
      pistiJoinedRef.current = false;
    }

    const loadPistiState = async (skipIfPlaying = false) => {
      try {
        // Ref'ten güncel state'i al (closure problemi için)
        const currentState = pistiStateRef.current;
        
        // Eğer oyun oynanıyorsa ve skipIfPlaying true ise, yükleme yapma
        if (skipIfPlaying && currentState?.status === 'PLAYING') {
          if (Platform.OS === 'web') {
            console.log('[Web ChatRoom] Pişti state yükleme atlandı - oyun oynanıyor, sadece Socket.IO event\'lerine güveniyoruz');
          }
          return;
        }
        
        // playerName parametresini ekle ki kartlar görünsün
        const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${currentRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
        if (stateResponse.ok) {
          const state = await stateResponse.json();
          if (Platform.OS === 'web') {
            const myPlayer = state.players?.find(p => p.name === currentUserName);
            console.log('[Web ChatRoom] Pişti state yüklendi:', {
              roomId: currentRoomId,
              players: state.players?.length || 0,
              status: state.status,
              myHand: myPlayer?.hand?.length || 0,
            });
          }
          // Oyun durumu geriye dönüşünü engelle - eğer oyun oynanıyorsa ve yeni state WAITING ise, bu bir hata
          if (currentState?.status === 'PLAYING' && state.status === 'WAITING') {
            console.warn('[Web ChatRoom] Oyun durumu yanlış güncelleniyor - PLAYING -> WAITING, eski state korunuyor');
            return; // Eski state'i koru, yanlış güncellemeyi engelle
          }
          
          // State'i sadece gerçekten değiştiyse güncelle (kartların kapanıp açılmasını önlemek için)
          const currentHand = currentState?.players?.find(p => p.name === currentUserName)?.hand;
          const newHand = state.players?.find(p => p.name === currentUserName)?.hand;
          const handChanged = JSON.stringify(currentHand) !== JSON.stringify(newHand);
          
          // Eğer kartlar değişmediyse ve oyun oynanıyorsa, sadece kritik değişiklikleri güncelle
          if (!handChanged && state.status === 'PLAYING' && currentState?.status === 'PLAYING') {
            // Sadece sıra, merkez kart, oyuncu skorları gibi değişiklikleri güncelle
            if (state.currentPlayerIndex !== currentState.currentPlayerIndex || 
                JSON.stringify(state.centerPile) !== JSON.stringify(currentState.centerPile)) {
              // Kartları koru, sadece diğer bilgileri güncelle
              setGameStateSnapshot(prev => {
                const updatedState = {
                  ...state,
                  players: prev.players?.map(p => {
                    const newPlayer = state.players?.find(np => np.name === p.name);
                    if (newPlayer && p.name === currentUserName) {
                      // Kendi kartlarımızı koru
                      return { ...newPlayer, hand: p.hand || newPlayer.hand };
                    }
                    return newPlayer || p;
                  }) || state.players,
                };
                // Ref'i güncelle
                pistiStateRef.current = updatedState;
                // initialGameState artık uygulandı, prop değişikliklerini ignore et
                initialGameStateAppliedRef.current = true;
                return updatedState;
              });
            }
          } else {
            // Kartlar değişti veya oyun durumu değişti, tam güncelleme yap
            // Sadece gerçekten değiştiyse güncelle (gereksiz render'ı önlemek için)
            const stateChanged = !currentState || JSON.stringify(currentState) !== JSON.stringify(state);
            if (stateChanged) {
              setGameStateSnapshot(state);
              // initialGameState artık uygulandı, prop değişikliklerini ignore et
              initialGameStateAppliedRef.current = true;
            }
            // Ref'i her zaman güncelle (closure problemi için)
            pistiStateRef.current = state;
          }
          
          // Periyodik yükleme kaldırıldı - sadece Socket.IO event'lerine güveniyoruz
          // Interval yönetimi artık gerekli değil
          
          // İlk oyuncu kontrolü - yapay zeka sorusu (Pişti için)
          // ÖNEMLİ: Modal zaten açık mı kontrol et (ref ile race condition önle)
          if (isPisti && 
              state.status === 'WAITING' && 
              state.players?.length === 1 && 
              !hasAskedAiQuestion &&
              !pistiAiPlayerAddedRef.current &&
              !pistiModalOpenedRef.current &&
              !showAiQuestionModal &&
              state.players[0]?.name === currentUserName) {
            console.log('[Pişti] Modal açılma koşulları sağlandı (loadPistiState), modal açılıyor');
            pistiModalOpenedRef.current = true; // Ref'i hemen işaretle (race condition önlemek için)
            setShowAiQuestionModal(true);
          }
          
          // App.js'e sadece kritik durumlarda bildir (gereksiz render'ı önlemek için)
          // Kritik durumlar: oyun başladı, oyun bitti, oyuncu sayısı değişti
          const isCriticalUpdate = !currentState || 
            currentState.status !== state.status || 
            (currentState.players?.length || 0) !== (state.players?.length || 0);
          
          if (isCriticalUpdate && typeof gameIntegration?.onStateUpdate === 'function') {
            gameIntegration.onStateUpdate(state);
          }
        } else {
          console.error('Pişti state yükleme hatası - response not ok:', stateResponse.status);
        }
      } catch (error) {
        console.error('Pişti state yükleme hatası:', error);
      }
    };

    // Socket.IO join-room event'i zaten backend'de joinPistiRoom'u çağırıyor
    // Bu yüzden ayrı bir API çağrısı yapmaya gerek yok
    // Sadece state'i yükle ve Socket.IO event'lerini dinle
    const joinPistiRoom = async () => {
      if (pistiJoinedRef.current === currentRoomId) {
        // Zaten bu odaya katıldık, sadece state yükle
        loadPistiState();
        return;
      }
      
      // Socket.IO join-room event'i zaten backend'de joinPistiRoom'u çağırıyor
      // Bu yüzden sadece state'i yükle ve ref'i güncelle
      pistiJoinedRef.current = currentRoomId;
      console.log('[ChatRoom] Pişti odaya katılıyor (Socket.IO üzerinden), roomId:', currentRoomId);
      
      // State'i hemen yükle (Socket.IO join-room event'i backend'de joinPistiRoom'u çağıracak)
      // Kısa bir gecikme ile state'i yükle ki backend işlemi tamamlansın
      setTimeout(() => {
        loadPistiState();
      }, 500);
    };

    // İlk katılım - Socket.IO join işlemi tamamlandıktan sonra
    // Socket.IO join işlemi zaten backend'de Pişti join'i yapıyor, burada sadece state'i yükle
    // Backend'de joinPistiRoom zaten çağrıldı, sadece state'i yükle
    const initialLoad = setTimeout(() => {
      joinPistiRoom();
    }, 500);
    
    // Periyodik yükleme kaldırıldı - sadece Socket.IO event'lerine güveniyoruz
    // handleGameUpdate içinde pisti:update event'i dinleniyor ve state güncelleniyor
    
    return () => {
      clearTimeout(initialLoad);
      // Interval yönetimi artık gerekli değil
    };
  }, [room?.roomId, room?.gameType, radio._id, socket, isConnected, currentUserName, gameIntegration, isGameIdentityReady, isPisti, hasAskedAiQuestion]);

  // UNO oyun state'ini yükle
  useEffect(() => {
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isUnoRoom = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');

    if (!isUnoRoom || !room?.roomId || !socket || !isConnected || !isGameIdentityReady) {
      unoJoinedRef.current = false;
      return;
    }

    const currentRoomId = room.roomId;
    if (unoJoinedRef.current && unoJoinedRef.current !== currentRoomId) {
      unoJoinedRef.current = false;
    }

    let loadTimeout;

    const loadUnoState = async () => {
      try {
        const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${currentRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
        if (stateResponse.ok) {
          const state = await stateResponse.json();
          setGameStateSnapshot(state);
          unoStateRef.current = state;
          initialGameStateAppliedRef.current = true;
          
          // İlk oyuncu kontrolü - yapay zeka sorusu
          if (state.status === 'WAITING' && 
              state.players?.length === 1 && 
              !hasAskedAiQuestion &&
              state.players[0]?.name === currentUserName) {
            setShowAiQuestionModal(true);
          }
          
          if (typeof gameIntegration?.onStateUpdate === 'function') {
            gameIntegration.onStateUpdate(state);
          }
        }
      } catch (error) {
        console.error('UNO state yükleme hatası:', error);
      }
    };

    const scheduleLoad = () => {
      if (loadTimeout) {
        clearTimeout(loadTimeout);
      }
      loadTimeout = setTimeout(loadUnoState, 500);
    };

    if (unoJoinedRef.current === currentRoomId) {
      loadUnoState();
    } else {
      unoJoinedRef.current = currentRoomId;
      scheduleLoad();
    }

    return () => {
      if (loadTimeout) {
        clearTimeout(loadTimeout);
      }
    };
  }, [room?.roomId, room?.gameType, radio._id, socket, isConnected, currentUserName, gameIntegration, isGameIdentityReady, hasAskedAiQuestion]);

  useEffect(() => {
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isBatakRoom = gameType === 'batak' || radio._id === 'game-batak';

    if (!isBatakRoom || !room?.roomId || !socket || !isConnected || !isGameIdentityReady) {
      batakJoinedRef.current = false;
      batakStateRef.current = null;
      return;
    }

    const currentRoomId = room.roomId;
    if (batakJoinedRef.current && batakJoinedRef.current !== currentRoomId) {
      batakJoinedRef.current = false;
    }

    const loadBatakState = async () => {
      try {
        const stateResponse = await fetch(`${API_BASE_URL}/games/batak/state/${currentRoomId}?playerName=${encodeURIComponent(currentUserName || '')}`);
        if (stateResponse.ok) {
          const state = await stateResponse.json();
            setGameStateSnapshot(state);
          batakStateRef.current = state;
            initialGameStateAppliedRef.current = true;
          if (typeof gameIntegration?.onStateUpdate === 'function') {
            gameIntegration.onStateUpdate(state);
          }
        }
      } catch (error) {
        console.error('Batak state yükleme hatası:', error);
      }
    };

    const joinBatakRoom = async () => {
      if (batakJoinedRef.current === currentRoomId) {
        loadBatakState();
        return;
      }
      batakJoinedRef.current = currentRoomId;
      setTimeout(() => {
        loadBatakState();
      }, 500);
    };

    const initialLoad = setTimeout(() => {
      joinBatakRoom();
    }, 500);
    
    return () => {
      clearTimeout(initialLoad);
    };
  }, [room?.roomId, room?.gameType, radio._id, socket, isConnected, currentUserName, gameIntegration, isGameIdentityReady]);


  useEffect(() => {
    setPendingWildCard(null);
  }, [room?.roomId]);

  // Pişti handler fonksiyonları - renderGameHud'dan önce tanımla
  const handleStartPistiGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('Hata', 'Oda bilgisi bulunamadı');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/games/pisti/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        Alert.alert('Hata', error.error || 'Oyun başlatılamadı');
        return;
      }

      // Oyun başladı, state'i güncelle - playerName ile birlikte yükle ki kartlar görünsün
      const playerNameToUse = currentUserNameRef.current || '';
      const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        if (Platform.OS === 'web') {
          const myPlayer = state.players?.find(p => {
            const playerName = (p?.name || '').toLowerCase();
            const currentName = (playerNameToUse || '').toLowerCase();
            return playerName === currentName || playerName.startsWith(currentName) || currentName.startsWith(playerName);
          });
          console.log('[Web ChatRoom] Pişti state loaded after game start:', {
            roomId: room.roomId,
            myHand: myPlayer?.hand?.length || 0,
            status: state.status,
          });
        }
        setGameStateSnapshot(state);
        // initialGameState artık uygulandı, prop değişikliklerini ignore et
        initialGameStateAppliedRef.current = true;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('Pişti oyun başlatma hatası:', error);
      Alert.alert('Hata', 'Oyun başlatılamadı. Lütfen tekrar deneyin.');
    }
  }, [room?.roomId, gameIntegration]);

  const handleRestartPistiGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('Pişti', 'Oda bilgisi bulunamadı');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/games/pisti/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Oyun yeniden başlatılamadı' }));
        Alert.alert('Pişti', error.error || 'Oyun yeniden başlatılamadı');
        return;
      }

      // Oyun yeniden başladı, state'i güncelle - playerName ile birlikte yükle ki kartlar görünsün
      const playerNameToUse = currentUserNameRef.current || '';
      const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        if (Platform.OS === 'web') {
          const myPlayer = state.players?.find(p => {
            const playerName = (p?.name || '').toLowerCase();
            const currentName = (playerNameToUse || '').toLowerCase();
            return playerName === currentName || playerName.startsWith(currentName) || currentName.startsWith(playerName);
          });
          console.log('[Web ChatRoom] Pişti state loaded after game restart:', {
            roomId: room.roomId,
            myHand: myPlayer?.hand?.length || 0,
            status: state.status,
          });
        }
        setGameStateSnapshot(state);
        initialGameStateAppliedRef.current = true;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('Pişti oyun yeniden başlatma hatası:', error);
      Alert.alert('Pişti', error.message || 'Oyun yeniden başlatılamadı');
    }
  }, [room?.roomId, gameIntegration]);

  const handlePlayPistiCard = useCallback(async (cardIndex) => {
    if (!room?.roomId) return;
    
    try {
      const playerNameToUse = currentUserNameRef.current || 'Anonim';
      const response = await fetch(`${API_BASE_URL}/games/pisti/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: playerNameToUse,
          move: { cardIndex },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Bilinmeyen hata' }));
        console.error('[Web ChatRoom] Pişti move hatası:', errorData);
        Alert.alert('Hata', errorData.error || 'Kart oynanamadı. Lütfen tekrar deneyin.');
        return;
      }

      if (response.ok) {
        // State'i güncelle - playerName ile birlikte yükle ki kartlar görünsün
        const stateResponse = await fetch(`${API_BASE_URL}/games/pisti/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        if (Platform.OS === 'web') {
            const myPlayer = state.players?.find(p => {
              const playerName = (p?.name || '').toLowerCase();
              const currentName = (playerNameToUse || '').toLowerCase();
              return playerName === currentName || playerName.startsWith(currentName) || currentName.startsWith(playerName);
            });
            console.log('[Web ChatRoom] Pişti state loaded after card play:', {
            roomId: room.roomId,
              myHand: myPlayer?.hand?.length || 0,
            status: state.status,
          });
        }
        setGameStateSnapshot(state);
        // initialGameState artık uygulandı, prop değişikliklerini ignore et
        initialGameStateAppliedRef.current = true;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
          }
        }
      }
    } catch (error) {
      console.error('Pişti kart oynama hatası:', error);
      Alert.alert('Hata', 'Kart oynanamadı. Lütfen tekrar deneyin.');
    }
  }, [room?.roomId, gameIntegration]);

  // UNO handler fonksiyonları - renderGameHud'dan önce tanımla
  const handleAddAiPlayer = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('Oyun', 'Oda bilgisi bulunamadı');
      return;
    }
    
    // Zaten ekleme işlemi devam ediyorsa, tekrar çağrılmasını engelle
    if (isAddingAiPlayer) {
      return;
    }
    
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isUnoGame = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
    const isPistiGame = gameType === 'pisti' || radio._id === 'game-pisti' || room?.roomId?.startsWith('pisti-');
    const isBatakGame = gameType === 'batak' || radio._id === 'game-batak' || room?.roomId?.startsWith('batak-');
    const isOkeyGame = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
    
    // Pişti için: Zaten bir AI oyuncu varsa ekleme
    if (isPistiGame) {
      // Ref kontrolü - daha önce eklenmişse engelle
      if (pistiAiPlayerAddedRef.current) {
        console.log('[Pişti] AI oyuncu zaten eklenmiş, tekrar ekleme engellendi');
        setShowAiQuestionModal(false);
        setHasAskedAiQuestion(true);
        setIsAddingAiPlayer(false);
        return;
      }
      
      // State kontrolü - oyuncu listesinde AI var mı?
      // ÖNEMLİ: isAi kontrolünü sıkı yap (undefined, null, false kontrolü)
      if (gameStateSnapshot?.players) {
        const existingAiPlayers = gameStateSnapshot.players.filter(p => p.isAi === true);
        if (existingAiPlayers.length > 0) {
          console.log(`[Pişti] State'de ${existingAiPlayers.length} AI oyuncu var, ekleme engellendi. AI oyuncular: ${existingAiPlayers.map(p => p.name).join(', ')}`);
          pistiAiPlayerAddedRef.current = true;
          Alert.alert('Oyun', 'Zaten bir yapay zeka oyuncusu var');
          setShowAiQuestionModal(false);
          setHasAskedAiQuestion(true);
          setIsAddingAiPlayer(false);
          return;
        }
      }
      
      // ÖNEMLİ: Ref'i API çağrısından ÖNCE işaretleme - sadece başarılı olduğunda işaretle
      // Bu, birden fazla çağrıyı önlemek için yeterli değil, mutex backend'de zaten var
    }
    
    try {
      setIsAddingAiPlayer(true);
      setShowAiQuestionModal(false);
      // Pişti için modal ref'ini temizle (modal kapandı)
      if (isPistiGame) {
        pistiModalOpenedRef.current = false;
      }
      
      // Oyun durumunu kontrol et - sadece WAITING durumunda AI eklenebilir
      const currentStatus = gameStateSnapshot?.status;
      if (currentStatus && currentStatus !== 'WAITING') {
        Alert.alert('Oyun', 'Oyun başlamış, yapay zeka oyuncusu eklenemez');
        if (isOkeyGame) {
          hasAskedOkeyAiQuestionRef.current = true;
        } else {
          setHasAskedAiQuestion(true);
        }
        setIsAddingAiPlayer(false);
        return;
      }
      
      if (isOkeyGame) {
        hasAskedOkeyAiQuestionRef.current = true;
      } else {
        setHasAskedAiQuestion(true);
      }
      
      const endpoint = isUnoGame 
        ? `${API_BASE_URL}/games/uno/add-ai`
        : isPistiGame
        ? `${API_BASE_URL}/games/pisti/add-ai`
        : isBatakGame
        ? `${API_BASE_URL}/games/batak/add-ai`
        : isOkeyGame
        ? `${API_BASE_URL}/games/okey/add-ai`
        : null;
      
      if (!endpoint) {
        Alert.alert('Oyun', 'Geçersiz oyun tipi');
        setIsAddingAiPlayer(false);
        return;
      }

      // ÖNEMLİ: API çağrısından önce bir kez daha kontrol et (race condition'ı önlemek için)
      if (isPistiGame && pistiAiPlayerAddedRef.current) {
        console.log('[Pişti] API çağrısından önce ref kontrolü: AI oyuncu zaten eklenmiş, istek iptal edildi');
        setIsAddingAiPlayer(false);
        return;
      }
      
      console.log(`[Pişti] API çağrısı yapılıyor: ${endpoint}, roomId: ${room.roomId}`);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.roomId }),
      });
      console.log(`[Pişti] API çağrısı tamamlandı, response.ok: ${response.ok}, status: ${response.status}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        Alert.alert('Oyun', errorData.error || 'Yapay zeka oyuncusu eklenirken hata oluştu');
        // Pişti için ref'i false yap (API başarısız oldu, tekrar deneyebilir)
        if (isPistiGame) {
          pistiAiPlayerAddedRef.current = false;
        }
        setIsAddingAiPlayer(false);
        return;
      }
      
      // ÖNEMLİ: API başarılı oldu, ref'i true yap (birden fazla çağrıyı önlemek için)
      if (isPistiGame) {
        pistiAiPlayerAddedRef.current = true;
      }
      // State'i yeniden yükle
      setTimeout(() => {
        const loadState = async () => {
          try {
            const stateEndpoint = isUnoGame
              ? `${API_BASE_URL}/games/uno/state/${room.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`
              : isPistiGame
              ? `${API_BASE_URL}/games/pisti/state/${room.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`
              : isBatakGame
              ? `${API_BASE_URL}/games/batak/state/${room.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`
              : isOkeyGame
              ? `${API_BASE_URL}/games/okey/state/${room.roomId}?playerName=${encodeURIComponent(currentUserName || '')}`
              : null;
            
            if (!stateEndpoint) {
              setIsAddingAiPlayer(false);
              return;
            }
            
            const stateResponse = await fetch(stateEndpoint);
            if (stateResponse.ok) {
              const state = await stateResponse.json();
              setGameStateSnapshot(state);
              if (isUnoGame) {
                unoStateRef.current = state;
              } else if (isPistiGame) {
                pistiStateRef.current = state;
              } else if (isBatakGame) {
                batakStateRef.current = state;
              }
              if (typeof gameIntegration?.onStateUpdate === 'function') {
                gameIntegration.onStateUpdate(state);
              }
            }
            setIsAddingAiPlayer(false);
          } catch (error) {
            console.error('State yükleme hatası:', error);
            setIsAddingAiPlayer(false);
          }
        };
        loadState();
      }, 500);
    } catch (error) {
      console.error('Yapay zeka oyuncusu ekleme hatası:', error);
      Alert.alert('Oyun', 'Yapay zeka oyuncusu eklenirken hata oluştu');
      setIsAddingAiPlayer(false);
    }
  }, [room?.roomId, room?.gameType, radio._id, currentUserName, isAddingAiPlayer]);

  const handleStartUnoGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('UNO', 'Oda bilgisi bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/games/uno/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.roomId }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Oyun başlatılamadı' }));
        Alert.alert('UNO', error.error || 'Oyun başlatılamadı');
        return;
      }
      // Oyun başladı, state'i güncelle - playerName ile birlikte yükle ki kartlar görünsün
      const playerNameToUse = currentUserNameRef.current || currentUserName || '';
      const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
        if (stateResponse.ok) {
          const state = await stateResponse.json();
          if (Platform.OS === 'web') {
          const myPlayer = state.players?.find(p => {
            const playerName = p.name || '';
            const currentName = playerNameToUse || '';
            const lowerPlayer = playerName.toLowerCase();
            const lowerCurrent = currentName.toLowerCase();
            return lowerPlayer === lowerCurrent || 
                   lowerPlayer.startsWith(lowerCurrent) || 
                   lowerCurrent.startsWith(lowerPlayer);
          });
          console.log('[Web ChatRoom] UNO state loaded after game start:', {
              roomId: room.roomId,
              myHand: myPlayer?.hand?.length || 0,
              status: state.status,
            myPlayer: myPlayer ? { name: myPlayer.name, hasHand: !!myPlayer.hand, handLength: myPlayer.hand?.length || 0 } : null,
            });
          }
          setGameStateSnapshot(state);
        unoStateRef.current = state;
          initialGameStateAppliedRef.current = true;
          if (typeof gameIntegration?.onStateUpdate === 'function') {
            gameIntegration.onStateUpdate(state);
          }
      }
    } catch (error) {
      console.error('UNO oyun başlatma hatası:', error);
      Alert.alert('UNO', error.message || 'Oyun başlatılamadı');
    }
  }, [room?.roomId, gameIntegration]);

  const handleRestartUnoGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('UNO', 'Oda bilgisi bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/games/uno/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.roomId }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Oyun yeniden başlatılamadı' }));
        Alert.alert('UNO', error.error || 'Oyun yeniden başlatılamadı');
      return;
    }
      // Oyun yeniden başladı, state'i güncelle - playerName ile birlikte yükle ki kartlar görünsün
      const playerNameToUse = currentUserNameRef.current || currentUserName || '';
      const stateResponse = await fetch(`${API_BASE_URL}/games/uno/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
        if (stateResponse.ok) {
          const state = await stateResponse.json();
          if (Platform.OS === 'web') {
          const myPlayer = state.players?.find(p => {
            const playerName = p.name || '';
            const currentName = playerNameToUse || '';
            const lowerPlayer = playerName.toLowerCase();
            const lowerCurrent = currentName.toLowerCase();
            return lowerPlayer === lowerCurrent || 
                   lowerPlayer.startsWith(lowerCurrent) || 
                   lowerCurrent.startsWith(lowerPlayer);
          });
          console.log('[Web ChatRoom] UNO state loaded after game restart:', {
          roomId: room.roomId,
              myHand: myPlayer?.hand?.length || 0,
              status: state.status,
            myPlayer: myPlayer ? { name: myPlayer.name, hasHand: !!myPlayer.hand, handLength: myPlayer.hand?.length || 0 } : null,
            });
          }
          setGameStateSnapshot(state);
        unoStateRef.current = state;
          initialGameStateAppliedRef.current = true;
          if (typeof gameIntegration?.onStateUpdate === 'function') {
            gameIntegration.onStateUpdate(state);
          }
      }
    } catch (error) {
      console.error('UNO oyun yeniden başlatma hatası:', error);
      Alert.alert('UNO', error.message || 'Oyun yeniden başlatılamadı');
    }
  }, [room?.roomId, gameIntegration]);

  // OKEY handler fonksiyonları
  const handleDiscardOkeyTile = useCallback(async (tile) => {
    if (!room?.roomId || !tile || isSubmittingOkeyMove) return;
    setIsSubmittingOkeyMove(true);
    try {
      const playerNameToUse = currentUserNameRef.current || currentUserName || 'Anonim';
      const response = await fetch(`${API_BASE_URL}/games/okey/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: playerNameToUse,
          move: {
            type: 'discard',
            tile: tile,
          },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Taş atılamadı' }));
        Alert.alert('OKEY', error.error || 'Taş atılamadı');
        return;
      }
      setMyLastDiscardedTile(tile);
      // State'i yeniden yükle
      const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        setGameStateSnapshot(state);
        okeyStateRef.current = state;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('OKEY taş atma hatası:', error);
      Alert.alert('OKEY', error.message || 'Taş atılamadı');
    } finally {
      setIsSubmittingOkeyMove(false);
    }
  }, [room?.roomId, currentUserName, gameIntegration, isSubmittingOkeyMove]);

  const handleDrawOkeyTile = useCallback(async (fromStock = true) => {
    if (!room?.roomId || isSubmittingOkeyMove) return;
    setIsSubmittingOkeyMove(true);
    try {
      const playerNameToUse = currentUserNameRef.current || currentUserName || 'Anonim';
      const response = await fetch(`${API_BASE_URL}/games/okey/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: playerNameToUse,
          move: {
            type: fromStock ? 'draw-from-stock' : 'draw-from-discard',
          },
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Taş çekilemedi' }));
        Alert.alert('OKEY', error.error || 'Taş çekilemedi');
        return;
      }
      // State'i yeniden yükle
      const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        setGameStateSnapshot(state);
        okeyStateRef.current = state;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('OKEY taş çekme hatası:', error);
      Alert.alert('OKEY', error.message || 'Taş çekilemedi');
    } finally {
      setIsSubmittingOkeyMove(false);
    }
  }, [room?.roomId, currentUserName, gameIntegration, isSubmittingOkeyMove]);

  const handleStartOkeyGame = useCallback(async () => {
    if (!room?.roomId) {
      Alert.alert('OKEY', 'Oda bilgisi bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/games/okey/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: room.roomId }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Oyun başlatılamadı' }));
        Alert.alert('OKEY', error.error || 'Oyun başlatılamadı');
        return;
      }
      // State'i yeniden yükle
      const playerNameToUse = currentUserNameRef.current || currentUserName || 'Anonim';
      const stateResponse = await fetch(`${API_BASE_URL}/games/okey/state/${room.roomId}?playerName=${encodeURIComponent(playerNameToUse)}`);
      if (stateResponse.ok) {
        const state = await stateResponse.json();
        setGameStateSnapshot(state);
        okeyStateRef.current = state;
        initialGameStateAppliedRef.current = true;
        if (typeof gameIntegration?.onStateUpdate === 'function') {
          gameIntegration.onStateUpdate(state);
        }
      }
    } catch (error) {
      console.error('OKEY oyun başlatma hatası:', error);
      Alert.alert('OKEY', error.message || 'Oyun başlatılamadı');
    }
  }, [room?.roomId, currentUserName, gameIntegration]);

  const handleUnoMoveRequest = useCallback(async (movePayload) => {
    if (!room?.roomId) return;
    setIsSubmittingUnoMove(true);
    try {
      // Ref'ten güncel currentUserName değerini al (closure problemi için)
      const playerNameToUse = currentUserNameRef.current || currentUserName || 'Anonim';
      if (Platform.OS === 'web') {
        console.log('[UNO] handleUnoMoveRequest: Submitting move:', {
          roomId: room.roomId,
          playerName: playerNameToUse,
          moveType: movePayload.type,
          cardId: movePayload.cardId,
          colorChoice: movePayload.colorChoice,
        });
      }
      const response = await fetch(`${API_BASE_URL}/games/uno/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId: room.roomId,
          playerName: playerNameToUse,
          move: movePayload,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Hamle yapılamadı' }));
        if (Platform.OS === 'web') {
          console.error('[UNO] handleUnoMoveRequest: Error response:', {
            status: response.status,
            error: error.error || error.message,
            playerName: playerNameToUse,
            roomId: room.roomId,
          });
        }
        throw new Error(error.error || error.message || 'Hamle yapılamadı');
      }
      // Başarılı hamle sonrası state güncellenecek (uno:update event'i ile)
    } catch (error) {
      console.error('[UNO] handleUnoMoveRequest: Exception:', error);
      Alert.alert('UNO', error.message || 'Hamle yapılamadı');
    } finally {
      setIsSubmittingUnoMove(false);
    }
  }, [room?.roomId]);

  const handleUnoDrawCard = useCallback(async () => {
    setPendingWildCard(null);
    await handleUnoMoveRequest({ type: 'DRAW' });
  }, [handleUnoMoveRequest]);

  const handleUnoPlayCard = useCallback(async (cardId, colorChoice = null) => {
    if (!cardId) return;
    setPendingWildCard(null);
    await handleUnoMoveRequest({
      type: 'PLAY_CARD',
      cardId,
      colorChoice,
    });
  }, [handleUnoMoveRequest]);



  const addReaction = (reaction) => {
    const reactionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry = {
      id: reactionId,
      emoji: reaction.emoji,
      userName: reaction.userName || 'Anonim',
      avatar: reaction.avatar,
    };

    setRecentReactions(prev => [...prev, entry]);

    setTimeout(() => {
      setRecentReactions(prev => prev.filter(item => item.id !== reactionId));
    }, 2000);
  };

  const toggleEmojiPicker = () => {
    if (isEmojiPickerVisible) {
      setIsEmojiPickerVisible(false);
      inputRef.current?.focus();
    } else {
      Keyboard.dismiss();
      setIsEmojiPickerVisible(true);
    }
  };

  const handleEmojiSelect = (emoji) => {
    if (!emoji) {
      return;
    }
    if (longPressTriggeredRef.current) {
      // Long press already handled this tap (reaction)
      longPressTriggeredRef.current = false;
      return;
    }
    setMessageText(prev => (prev || '') + emoji);
    setIsEmojiPickerVisible(false);
    inputRef.current?.focus();
  };

  const sendReaction = (emoji) => {
    if (!emoji) {
      return;
    }

    const reactionPayload = {
      roomId: activeRoomId,
      radioId: radio._id,
      userName: currentUserName || 'Anonim',
      avatar: normalizedUserAvatar,
      emoji,
    };

    // Tepkiyi anında göster
    addReaction(reactionPayload);

    if (socket && isConnected) {
      socket.emit('send-reaction', reactionPayload, (ack) => {
        if (ack?.error) {
          console.error('Emoji gönderilemedi:', ack.error);
        }
      });
    } else {
      console.warn('Socket bağlantısı yok - emoji gönderilemedi');
    }
  };

  const handleEmojiLongPress = (emoji) => {
    longPressTriggeredRef.current = true;
    sendReaction(emoji);
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 250);
  };

  const sendMessage = () => {
    const trimmed = messageText.trim();
    if (!trimmed) return;
    const outgoingText = trimmed;

    const clearInput = () => {
      setMessageText('');
      setIsEmojiPickerVisible(false);
      Keyboard.dismiss();
    };

    const emitChatMessage = () => {
      if (socket && isConnected) {
        socket.emit(
          'send-message',
          {
            roomId: activeRoomId,
            radioId: radio._id,
            userName: currentUserName || 'Anonim',
            avatar: normalizedUserAvatar,
            message: outgoingText,
          },
          (response) => {
            if (response?.error) {
              Alert.alert('Hata', response.error);
            } else {
              clearInput();
            }
          }
        );
      } else {
        // Socket bağlantısı yoksa mesajı yerel olarak göster
        const fallbackMessage = {
          id: `${Date.now()}-local`,
          socketId: 'local',
          userName: currentUserName || 'Anonim',
          avatar: normalizedUserAvatar,
          message: outgoingText,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, fallbackMessage]);
        clearInput();
      }
    };

    emitChatMessage();

    if (isGameRoom) {
      if (typeof gameIntegration?.submitGuess === 'function') {
        gameIntegration.submitGuess(outgoingText).catch((error) => {
          const message =
            error?.response?.data?.error ||
            error?.message ||
            'Tahmin değerlendirilemedi';
          Alert.alert('Gizemli Kelime Avı', message);
        });
      } else if (typeof gameIntegration?.submitWord === 'function') {
        gameIntegration.submitWord(outgoingText).catch((error) => {
          const message =
            error?.response?.data?.error ||
            error?.message ||
            'Kelime değerlendirilemedi';
          Alert.alert('Kategori Savaşı', message);
        });
      }
    }
  };

  const renderMessage = ({ item }) => {
    const isOwnMessage = item.socketId && socket ? item.socketId === socket.id : (item.userName === currentUserName);
    const messageTime = new Date(item.timestamp).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
    const fallbackLabel = item.userName?.[0] || '🙂';

    return (
      <View
        style={[
          styles.messageRow,
          isOwnMessage ? styles.messageRowOwn : styles.messageRowOther
        ]}
      >
        {!isOwnMessage && (
          renderAvatarBadge(
            item.avatar,
            32,
            styles.messageAvatar,
            styles.messageAvatarLabel,
            fallbackLabel
          )
        )}
        <View
          style={[
            styles.messageBubble,
            isOwnMessage ? styles.messageBubbleOwn : styles.messageBubbleOther
          ]}
        >
          {!isOwnMessage && (
            <Text style={styles.messageUser}>{item.userName}</Text>
          )}
          <Text
            style={[
              styles.messageText,
              isOwnMessage ? styles.messageTextOwn : styles.messageTextOther
            ]}
          >
            {item.message}
          </Text>
          <Text
            style={[
              styles.timestamp,
              isOwnMessage ? styles.timestampOwn : styles.timestampOther
            ]}
          >
            {messageTime}
          </Text>
        </View>
        {isOwnMessage && (
          renderAvatarBadge(
            item.avatar,
            32,
            styles.messageAvatarOwn,
            styles.messageAvatarLabel,
            fallbackLabel
          )
        )}
      </View>
    );
  };

  const getPhaseLabel = () => {
    if (!gameStateSnapshot) {
      return 'Hazırlanıyor';
    }
    if (gameStateSnapshot.phase === 'COLLECTING') {
      return 'Tur Devam Ediyor';
    }
    if (gameStateSnapshot.phase === 'EVALUATION') {
      return 'Ara Verildi';
    }
    return gameStateSnapshot.phase;
  };

  const formatTimeLeft = () => {
    // Gizemli Kelime Avı için: Soru gelene kadar sayacı başlatma
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isMysteryWordHunt = gameType === 'mystery-word-hunt' || radio._id === 'game-mystery-word-hunt';
    if (isMysteryWordHunt) {
      if (!gameStateSnapshot?.currentQuestion || !gameStateSnapshot?.roundEndsAt) {
        return '--:--';
      }
    } else {
      if (!gameStateSnapshot?.roundEndsAt) {
        return '--:--';
      }
    }
    const remaining = Math.max(0, gameStateSnapshot.roundEndsAt - gameClockTick);
    const seconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(seconds / 60);
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const renderGameHud = () => {
    try {
    if (!isGameRoom) {
      return null;
    }

    // Oyun tipini belirle: room.gameType varsa onu kullan, yoksa radyo ID'sinden çıkar
    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
    const isMysteryWordHunt = gameType === 'mystery-word-hunt' || radio._id === 'game-mystery-word-hunt';
    const isPisti = gameType === 'pisti' || radio._id === 'game-pisti';
    const isBatak = gameType === 'batak' || radio._id === 'game-batak' || room?.roomId?.startsWith('batak-');
    const isUno = gameType === 'uno' || radio._id === 'game-uno' || room?.roomId?.startsWith('uno-');
    const isOkey = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');

    if (isBatak) {
        const phase = gameStateSnapshot?.phase || 'WAITING';
        const currentPlayerIndex = gameStateSnapshot?.currentPlayerIndex || 0;
        const players = gameStateSnapshot?.players || [];
        const currentPlayer = players[currentPlayerIndex];
        const sessionUserName = gameStateSnapshot?.sessionUserName || userName;
        const isMyTurn = currentPlayer?.name === sessionUserName;
        
      return renderBatakGame;
    }

    // Pişti için özel HUD
    if (isPisti) {
      const players = gameStateSnapshot?.players || [];
      const currentPlayerIndex = gameStateSnapshot?.currentPlayerIndex || 0;
      const status = gameStateSnapshot?.status || 'WAITING';
      const winner = gameStateSnapshot?.winner || null;
      
      // Debug: Render sırasında state'i logla
      if (Platform.OS === 'web') {
        console.log('[Web ChatRoom] renderGameHud - Pişti state:', {
          status: gameStateSnapshot?.status,
          playersCount: players.length,
          playerNames: players.map(p => p.name),
          gameStateSnapshotExists: !!gameStateSnapshot,
          gameStateSnapshotKeys: gameStateSnapshot ? Object.keys(gameStateSnapshot) : [],
        });
      }
      const centerPile = gameStateSnapshot?.centerPile || [];
      const currentPlayer = players[currentPlayerIndex] || null;
      // ÖNEMLİ: İsim eşleşmesi için case-insensitive ve kısmi eşleşme (backend'deki isim farklı olabilir)
      // Güvenli null kontrolleri ekle
      const currentUserNameLower = (currentUserName || '').toLowerCase();
      const currentPlayerNameLower = (currentPlayer?.name || '').toLowerCase();
      const isMyTurn = currentPlayer && (
        currentPlayerNameLower === currentUserNameLower || 
        currentPlayerNameLower.startsWith(currentUserNameLower) ||
        currentUserNameLower.startsWith(currentPlayerNameLower)
      );
      // ÖNEMLİ: İsim eşleşmesi için case-insensitive ve kısmi eşleşme
      const myPlayer = players.find(p => {
        const playerNameLower = (p?.name || '').toLowerCase();
        return playerNameLower === currentUserNameLower ||
               playerNameLower.startsWith(currentUserNameLower) ||
               currentUserNameLower.startsWith(playerNameLower);
      });
      const myHand = myPlayer?.hand || [];
      
      // Debug: Kartlar neden görünmüyor?
      if (Platform.OS === 'web' && status === 'PLAYING') {
        console.log('[Web ChatRoom] renderGameHud - Kart debug:', {
          currentUserName,
          myPlayerName: myPlayer?.name,
          myHandLength: myHand.length,
          myHand: myHand,
          allPlayers: players.map(p => ({ name: p.name, handLength: p.hand?.length || 0, hasHand: !!p.hand })),
        });
      }

      return (
        <View style={[styles.gameHudContainer, status === 'PLAYING' && isMyTurn && { borderColor: '#0FBF61' }]}>
          <View style={styles.gameHudRow}>
            <View style={[styles.gameHudColumn, { flex: 1 }]}>
              <Text style={styles.gameHudLabel}>Durum</Text>
              <Text style={styles.gameHudValue}>
                {status === 'WAITING' ? 'Bekleniyor' : 
                 status === 'PLAYING' ? 'Oynanıyor' : 
                 status === 'FINISHED' ? 'Bitti' : status}
              </Text>
            </View>
            {status === 'PLAYING' && (
              <View style={[styles.gameHudColumn, { flex: 1 }]}>
                <Text style={styles.gameHudLabel}>Sıra</Text>
                <Text style={[styles.gameHudValue, isMyTurn && { color: '#E94560', fontWeight: 'bold' }]}>
                  {isMyTurn ? 'Sende' : (currentPlayer?.name || '—')}
                </Text>
              </View>
            )}
          </View>
          {players.length > 0 && (
            <View style={styles.gameLeaderboard}>
              <Text style={styles.gameHudLabel}>Oyuncular</Text>
              {players.map((player, index) => (
                <View key={player.name || index} style={styles.gameLeaderboardItem}>
                  <Text style={styles.gameLeaderboardName} numberOfLines={1}>
                    {player.name} {index === currentPlayerIndex && status === 'PLAYING' ? '▶️' : ''}
                  </Text>
                  <Text style={styles.gameLeaderboardScore}>
                    {player.score || 0} puan • {player.handCount || 0} kart
                  </Text>
                </View>
              ))}
            </View>
          )}
          
          {/* Oyuncunun kendi kartları */}
          {status === 'PLAYING' && myHand.length > 0 && (
            <View style={styles.pistiHandContainer}>
              {/* Yer (centerPile) bilgisi - kartların üstünde */}
              {centerPile.length > 0 && (
                <View style={styles.pistiYerContainer}>
                  <Text style={styles.gameHudLabel}>Yer</Text>
                  <View style={styles.pistiYerCards}>
                    {centerPile.map((card, idx) => {
                      if (!card || !card.value || !card.suit) return null;
                      return (
                      <View key={idx} style={styles.pistiYerCard}>
                        <Text style={styles.pistiYerCardText}>
                          {getCardValueDisplay(card.value)} {getCardSuitEmoji(card.suit)}
                        </Text>
                      </View>
                      );
                    })}
                  </View>
                </View>
              )}
              {centerPile.length === 0 && (
                <View style={styles.pistiYerContainer}>
                  <Text style={styles.gameHudLabel}>Yer</Text>
                  <Text style={styles.pistiYerEmptyText}>Boş</Text>
                </View>
              )}
              <Text style={styles.gameHudLabel}>Kartların ({myHand.length})</Text>
              <View style={styles.pistiHandRow}>
                {myHand.map((card, index) => {
                  if (!card || !card.value || !card.suit) return null;
                  return (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.pistiCard,
                      isMyTurn && styles.pistiCardPlayable,
                      !isMyTurn && styles.pistiCardDisabled,
                      // Mobil için dinamik boyutlar
                        Platform.OS !== 'web' && {
                          width: 50,
                          height: 70,
                          minWidth: 45,
                          minHeight: 65,
                        },
                      Platform.OS === 'web' && isMobile && {
                        width: 50,
                        height: 70,
                        minWidth: 45,
                        minHeight: 65,
                      },
                    ]}
                    onPress={() => isMyTurn && handlePlayPistiCard(index)}
                    disabled={!isMyTurn}
                  >
                    <Text style={[
                      styles.pistiCardValue,
                        Platform.OS !== 'web' && { fontSize: 16 },
                      Platform.OS === 'web' && isMobile && { fontSize: 16 },
                    ]}>
                      {getCardValueDisplay(card.value)}
                    </Text>
                    <Text style={[
                      styles.pistiCardSuit,
                        Platform.OS !== 'web' && { fontSize: 20 },
                      Platform.OS === 'web' && isMobile && { fontSize: 20 },
                    ]}>
                      {getCardSuitEmoji(card.suit)}
                    </Text>
                  </TouchableOpacity>
                  );
                })}
              </View>
              {!isMyTurn && (
                <Text style={styles.pistiWaitText}>
                  Sıra {currentPlayer?.name || '—'} oyuncusunda...
                </Text>
              )}
            </View>
          )}

          {status === 'WAITING' && players.length >= 2 && (
            <View style={styles.gameAnnouncement}>
              <Text style={styles.gameHudLabel}>
                Oyun başlamak için yeterli oyuncu var! ({players.length}/{gameStateSnapshot?.maxPlayers || 4})
              </Text>
              <Button
                mode="contained"
                onPress={handleStartPistiGame}
                style={styles.startGameButton}
                buttonColor="#E94560"
              >
                Oyunu Başlat
              </Button>
            </View>
          )}
          {status === 'WAITING' && players.length < 2 && (
            <View style={styles.gameAnnouncement}>
              <Text style={styles.gameHudLabel}>
                Oyun başlamak için en az 2 oyuncu gerekli. ({players.length}/{gameStateSnapshot?.minPlayers || 2})
              </Text>
            </View>
          )}

          {winner && status === 'FINISHED' && (
            <View style={styles.gameAnnouncement}>
              <Text style={styles.gameHudLabel}>
                {winner.toLowerCase() === (currentUserName || '').toLowerCase()
                  ? 'Kazandın! 🎉'
                  : `${winner} kazandı!`}
              </Text>
              <Text style={[styles.gameHudLabel, { marginTop: 4 }]}>
                Yeniden oynamak ister misin?
              </Text>
              <View style={{ flexDirection: 'row', marginTop: 8, gap: 8 }}>
                <Button
                  mode="contained"
                  onPress={handleRestartPistiGame}
                  style={[styles.startGameButton, { flex: 1 }]}
                  buttonColor="#E94560"
                >
                  Yeniden Oyna
                </Button>
              </View>
            </View>
          )}
        </View>
      );
    }

    if (isUno) {
      const players = gameStateSnapshot?.players || [];
      const status = gameStateSnapshot?.status || 'WAITING';
      const currentPlayerIndex = gameStateSnapshot?.currentPlayerIndex ?? 0;
      const currentPlayer = players[currentPlayerIndex];
      const normalizeName = (value = '') => (value || '').toLowerCase();
      const namesMatch = (a = '', b = '') => {
        const lowerA = normalizeName(a);
        const lowerB = normalizeName(b);
        return lowerA === lowerB || lowerA.startsWith(lowerB) || lowerB.startsWith(lowerA);
      };
      const isMyTurn = status === 'PLAYING' && currentPlayer && namesMatch(currentPlayer.name, currentUserName);
      const myPlayer = players.find((player) => namesMatch(player.name, currentUserName));
      const myHand = myPlayer?.hand || [];
      
      // Debug: Kartlar neden görünmüyor?
      if (Platform.OS === 'web' && status === 'PLAYING') {
        console.log('[UNO Debug] Player info:', {
          currentUserName,
          gameStateSnapshotRoomId: gameStateSnapshot?.roomId,
          players: players.map(p => ({ 
            name: p.name, 
            handCount: p.handCount, 
            hasHand: !!p.hand, 
            handLength: p.hand?.length || 0,
            handType: typeof p.hand,
            handIsArray: Array.isArray(p.hand),
            handValue: p.hand, // Tam hand değerini göster
          })),
          myPlayer: myPlayer ? JSON.parse(JSON.stringify({ 
            name: myPlayer.name, 
            handCount: myPlayer.handCount, 
            hasHand: !!myPlayer.hand, 
            handLength: myPlayer.hand?.length || 0,
            handType: typeof myPlayer.hand,
            handIsArray: Array.isArray(myPlayer.hand),
            handValue: myPlayer.hand, // Tam hand değerini göster
            fullPlayer: myPlayer, // Tüm player objesini göster
          })) : null,
          myHandLength: myHand.length,
          myHandType: typeof myHand,
          myHandIsArray: Array.isArray(myHand),
          myHandValue: myHand, // Tam myHand değerini göster
          namesMatchTest: players.map(p => ({
            playerName: p.name,
            currentUserName,
            matches: namesMatch(p.name, currentUserName),
          })),
        });
      }
      const discardTop = gameStateSnapshot?.discardTop;
      const drawPileCount = gameStateSnapshot?.drawPileCount || 0;
      const pendingDraw = gameStateSnapshot?.pendingDraw || 0;
      const currentColor = gameStateSnapshot?.currentColor || discardTop?.color || 'wild';
      const winner = gameStateSnapshot?.winner;
      const minPlayers = gameStateSnapshot?.minPlayers || 2;
      const directionLabel = gameStateSnapshot?.playDirection === -1 ? '↺ Ters' : '↻ Düz';

      const onUnoCardPress = (card) => {
        if (!isMyTurn || status !== 'PLAYING' || isSubmittingUnoMove) {
          return;
        }
        // Wild kartlar için renk seçimi gerekli
        if (card.color === 'wild') {
          setPendingWildCard(card);
          return;
        }
        // Kart oynanabilir mi kontrol et
        const isPlayable = isCardPlayable(card, gameStateSnapshot);
        if (!isPlayable) {
          Alert.alert('UNO', 'Bu kartı şu anda oynayamazsın!');
          return;
        }
        handleUnoPlayCard(card.id);
      };

      const isCardPlayable = (card, gameState) => {
        if (!card || !gameState) return false;
        if (card.color === 'wild') return true;
        const currentColor = gameState.currentColor || gameState.discardTop?.color;
        const currentValue = gameState.currentValue || gameState.discardTop?.value;
        return card.color === currentColor || card.value === currentValue;
      };

      const isMobileDevice = (Platform.OS === 'web' && isMobile) || (Platform.OS !== 'web');
      
      return (
        <View style={[
          styles.gameHudContainer, 
          status === 'PLAYING' && isMyTurn && { borderColor: '#0FBF61' },
          isMobileDevice && styles.gameHudContainerMobile,
        ]}>
          <View style={styles.gameHudRow}>
            <View style={[styles.gameHudColumn, { flex: 1 }]}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Durum</Text>
              <Text style={[
                styles.gameHudValue,
                isMobileDevice && styles.gameHudValueMobile,
              ]}>
                {status === 'WAITING' ? 'Bekleniyor' :
                 status === 'PLAYING' ? 'Oynanıyor' :
                 status === 'FINISHED' ? 'Bitti' : status}
              </Text>
            </View>
            <View style={[styles.gameHudColumn, { flex: 1 }]}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Sıra</Text>
              <Text style={[
                styles.gameHudValue,
                isMyTurn && styles.unoCurrentTurn,
                isMobileDevice && styles.gameHudValueMobile,
              ]}>
                {status !== 'PLAYING' ? '—' : isMyTurn ? 'Sende' : (currentPlayer?.name || '—')}
              </Text>
            </View>
          </View>

          <View style={[
            styles.unoInfoRow,
            isMobileDevice && styles.unoInfoRowMobile,
          ]}>
            <View style={styles.unoColorBadge}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Renk</Text>
              <View style={[styles.unoColorChip, { backgroundColor: UNO_COLOR_STYLES[currentColor] || UNO_COLOR_STYLES.wild }]} />
              <Text style={styles.unoColorText}>
                {UNO_COLOR_LABELS[currentColor] || (currentColor?.toUpperCase?.() || 'Serbest')}
              </Text>
            </View>
            <View style={styles.unoInfoItem}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Destede</Text>
              <Text style={[
                styles.gameHudValue,
                isMobileDevice && styles.gameHudValueMobile,
              ]}>{drawPileCount}</Text>
            </View>
            <View style={styles.unoInfoItem}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Yön</Text>
              <Text style={[
                styles.gameHudValue,
                isMobileDevice && styles.gameHudValueMobile,
              ]}>{directionLabel}</Text>
            </View>
            {pendingDraw > 0 && (
              <View style={styles.unoInfoItem}>
                <Text style={[
                  styles.gameHudLabel,
                  isMobileDevice && styles.gameHudLabelMobile,
                ]}>Cezalı</Text>
                <Text style={[
                  styles.gameHudValue,
                  isMobileDevice && styles.gameHudValueMobile,
                ]}>+{pendingDraw}</Text>
              </View>
            )}
          </View>

          <View style={[
            styles.unoDiscardSection,
            isMobileDevice && styles.unoDiscardSectionMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Açık Kart</Text>
            {discardTop ? (
              <View style={[
                styles.unoDiscardCard,
                { backgroundColor: UNO_COLOR_STYLES[discardTop.color] || UNO_COLOR_STYLES.wild },
                discardTop.color === 'wild' && styles.unoDiscardCardWild,
                (Platform.OS === 'web' && isMobile) && styles.unoDiscardCardMobile,
                (Platform.OS !== 'web') && styles.unoDiscardCardMobile,
              ]}>
                <Text style={[
                  styles.unoDiscardLabel,
                  (Platform.OS === 'web' && isMobile) && styles.unoDiscardLabelMobile,
                  (Platform.OS !== 'web') && styles.unoDiscardLabelMobile,
                ]}>{getUnoCardText(discardTop)}</Text>
                {getUnoCardSubtext(discardTop) ? (
                  <Text style={[
                    styles.unoDiscardSubtext,
                    (Platform.OS === 'web' && isMobile) && styles.unoDiscardSubtextMobile,
                    (Platform.OS !== 'web') && styles.unoDiscardSubtextMobile,
                  ]}>{getUnoCardSubtext(discardTop)}</Text>
                ) : null}
              </View>
            ) : (
              <Text style={[
                styles.gameHudValue,
                isMobileDevice && styles.gameHudValueMobile,
              ]}>—</Text>
            )}
          </View>

          <View style={[
            styles.gameLeaderboard,
            isMobileDevice && styles.gameLeaderboardMobile,
          ]}>
            <Text style={[
              styles.gameHudLabel,
              isMobileDevice && styles.gameHudLabelMobile,
            ]}>Oyuncular</Text>
            {players.map((player, index) => {
              const hasUno = (player.handCount || 0) === 1;
              return (
                <View key={player.name || index} style={[
                  styles.gameLeaderboardItem,
                  isMobileDevice && styles.gameLeaderboardItemMobile,
                ]}>
                  <Text style={[
                    styles.gameLeaderboardName,
                    isMobileDevice && styles.gameLeaderboardNameMobile,
                  ]} numberOfLines={1}>
                    {player.name} {index === currentPlayerIndex && status === 'PLAYING' ? '▶️' : ''}
                  </Text>
                  <Text style={[
                    styles.gameLeaderboardScore,
                    isMobileDevice && styles.gameLeaderboardScoreMobile,
                  ]}>
                    {player.handCount || 0} kart{hasUno ? ' · UNO!' : ''}
                  </Text>
                </View>
              );
            })}
          </View>

          {status === 'PLAYING' && (
            <View style={[
              styles.unoHandContainer,
              isMobileDevice && styles.unoHandContainerMobile,
            ]}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Kartların ({myHand.length})</Text>
              {myHand.length === 0 ? (
                <Text style={[
                  styles.gameHudValue,
                  isMobileDevice && styles.gameHudValueMobile,
                ]}>Kartın yok</Text>
              ) : (
                <View style={[
                  styles.unoHandRow,
                  isMobileDevice && styles.unoHandRowMobile,
                ]}>
                  {myHand.map((card) => {
                    const isWild = card.color === 'wild';
                    const subtext = getUnoCardSubtext(card);
                    const canPlay = isMyTurn && status === 'PLAYING' && !isSubmittingUnoMove && isCardPlayable(card, gameStateSnapshot);
                    
                    return (
                      <TouchableOpacity
                        key={card.id}
                        style={[
                          styles.unoCard,
                          { backgroundColor: UNO_COLOR_STYLES[card.color] || UNO_COLOR_STYLES.wild },
                          (!canPlay) && styles.unoCardDisabled,
                          isWild && styles.unoCardWild,
                          canPlay && styles.unoCardPlayable,
                          // Mobil responsive ayarları
                          (Platform.OS === 'web' && isMobile) && styles.unoCardMobile,
                          (Platform.OS !== 'web') && styles.unoCardMobile,
                        ]}
                        onPress={() => onUnoCardPress(card)}
                        disabled={!canPlay}
                        activeOpacity={canPlay ? 0.6 : 1}
                      >
                        <Text style={[
                          styles.unoCardLabel,
                          (Platform.OS === 'web' && isMobile) && styles.unoCardLabelMobile,
                          (Platform.OS !== 'web') && styles.unoCardLabelMobile,
                        ]}>{getUnoCardText(card)}</Text>
                        {subtext ? (
                          <Text style={[
                            styles.unoCardSubtext,
                            (Platform.OS === 'web' && isMobile) && styles.unoCardSubtextMobile,
                            (Platform.OS !== 'web') && styles.unoCardSubtextMobile,
                          ]}>{subtext}</Text>
                        ) : null}
                        {!canPlay && isMyTurn && status === 'PLAYING' && (
                          <View style={styles.unoCardBlockedOverlay}>
                            <Text style={[
                              styles.unoCardBlockedText,
                              (Platform.OS === 'web' && isMobile) && styles.unoCardBlockedTextMobile,
                              (Platform.OS !== 'web') && styles.unoCardBlockedTextMobile,
                            ]}>✕</Text>
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          )}

          {pendingWildCard && isMyTurn && status === 'PLAYING' && (
            <View style={[
              styles.unoColorPicker,
              isMobileDevice && styles.unoColorPickerMobile,
            ]}>
              <Text style={[
                styles.gameHudLabel,
                isMobileDevice && styles.gameHudLabelMobile,
              ]}>Vahşi kart için renk seç</Text>
              <View style={[
                styles.unoColorPickerRow,
                isMobileDevice && styles.unoColorPickerRowMobile,
              ]}>
                {['red', 'yellow', 'green', 'blue'].map((color) => (
                  <TouchableOpacity
                    key={color}
                    style={[
                      styles.unoColorOption, 
                      { backgroundColor: UNO_COLOR_STYLES[color] },
                      isMobileDevice && styles.unoColorOptionMobile,
                    ]}
                    onPress={() => handleUnoPlayCard(pendingWildCard.id, color)}
                  >
                    <Text style={[
                      styles.unoColorOptionLabel,
                      isMobileDevice && styles.unoColorOptionLabelMobile,
                    ]}>{UNO_COLOR_LABELS[color]}</Text>
                  </TouchableOpacity>
                ))}
                <TouchableOpacity
                  style={[
                    styles.unoColorOption, 
                    styles.unoColorCancel,
                    isMobileDevice && styles.unoColorOptionMobile,
                  ]}
                  onPress={() => setPendingWildCard(null)}
                >
                  <Text style={[
                    styles.unoColorOptionLabel, 
                    styles.unoColorCancelLabel,
                    isMobileDevice && styles.unoColorOptionLabelMobile,
                  ]}>İptal</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          <View style={[
            styles.unoActionRow,
            isMobileDevice && styles.unoActionRowMobile,
          ]}>
            <Button
              mode="contained"
              onPress={handleUnoDrawCard}
              disabled={!isMyTurn || status !== 'PLAYING' || isSubmittingUnoMove}
              style={styles.unoActionButton}
            >
              Kart Çek
            </Button>
            {status === 'WAITING' && players.length >= minPlayers && (
              <Button
                mode="contained"
                onPress={handleStartUnoGame}
                style={[styles.unoActionButton, { backgroundColor: '#E94560' }]}
              >
                Oyunu Başlat
              </Button>
            )}
          </View>

          {pendingDraw > 0 && isMyTurn && status === 'PLAYING' && (
            <Text style={styles.unoInfoText}>
              {`Önce ${pendingDraw} kart çekmelisin.`}
            </Text>
          )}
          {winner && status === 'FINISHED' && (
            <View>
              <Text style={styles.unoInfoText}>
                {winner === currentUserName ? 'Kazandın! 🎉' : `${winner} kazandı!`}
              </Text>
              <Button
                mode="contained"
                onPress={handleRestartUnoGame}
                style={[styles.unoActionButton, { backgroundColor: '#E94560', marginTop: 12 }]}
              >
                Yeniden Başla
              </Button>
            </View>
          )}
          {status === 'WAITING' && players.length < minPlayers && (
            <Text style={styles.unoInfoText}>
              {`Oyun için en az ${minPlayers} oyuncu gerekli. (${players.length}/${minPlayers})`}
            </Text>
          )}
        </View>
      );
    }

    // Mystery Word Hunt için özel HUD
    if (isMysteryWordHunt) {
      const letterCount = gameStateSnapshot?.letterCount || 0;
      const firstLetter = (gameStateSnapshot?.firstLetter || '—').toUpperCase();
      let wordProgress = firstLetter;
      if (letterCount > 1) {
        const underscores = Array(Math.max(letterCount - 1, 0))
          .fill('_')
          .join(' ');
        wordProgress = `${firstLetter} ${underscores}`.trim();
      }
      if (letterCount === 0) {
        wordProgress = '— —';
      }

      return (
        <View style={styles.gameHudContainer}>
          <View style={styles.gameHudRow}>
            <View style={[styles.gameHudColumn, { flex: 2 }]}>
              <Text style={styles.gameHudLabel}>Kelime</Text>
              <Animated.View
                style={[
                  styles.mysteryWordValueWrapper,
                  {
                    transform: [{ scale: wordPulseScale }],
                    opacity: wordPulseOpacity,
                  },
                ]}
              >
                <Text style={[styles.gameHudValue, styles.mysteryWordValue]}>
                  {wordProgress}
                </Text>
              </Animated.View>
            </View>
            <View style={styles.gameHudColumn}>
              <Text style={styles.gameHudLabel}>Süre</Text>
              <Text style={styles.gameHudValue}>{formatTimeLeft()}</Text>
            </View>
          </View>
          {gameStateSnapshot?.currentQuestion ? (
            <Animated.View
              style={[
                styles.gameAnnouncement,
                styles.mysteryQuestionCard,
                { opacity: questionPulseOpacity },
              ]}
            >
              <Text style={styles.gameHudLabel}>Soru</Text>
              <Text style={styles.mysteryQuestionText}>
                {gameStateSnapshot.currentQuestion}
              </Text>
            </Animated.View>
          ) : null}
          {gameStateSnapshot?.scores && Object.keys(gameStateSnapshot.scores).length > 0 ? (
            <View style={styles.gameLeaderboard}>
              <Text style={styles.gameHudLabel}>Puanlar</Text>
              {Object.entries(gameStateSnapshot.scores)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([name, score]) => (
                  <View key={name} style={styles.gameLeaderboardItem}>
                    <Text style={styles.gameLeaderboardName} numberOfLines={1}>{name}</Text>
                    <Text style={styles.gameLeaderboardScore}>{score}</Text>
                  </View>
                ))}
            </View>
          ) : null}
        </View>
      );
    }

    // OKEY için özel HUD - Gerçekçi masa görünümü - KALDIRILDI
    if (false && isOkey) {
      const isMobileDevice = (Platform.OS === 'web' && isMobile) || (Platform.OS !== 'web');
      const players = gameStateSnapshot?.players || [];
      const currentPlayerIndex = gameStateSnapshot?.currentPlayerIndex || 0;
      const status = gameStateSnapshot?.status || 'WAITING';
      const currentPlayer = players[currentPlayerIndex];
      const sessionUserName = gameStateSnapshot?.sessionUserName || userName;
      const isMyTurn = status === 'PLAYING' && currentPlayer?.name === sessionUserName;
      const myPlayer = players.find((player) => player.name === sessionUserName);
      const myHand = Array.isArray(myPlayer?.hand) ? myPlayer.hand : [];
      const okeyTile = gameStateSnapshot?.okeyTile;
      const stockCount = gameStateSnapshot?.stockCount || 0;
      const discardPileTop = gameStateSnapshot?.discardPileTop;
      const discardPileCount = gameStateSnapshot?.discardPileCount || 0;
      const playersLastDiscardedTiles = gameStateSnapshot?.playersLastDiscardedTiles || [];
      const round = gameStateSnapshot?.round || 1;
      const myScore = myPlayer?.score || 0;
      const myPlayerIndex = players.findIndex(p => p.name === sessionUserName);
      
      // Her oyuncunun son attığı taşı bul
      const getPlayerLastDiscardedTile = (playerName) => {
        const playerData = playersLastDiscardedTiles.find(p => p.name === playerName);
        return playerData?.lastDiscardedTile || null;
      };
      
      // Önceki oyuncunun index'i (benim solumdaki oyuncu)
      const previousPlayerIndex = myPlayerIndex > 0 ? myPlayerIndex - 1 : players.length - 1;
      const previousPlayer = players[previousPlayerIndex];
      const previousPlayerDiscardedTile = previousPlayer ? getPlayerLastDiscardedTile(previousPlayer.name) : null;
      
      // Benim attığım taş
      const myLastDiscardedTile = getPlayerLastDiscardedTile(sessionUserName);

      // Oyuncuları pozisyonlara göre sırala: [Alt (Ben), Üst, Sağ, Sol]
      const getPlayerPosition = (index, myIndex) => {
        if (index === myIndex) return 'bottom'; // Ben alt kısımda
        const relativeIndex = (index - myIndex + players.length) % players.length;
        if (relativeIndex === 1) return 'top';
        if (relativeIndex === 2) return 'right';
        if (relativeIndex === 3) return 'left';
        return 'top'; // Fallback
      };

      // Taş renk yardımcı fonksiyonları
      const getOkeyTileColorStyle = (color) => {
        switch (color) {
          case 'Sarı': return '#FF8C00'; // Turuncu/Sarı
          case 'Mavi': return '#4169E1';
          case 'Siyah': return '#000000';
          case 'Kırmızı': return '#DC143C';
          case 'Joker': return '#9370DB';
          default: return '#FFFFFF';
        }
      };

      const getOkeyTileColorBg = (color) => {
        switch (color) {
          case 'Sarı': return '#FFF8DC';
          case 'Mavi': return '#E6F3FF';
          case 'Siyah': return '#F5F5F5';
          case 'Kırmızı': return '#FFE4E1';
          case 'Joker': return '#F0E6FF';
          default: return '#FFFFFF';
        }
      };

      // Istaka ref'i ve taş genişliği (web için drag & drop)
      const rackRef = useRef(null);
      const tileWidth = isMobileDevice ? 26 : 30; // Taş genişliği (margin dahil)

      // orderedHand senkronizasyonu - myHand değiştiğinde (pozisyonları da koru)
      const syncOrderedHand = () => {
        if (!myHand || myHand.length === 0) {
          if (orderedHand.length > 0) {
            setOrderedHand([]);
          }
          return;
        }

        const getTileId = (t) => `${t?.color}-${t?.number}-${t?.isFakeOkey ? '1' : '0'}`;
        const currentTileWidth = isMobileDevice ? 26 : 30; // Fonksiyon içinde hesapla

        // Backend'den gelen taşlar zaten posX/posY bilgisi içeriyorsa, bunları koru
        const hasPositionData = myHand.some(t => typeof t?.posX === 'number' && typeof t?.posY === 'number');
        
        if (hasPositionData && orderedHand.length === myHand.length) {
          // Backend'den gelen pozisyonları kullan ve koru
          // Mevcut orderedHand'taki pozisyonları backend'den gelenlerle birleştir
          const next = myHand.map((t, idx) => {
            // Backend'den gelen pozisyon var mı?
            if (typeof t?.posX === 'number' && typeof t?.posY === 'number') {
              return { ...t, posX: t.posX, posY: t.posY };
            }
            // Mevcut orderedHand'ta bu taşın pozisyonu var mı?
            const existingTile = orderedHand.find(ot => getTileId(ot) === getTileId(t));
            if (existingTile && typeof existingTile?.posX === 'number' && typeof existingTile?.posY === 'number') {
              return { ...t, posX: existingTile.posX, posY: existingTile.posY };
            }
            // Hiçbiri yoksa varsayılan pozisyon
            const perRow = Math.ceil(myHand.length / 2) || 1;
            const tileH = isMobileDevice ? 36 : 42;
            const gap = 4;
            const row = Math.floor(idx / perRow);
            const col = idx % perRow;
            return {
              ...t,
              posX: col * (currentTileWidth + gap),
              posY: row * (tileH + gap),
            };
          });
          setOrderedHand(next);
          return;
        }

        if (orderedHand.length === 0 || orderedHand.length !== myHand.length) {
          // İlk yükleme veya uzunluk değişti: temel pozisyonları hesapla
          // Ancak backend'den gelen pozisyonları öncelikle kullan
          const perRow = Math.ceil(myHand.length / 2) || 1;
          const tileH = isMobileDevice ? 36 : 42;
          const gap = 4;
          const next = myHand.map((t, idx) => {
            // Backend'den gelen pozisyon var mı?
            if (typeof t?.posX === 'number' && typeof t?.posY === 'number') {
              return { ...t, posX: t.posX, posY: t.posY };
            }
            // Yoksa varsayılan pozisyon hesapla
            const row = Math.floor(idx / perRow);
            const col = idx % perRow;
            return {
              ...t,
              posX: col * (currentTileWidth + gap),
              posY: row * (tileH + gap),
            };
          });
          setOrderedHand(next);
          return;
        }

        // İçerik aynı mı (taş seti aynı mı)? Evetse mevcut pozisyonları koru
        const currentIds = new Set(orderedHand.map(getTileId));
        const newIds = new Set(myHand.map(getTileId));
        const sameContent =
          orderedHand.length === myHand.length &&
          [...currentIds].every((id) => newIds.has(id)) &&
          [...newIds].every((id) => currentIds.has(id));

        if (!sameContent) {
          // Taş seti değişti - yeni taşlar için pozisyon hesapla, mevcut olanların pozisyonlarını koru
          const perRow = Math.ceil(myHand.length / 2) || 1;
          const tileH = isMobileDevice ? 36 : 42;
          const gap = 4;
          const next = myHand.map((t, idx) => {
            // Backend'den gelen pozisyon var mı?
            if (typeof t?.posX === 'number' && typeof t?.posY === 'number') {
              return { ...t, posX: t.posX, posY: t.posY };
            }
            // Mevcut orderedHand'ta bu taşın pozisyonu var mı?
            const existingTile = orderedHand.find(ot => getTileId(ot) === getTileId(t));
            if (existingTile && typeof existingTile?.posX === 'number' && typeof existingTile?.posY === 'number') {
              return { ...t, posX: existingTile.posX, posY: existingTile.posY };
            }
            // Yeni taş için varsayılan pozisyon
            const row = Math.floor(idx / perRow);
            const col = Math.max(0, Math.min(perRow - 1, idx));
            return {
              ...t,
              posX: col * (currentTileWidth + gap),
              posY: row * (tileH + gap),
            };
          });
          setOrderedHand(next);
        } else {
          // İçerik aynı - backend'den gelen pozisyonları kullan veya mevcut pozisyonları koru
          const next = myHand.map((t) => {
            // Backend'den gelen pozisyon var mı?
            if (typeof t?.posX === 'number' && typeof t?.posY === 'number') {
              return { ...t, posX: t.posX, posY: t.posY };
            }
            // Mevcut orderedHand'ta bu taşın pozisyonu var mı?
            const existingTile = orderedHand.find(ot => getTileId(ot) === getTileId(t));
            if (existingTile && typeof existingTile?.posX === 'number' && typeof existingTile?.posY === 'number') {
              return { ...t, posX: existingTile.posX, posY: existingTile.posY };
            }
            // Fallback: mevcut taşı koru
            return t;
          });
          setOrderedHand(next);
        }
      };
      
      // İlk yükleme veya uzunluk değiştiğinde syncOrderedHand'ı çağır
      // Backend'den gelen pozisyon bilgilerini korumak için syncOrderedHand fonksiyonu içinde işleniyor
      if (myHand.length > 0 && (orderedHand.length === 0 || orderedHand.length !== myHand.length)) {
        syncOrderedHand();
      }

      // Taşları iki satıra böl (görsel gruplama; gerçek konum posX/posY ile belirlenir)
      const { tilesPerRow, firstRowTiles, secondRowTiles } = useMemo(() => {
        const perRow = Math.ceil(orderedHand.length / 2) || 1;
        const first = orderedHand.slice(0, perRow);
        const second = orderedHand.slice(perRow);
        return { tilesPerRow: perRow, firstRowTiles: first, secondRowTiles: second };
      }, [orderedHand]);

      // Seçili taş kontrolü
      const isTileSelected = (tile, index) => {
        return selectedTiles.some(t => t.color === tile.color && t.number === tile.number && t.index === index);
      };

      // Drag & drop handler'ları - dragStartIndexRef component seviyesinde tanımlı

      // Serbest sürükleme için hover index kullanılmıyor
      const handleTileDragOver = useCallback(() => {}, []);

      const roomId = room?.roomId || gameStateSnapshot?.roomId || activeRoomId;

      // Taş sırasını güncelleme fonksiyonu - doğrudan orderedHand'i günceller
      const updateTileOrder = useCallback((sourceIndex, targetIndex) => {
        if (sourceIndex === targetIndex) {
          console.log('[OKEY] updateTileOrder: Aynı index, güncelleme yok', { sourceIndex, targetIndex });
          return;
        }
        
        console.log('[OKEY] updateTileOrder çağrıldı', { sourceIndex, targetIndex });
        
        setOrderedHand((prev) => {
          if (prev.length === 0) {
            console.log('[OKEY] updateTileOrder: orderedHand boş, iptal');
            return prev;
          }
          
          const newOrder = [...prev];
          
          // Index'leri sınırla
          const safeSourceIndex = Math.max(0, Math.min(sourceIndex, newOrder.length - 1));
          const safeTargetIndex = Math.max(0, Math.min(targetIndex, newOrder.length - 1));
          
          if (safeSourceIndex === safeTargetIndex) {
            console.log('[OKEY] updateTileOrder: Safe indexler aynı, güncelleme yok');
            return prev;
          }
          
          // Taşı çıkar
          const [draggedTile] = newOrder.splice(safeSourceIndex, 1);
          
          // Taşı yeni yerine ekle (çıkarma sonrası mevcut index kullanılır)
          newOrder.splice(safeTargetIndex, 0, draggedTile);
          
          console.log('[OKEY] updateTileOrder: Taş taşındı', { 
            draggedTile: `${draggedTile?.color}-${draggedTile?.number}`,
            sourceIndex: safeSourceIndex,
            targetIndex: safeTargetIndex,
            newOrderLength: newOrder.length
          });

          // Sunucu ile el sırasını senkronize et
          try {
            if (socket && roomId && sessionUserName) {
              socket.emit('submitOkeySort', {
                roomId,
                playerName: sessionUserName,
                hand: newOrder,
              });
            }
          } catch (error) {
            console.error('[OKEY] submitOkeySort emit hatası:', error);
          }
          
          return newOrder;
        });
      }, [socket, roomId, sessionUserName]);

      // Drag başlatma
      const handleTileDragStart = useCallback((index) => {
        dragStartIndexRef.current = index;
        setDraggedTileIndex(index);
      }, []);

      // Drag bitirme (drop) - serbest konumlandırma için yeni pozisyonu kaydet
      const handleTileDragEnd = useCallback((index, newX, newY) => {
        console.log('[OKEY] handleTileDragEnd (free)', { index, newX, newY, orderedHandLength: orderedHand.length });

        if (index === null || index === undefined) {
          console.log('[OKEY] handleTileDragEnd: Geçersiz index', { index });
          setDraggedTileIndex(null);
          dragStartIndexRef.current = null;
          return;
        }

        setOrderedHand((prev) => {
          if (!prev || !prev[index]) return prev;
          const next = [...prev];
          const tile = next[index] ? { ...next[index], posX: newX, posY: newY } : next[index];
          next[index] = tile;

          // Yeni dizilimi/pozisyonları sunucuya gönder
          try {
            if (socket && roomId && sessionUserName) {
              socket.emit('submitOkeySort', {
                roomId,
                playerName: sessionUserName,
                hand: next,
              });
            }
          } catch (error) {
            console.error('[OKEY] submitOkeySort (free) hata:', error);
          }

          return next;
        });

        setDraggedTileIndex(null);
        dragStartIndexRef.current = null;
      }, [socket, roomId, sessionUserName, orderedHand.length]);

      // Tıklama ile taş seçme ve yerleştirme (click-to-select, click-to-place)
      const handleTilePress = useCallback((index) => {
        console.log('[OKEY] handleTilePress', { 
          index, 
          draggedTileIndex, 
          isMyTurn, 
          status, 
          myHandLength: myHand.length,
          orderedHandLength: orderedHand.length
        });
        
        if (!isMyTurn || status !== 'PLAYING' || isSubmittingOkeyMove) {
          console.log('[OKEY] handleTilePress: Koşullar uygun değil', { isMyTurn, status, isSubmittingOkeyMove });
          return;
        }
        
        if (myHand.length === 15 && selectedTiles.length === 0 && draggedTileIndex === null) {
          // 15 taş varsa ve seçili taş yoksa discard
          console.log('[OKEY] handleTilePress: 15 taş var, discard yapılıyor', { index, tile: orderedHand[index] });
          handleDiscardOkeyTile(orderedHand[index]);
          return;
        }
        
        if (draggedTileIndex === null) {
          // İlk taşa basıldı - seç
          console.log('[OKEY] handleTilePress: Taş seçildi', { index });
          dragStartIndexRef.current = index;
          setDraggedTileIndex(index);
        } else if (draggedTileIndex === index) {
          // Aynı taşa tekrar basıldı - seçimi iptal
          console.log('[OKEY] handleTilePress: Seçim iptal edildi', { index });
          setDraggedTileIndex(null);
          dragStartIndexRef.current = null;
        } else {
          // Başka bir taşa basıldı - yer değiştir
          const sourceIndex = dragStartIndexRef.current;
          console.log('[OKEY] handleTilePress: Yer değiştirme', { sourceIndex, targetIndex: index });
          if (sourceIndex !== null) {
            updateTileOrder(sourceIndex, index);
          }
          setDraggedTileIndex(null);
          dragStartIndexRef.current = null;
        }
      }, [isMyTurn, status, isSubmittingOkeyMove, myHand.length, selectedTiles.length, draggedTileIndex, orderedHand, handleDiscardOkeyTile, updateTileOrder]);

      return (
        <View style={[styles.okeyTableContainer, isMobileDevice && styles.okeyTableContainerMobile]}>
          {/* Yeşil çuha masa zemin */}
          <View style={[styles.okeyTableFelt, isMobileDevice && styles.okeyTableFeltMobile]}>
            {/* Üst oyuncu */}
            {players.map((player, index) => {
              if (index === myPlayerIndex) return null;
              const position = getPlayerPosition(index, myPlayerIndex);
              if (position !== 'top') return null;
              return (
                <View key={player.name} style={styles.okeyPlayerTop}>
                  <View style={styles.okeyPlayerPanel}>
                    <Text style={styles.okeyPlayerName}>{player.name}</Text>
                    <Text style={styles.okeyPlayerScore}>{player.score || 0}</Text>
                  </View>
                  {/* Oyuncunun attığı taş (üst sağ köşe) */}
                  {status === 'PLAYING' && getPlayerLastDiscardedTile(player.name) && (
                    <View style={[styles.okeyPlayerDiscardCorner, { top: 0, right: 0 }]}>
                      <TouchableOpacity
                        disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                        onPress={() => {
                          if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                            handleDrawOkeyTile(false);
                          }
                        }}
                      >
                        <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                          <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]}>
                            {getPlayerLastDiscardedTile(player.name).number === 0 ? 'J' : getPlayerLastDiscardedTile(player.name).number}
                          </Text>
                          <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]} />
                        </View>
                      </TouchableOpacity>
                    </View>
                  )}
                  {index === currentPlayerIndex && status === 'PLAYING' && (
                    <View style={styles.okeyTurnIndicator}>
                      <Text style={styles.okeyTurnArrow}>▶</Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Sol oyuncu */}
            {players.map((player, index) => {
              if (index === myPlayerIndex) return null;
              const position = getPlayerPosition(index, myPlayerIndex);
              if (position !== 'left') return null;
              return (
                <View key={player.name} style={styles.okeyPlayerLeft}>
                  <View style={styles.okeyPlayerPanel}>
                    <Text style={styles.okeyPlayerName}>{player.name}</Text>
                    <Text style={styles.okeyPlayerScore}>{player.score || 0}</Text>
                  </View>
                  {/* Oyuncunun attığı taş (sağ köşe) */}
                  {status === 'PLAYING' && getPlayerLastDiscardedTile(player.name) && (
                    <View style={[styles.okeyPlayerDiscardCorner, { top: -20, right: -20 }]}>
                      <TouchableOpacity
                        disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                        onPress={() => {
                          if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                            handleDrawOkeyTile(false);
                          }
                        }}
                      >
                        <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                          <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]}>
                            {getPlayerLastDiscardedTile(player.name).number === 0 ? 'J' : getPlayerLastDiscardedTile(player.name).number}
                          </Text>
                          <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]} />
                        </View>
                      </TouchableOpacity>
                    </View>
                  )}
                  {index === currentPlayerIndex && status === 'PLAYING' && (
                    <View style={styles.okeyTurnIndicator}>
                      <Text style={styles.okeyTurnArrow}>▶</Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Sağ oyuncu */}
            {players.map((player, index) => {
              if (index === myPlayerIndex) return null;
              const position = getPlayerPosition(index, myPlayerIndex);
              if (position !== 'right') return null;
              return (
                <View key={player.name} style={styles.okeyPlayerRight}>
                  <View style={styles.okeyPlayerPanel}>
                    <Text style={styles.okeyPlayerName}>{player.name}</Text>
                    <Text style={styles.okeyPlayerScore}>{player.score || 0}</Text>
                  </View>
                  {/* Oyuncunun attığı taş (sağ üst köşe) */}
                  {status === 'PLAYING' && getPlayerLastDiscardedTile(player.name) && (
                    <View style={[styles.okeyPlayerDiscardCorner, { top: 0, right: 0 }]}>
                      <TouchableOpacity
                        disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                        onPress={() => {
                          if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                            handleDrawOkeyTile(false);
                          }
                        }}
                      >
                        <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                          <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]}>
                            {getPlayerLastDiscardedTile(player.name).number === 0 ? 'J' : getPlayerLastDiscardedTile(player.name).number}
                          </Text>
                          <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(getPlayerLastDiscardedTile(player.name).color) }]} />
                        </View>
                      </TouchableOpacity>
                    </View>
                  )}
                  {index === currentPlayerIndex && status === 'PLAYING' && (
                    <View style={styles.okeyTurnIndicator}>
                      <Text style={styles.okeyTurnArrow}>▶</Text>
                    </View>
                  )}
                </View>
              );
            })}

            {/* Merkez alan - Çekme destesi ve gösterge taşı */}
            {status === 'PLAYING' && (
              <View style={styles.okeyCenterArea}>
                {/* Gösterge taşı (Okey) */}
                {okeyTile && (
                  <View style={styles.okeyIndicatorTile}>
                    <View style={[styles.okeyRealisticTileCenter, isMobileDevice && styles.okeyRealisticTileCenterMobile, { backgroundColor: '#F5F5DC' }]}>
                      <Text style={[styles.okeyRealisticTileNumberCenter, isMobileDevice && styles.okeyRealisticTileNumberCenterMobile, { color: getOkeyTileColorStyle(okeyTile.color) }]}>
                        {okeyTile.number === 0 ? 'J' : okeyTile.number}
                      </Text>
                      <View style={[styles.okeyRealisticTileColorCircleCenter, isMobileDevice && styles.okeyRealisticTileColorCircleCenterMobile, { backgroundColor: getOkeyTileColorStyle(okeyTile.color) }]} />
                    </View>
                    <Text style={styles.okeyIndicatorLabel}>Okey</Text>
                  </View>
                )}

                {/* Çekme destesi (Stock) */}
                <TouchableOpacity
                  style={styles.okeyStockPile}
                  disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                  onPress={() => {
                    if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                      handleDrawOkeyTile(true);
                    }
                  }}
                >
                  <View style={[styles.okeyStockTileBack, isMobileDevice && styles.okeyStockTileBackMobile]}>
                    <Text style={[styles.okeyStockCount, isMobileDevice && styles.okeyStockCountMobile]}>{stockCount}</Text>
                  </View>
                </TouchableOpacity>

                {/* Önceki oyuncunun attığı taş (çekilebilir) */}
                {discardPileTop && (
                  <TouchableOpacity
                    style={styles.okeyDiscardPile}
                    disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                    onPress={() => {
                      if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                        handleDrawOkeyTile(false);
                      }
                    }}
                  >
                    <View style={[styles.okeyRealisticTileCenter, isMobileDevice && styles.okeyRealisticTileCenterMobile, { backgroundColor: '#F5F5DC' }]}>
                      <Text style={[styles.okeyRealisticTileNumberCenter, isMobileDevice && styles.okeyRealisticTileNumberCenterMobile, { color: getOkeyTileColorStyle(discardPileTop.color) }]}>
                        {discardPileTop.number === 0 ? 'J' : discardPileTop.number}
                      </Text>
                      <View style={[styles.okeyRealisticTileColorCircleCenter, isMobileDevice && styles.okeyRealisticTileColorCircleCenterMobile, { backgroundColor: getOkeyTileColorStyle(discardPileTop.color) }]} />
                    </View>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Alt köşeler - Aktif oyuncu için discard pile'lar */}
            {/* Sol alt köşe - Önceki oyuncunun attığı taş */}
            {status === 'PLAYING' && previousPlayerDiscardedTile && (
              <View style={[styles.okeyPlayerDiscardCorner, { bottom: 0, left: 0 }]}>
                <TouchableOpacity
                  disabled={!isMyTurn || isSubmittingOkeyMove || myHand.length === 15}
                  onPress={() => {
                    if (isMyTurn && !isSubmittingOkeyMove && myHand.length !== 15) {
                      handleDrawOkeyTile(false);
                    }
                  }}
                >
                  <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                    <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(previousPlayerDiscardedTile.color) }]}>
                      {previousPlayerDiscardedTile.number === 0 ? 'J' : previousPlayerDiscardedTile.number}
                    </Text>
                    <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(previousPlayerDiscardedTile.color) }]} />
                  </View>
                </TouchableOpacity>
              </View>
            )}
            {/* Sağ alt köşe - Benim attığım taş */}
            {status === 'PLAYING' && myLastDiscardedTile && (
              <View style={[styles.okeyPlayerDiscardCorner, { bottom: 0, right: 0 }]}>
                <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                  <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(myLastDiscardedTile.color) }]}>
                    {myLastDiscardedTile.number === 0 ? 'J' : myLastDiscardedTile.number}
                  </Text>
                  <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(myLastDiscardedTile.color) }]} />
                </View>
              </View>
            )}
          </View>

          {/* Alt kısım - Aktif oyuncu (Ben) */}
          {status === 'PLAYING' && myPlayer && (
            <View style={[styles.okeyMyPlayerArea, isMobileDevice && styles.okeyMyPlayerAreaMobile]}>
              {/* Oyuncu paneli */}
              <View style={styles.okeyMyPlayerPanel}>
                <Text style={styles.okeyMyPlayerName}>{myPlayer.name}</Text>
                <Text style={styles.okeyMyPlayerScore}>{myScore} puan</Text>
                {isMyTurn && (
                  <View style={styles.okeyMyTurnBadge}>
                    <Text style={styles.okeyMyTurnText}>Sıra Sende</Text>
                  </View>
                )}
              </View>

              {/* Ahşap dokulu ıstaka */}
              <View style={[styles.okeyRackContainer, isMobileDevice && styles.okeyRackContainerMobile]}>
                
                {Platform.OS === 'web' ? (
                  <div
                    ref={rackRef}
                    style={{
                      backgroundColor: '#DEB887',
                      borderRadius: 8,
                      padding: isMobileDevice ? 6 : 8,
                      border: '2px solid #A0522D',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                      position: 'relative',
                      minHeight: isMobileDevice ? 90 : 110,
                      width: '100%',
                    }}
                  >
                    {/* Tüm taşları absolute positioning ile tek container içinde render et */}
                    {orderedHand.map((tile, index) => {
                      if (!tile || typeof tile !== 'object') return null;
                      const tileColor = tile.color || '';
                      const tileNumber = tile.number !== undefined ? tile.number : '';
                      const tileSelected = isTileSelected(tile, index);
                      const isDragging = draggedTileIndex === index;
                      const { posX = 0, posY = 0 } = tile;
                      return (
                        <OkeyDraggableTile
                          key={`okey-tile-${tileColor}-${tileNumber}-${index}`}
                          tile={tile}
                          index={index}
                          baseX={posX}
                          baseY={posY}
                          tileColor={tileColor}
                          tileNumber={tileNumber}
                          tileSelected={tileSelected}
                          isDragging={isDragging}
                          isMyTurn={isMyTurn}
                          status={status}
                          isSubmittingOkeyMove={isSubmittingOkeyMove}
                          selectedTiles={selectedTiles}
                          myHandLength={myHand.length}
                          isMobileDevice={isMobileDevice}
                          styles={styles}
                          getOkeyTileColorStyle={getOkeyTileColorStyle}
                          rackRef={rackRef}
                          tileWidth={tileWidth}
                          totalTiles={orderedHand.length}
                          onLongPress={() => {}}
                          onPress={() => handleTilePress(index)}
                          onDragStart={handleTileDragStart}
                          onDragOver={handleTileDragOver}
                          onDragEnd={handleTileDragEnd}
                        />
                      );
                    })}
                  </div>
                ) : (
                <View 
                  ref={rackRef}
                  style={[
                    styles.okeyRack, 
                    isMobileDevice && styles.okeyRackMobile,
                    { position: 'relative' }
                  ]}
                >
                  {/* Tüm taşları absolute positioning ile tek container içinde render et */}
                  {orderedHand.map((tile, index) => {
                    if (!tile || typeof tile !== 'object') return null;
                    const tileColor = tile.color || '';
                    const tileNumber = tile.number !== undefined ? tile.number : '';
                    const tileSelected = isTileSelected(tile, index);
                    const isDragging = draggedTileIndex === index;
                    const { posX = 0, posY = 0 } = tile;
                    return (
                      <OkeyDraggableTile
                        key={`okey-tile-native-${tileColor}-${tileNumber}-${index}`}
                        tile={tile}
                        index={index}
                        baseX={posX}
                        baseY={posY}
                        tileColor={tileColor}
                        tileNumber={tileNumber}
                        tileSelected={tileSelected}
                        isDragging={isDragging}
                        isMyTurn={isMyTurn}
                        status={status}
                        isSubmittingOkeyMove={isSubmittingOkeyMove}
                        selectedTiles={selectedTiles}
                        myHandLength={myHand.length}
                        isMobileDevice={isMobileDevice}
                        styles={styles}
                        getOkeyTileColorStyle={getOkeyTileColorStyle}
                        rackRef={rackRef}
                        tileWidth={tileWidth}
                        totalTiles={orderedHand.length}
                        onLongPress={() => {
                          if (tileSelected) {
                            setSelectedTiles(selectedTiles.filter(t => !(t.color === tile.color && t.number === tile.number && t.index === index)));
                          } else {
                            setSelectedTiles([...selectedTiles, { ...tile, index }]);
                          }
                        }}
                        onPress={() => handleTilePress(index)}
                        onDragStart={handleTileDragStart}
                        onDragOver={handleTileDragOver}
                        onDragEnd={handleTileDragEnd}
                      />
                    );
                  })}
                </View>
                )}
                
                {/* Benim attığım taş (sağ alt köşe) */}
                {status === 'PLAYING' && myLastDiscardedTile && (
                  <View style={[styles.okeyPlayerDiscardCorner, { bottom: 0, right: 0 }]}>
                    <View style={[styles.okeyRealisticTileSmall, isMobileDevice && styles.okeyRealisticTileSmallMobile, { backgroundColor: '#F5F5DC' }]}>
                      <Text style={[styles.okeyRealisticTileNumberSmall, isMobileDevice && styles.okeyRealisticTileNumberSmallMobile, { color: getOkeyTileColorStyle(myLastDiscardedTile.color) }]}>
                        {myLastDiscardedTile.number === 0 ? 'J' : myLastDiscardedTile.number}
                      </Text>
                      <View style={[styles.okeyRealisticTileColorCircleSmall, isMobileDevice && styles.okeyRealisticTileColorCircleSmallMobile, { backgroundColor: getOkeyTileColorStyle(myLastDiscardedTile.color) }]} />
                    </View>
                  </View>
                )}
              </View>

              {/* Seçili taşlar için seri oluşturma butonu */}
              {selectedTiles.length >= 3 && isMyTurn && (
                <TouchableOpacity
                  style={styles.okeyGroupButton}
                  onPress={() => {
                    setTileGroups([...tileGroups, [...selectedTiles]]);
                    setSelectedTiles([]);
                  }}
                >
                  <Text style={styles.okeyGroupButtonText}>Seri Oluştur ({selectedTiles.length})</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Bekleme durumu */}
          {status === 'WAITING' && (
            <View style={styles.okeyWaitingArea}>
              <Text style={styles.okeyWaitingText}>
                {`Oyun için 4 oyuncu gerekli. (${players.length}/4)`}
              </Text>
            </View>
          )}
        </View>
      );
    }

    // Kategori Savaşı için HUD
    return (
      <View style={styles.gameHudContainer}>
        <View style={styles.gameHudRow}>
          <View style={styles.gameHudColumn}>
            <Text style={styles.gameHudLabel}>Harf</Text>
            <Text style={styles.gameHudValue}>{gameStateSnapshot?.currentLetter || '—'}</Text>
          </View>
          <View style={[styles.gameHudColumn, { flex: 2 }]}>
            <Text style={styles.gameHudLabel}>Kategori</Text>
            <Text style={[styles.gameHudValue, styles.gameHudCategory]}>
              {gameStateSnapshot?.currentCategory || 'Hazırlanıyor'}
            </Text>
          </View>
          <View style={styles.gameHudColumn}>
            <Text style={styles.gameHudLabel}>Süre</Text>
            <Text style={styles.gameHudValue}>{formatTimeLeft()}</Text>
          </View>
        </View>
        <View style={styles.gamePhaseBadge}>
          <Text style={styles.gamePhaseText}>{getPhaseLabel()}</Text>
        </View>
        {gameStateSnapshot?.leaderboard?.length ? (
          <View style={styles.gameLeaderboard}>
            {gameStateSnapshot.leaderboard.slice(0, 4).map((entry) => (
              <View key={`${entry.name}-${entry.score}`} style={styles.gameLeaderboardItem}>
                <Text style={styles.gameLeaderboardName} numberOfLines={1}>{entry.name}</Text>
                <Text style={styles.gameLeaderboardScore}>{entry.score}</Text>
              </View>
            ))}
          </View>
        ) : null}
        {gameStateSnapshot?.usedWords?.length ? (
          <View style={styles.gameUsedWords}>
            <Text style={styles.gameHudLabel}>Son Kelimeler</Text>
            <View style={styles.gameUsedWordsList}>
              {gameStateSnapshot.usedWords.slice(-6).reverse().map((entry, idx) => (
                <View key={`${entry.word}-${idx}`} style={styles.gameUsedWordChip}>
                  <Text style={styles.gameUsedWordText}>{entry.word}</Text>
                  <Text style={styles.gameUsedWordPlayer} numberOfLines={1}>{entry.player}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}
        {gameStateSnapshot?.lastAnnouncement ? (
          <View style={styles.gameAnnouncement}>
            <Text style={styles.gameAnnouncementText}>
              {gameStateSnapshot.lastAnnouncement}
            </Text>
          </View>
        ) : null}
      </View>
    );
    } catch (error) {
      // Hata durumunda boş sayfa yerine hata mesajı göster
      console.error('[ChatRoom] renderGameHud hatası:', error);
      if (Platform.OS !== 'web') {
        // Mobil cihazlarda hata mesajı göster
        return (
          <View style={styles.gameHudContainer}>
            <Text style={styles.gameHudLabel}>Oyun yüklenirken bir hata oluştu</Text>
            <Text style={styles.gameHudValue}>Lütfen sayfayı yenileyin</Text>
          </View>
        );
      }
      // Web'de null döndür (konsolda hata görünecek)
      return null;
    }
  };

  // Web'de font yüklenene kadar bekleme - direkt render et
  if (Platform.OS === 'web') {
    // Web'de font yükleme sorunları olabilir, yine de render et
    console.log('[Web ChatRoom] Rendering component, messages:', messages.length, 'isConnected:', isConnected, 'activeRoomId:', activeRoomId);
  } else if (!fontsLoaded && !fontsError) {
    // Mobil'de font yüklenene kadar loading göster
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text style={{ color: '#FFFFFF' }}>Yükleniyor...</Text>
      </View>
    );
  }

  if (Platform.OS === 'web') {
    console.log('[Web ChatRoom] About to render, socket:', socket ? 'exists' : 'null', 'messages count:', messages.length);
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={0}
      enabled={Platform.OS === 'ios'}
    >
      <View style={[
        styles.header,
        {
          paddingTop: Platform.OS === 'ios' ? 44 : (StatusBar.currentHeight || 24),
        },
      ]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            onPress={onClose}
            style={{
              width: 40,
              height: 40,
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: 8,
            }}
          >
            <Text style={{ fontSize: 24, color: '#ffffff' }}>✕</Text>
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <Text style={styles.radioName}>{radio.name}</Text>
            {room?.roomName ? (
              <Text style={styles.roomName}>{room.roomName}</Text>
            ) : null}
            <View style={styles.headerSubInfo}>
              <Text style={styles.participantCount}>
                {participants.length} dinleyici
                {isConnected ? ' • Bağlı' : ' • Bağlanıyor...'}
              </Text>
            </View>
          </View>
        </View>
      </View>

      <FlatList
        ref={flatListRef}
        data={messages}
        renderItem={renderMessage}
        keyExtractor={(item, index) => item.id || `${item.timestamp}-${index}`}
        style={styles.messagesList}
        contentContainerStyle={styles.messagesContent}
        ListHeaderComponent={isGameRoom ? renderGameHud : null}
        stickyHeaderIndices={isGameRoom ? [0] : undefined}
        onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: true })}
        keyboardShouldPersistTaps="handled"
      />
      
      {/* Batak Bid Floating Overlay - ChatRoom render'larından izole */}
      {isBatak && gameStateSnapshot?.currentPhase === 'BIDDING' && (() => {
        const normalizedBaseUserNameValue = normalizeDisplayName(userName);
        const currentUserNameValue = isGameContext ? sessionUserName : normalizedBaseUserNameValue;
        const players = gameStateSnapshot?.players || [];
        const currentPlayerIndex = typeof gameStateSnapshot?.currentPlayerIndex === 'number'
          ? gameStateSnapshot.currentPlayerIndex
          : null;
        const currentPlayer = typeof currentPlayerIndex === 'number' ? players[currentPlayerIndex] : null;
        const matchName = (a = '', b = '') => (
          a?.toLowerCase() === b?.toLowerCase() ||
          a?.toLowerCase().startsWith(b?.toLowerCase()) ||
          b?.toLowerCase().startsWith(a?.toLowerCase())
        );
        const isMyTurn = currentPlayer ? matchName(currentPlayer.name, currentUserNameValue) : false;
        const isMobileDevice = (Platform.OS === 'web' && isMobile) || (Platform.OS !== 'web');
        
        if (!isMyTurn) return null;
        
        return (
          <View style={styles.batakBidFloatingOverlay} pointerEvents="box-none">
            <View style={[
              styles.batakBidFloatingContainer,
              isMobileDevice && styles.batakBidFloatingContainerMobile,
            ]}>
              <Text style={[
                styles.batakBidOverlayTitle,
                isMobileDevice && styles.batakBidOverlayTitleMobile,
              ]}>Elin kaç alır</Text>
              <View style={styles.batakActionRow}>
                {Platform.OS === 'web' ? (
                  <BatakBidInput
                    key={`batak-bid-floating-${gameStateSnapshot?.round}-${isMyTurn}`}
                    inputRef={batakBidInputRef}
                    defaultValue={batakBidValue}
                    disabled={isSubmittingBatakMove}
                    onFocus={handleBatakInputFocus}
                    onBlur={handleBatakInputBlur}
                    onKeyDown={handleBatakInputKeyDown}
                    inputKey={`batak-bid-floating-${gameStateSnapshot?.round}`}
                  />
                ) : (
                  <TextInput
                    mode="outlined"
                    label="Teklif"
                    value={batakBidValue}
                    keyboardType="number-pad"
                    onChangeText={setBatakBidValue}
                    editable={!isSubmittingBatakMove}
                    disabled={isSubmittingBatakMove}
                    style={styles.batakBidInput}
                    dense
                  />
                )}
                <Button
                  mode="contained"
                  data-batak-submit-button
                  onPress={() => {
                    if (typeof handleSubmitBatakBid === 'function') {
                      handleSubmitBatakBid();
                    }
                  }}
                  disabled={isSubmittingBatakMove}
                  buttonColor="#E94560"
                >
                  Gönder
                </Button>
              </View>
            </View>
          </View>
        );
      })()}

      {recentReactions.length > 0 && (
        <View style={[styles.reactionOverlay, isEmojiPickerVisible && styles.reactionOverlayWithPicker]} pointerEvents="none">
          {recentReactions.map(reaction => (
            <View key={reaction.id} style={styles.reactionBubble}>
              {renderAvatarBadge(
                reaction.avatar,
                32,
                styles.reactionAvatar,
                styles.reactionAvatarLabel,
                reaction.userName?.[0] || '🙂'
              )}
              <Text style={styles.reactionEmoji}>{reaction.emoji}</Text>
              <Text style={styles.reactionUser}>{reaction.userName}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Pişti geri bildirimi */}
      {pistiNotification && (
        <Animated.View
          style={[
            styles.pistiNotificationOverlay,
            {
              opacity: pistiNotificationAnim,
              transform: [{ scale: pistiNotificationScale }],
            },
          ]}
          pointerEvents="none"
        >
          <View style={[
            styles.pistiNotificationBubble,
            pistiNotification.isMyPisti && styles.pistiNotificationBubbleMy,
          ]}>
            <Text style={styles.pistiNotificationText}>
              {pistiNotification.message}
            </Text>
          </View>
        </Animated.View>
      )}

      <View style={styles.inputWrapper}>
        <View style={styles.inputContainer}>
          {fontsLoaded ? (
            <IconButton
              icon={isEmojiPickerVisible ? 'keyboard-outline' : 'emoticon-outline'}
              size={26}
              iconColor="#FF4444"
              onPress={toggleEmojiPicker}
            />
          ) : (
            <TouchableOpacity
              onPress={toggleEmojiPicker}
              style={{ width: 40, height: 40, justifyContent: 'center', alignItems: 'center' }}
            >
              <Text style={{ fontSize: 20 }}>{isEmojiPickerVisible ? '⌨️' : '😊'}</Text>
            </TouchableOpacity>
          )}
          <TextInput
            ref={inputRef}
            style={styles.input}
            mode="flat"
            underlineColor="transparent"
            activeUnderlineColor="transparent"
            placeholder="Mesaj yazın..."
            value={messageText}
            onChangeText={setMessageText}
            multiline
            disabled={!isConnected}
            onFocus={() => setIsEmojiPickerVisible(false)}
          />
          {fontsLoaded ? (
            <IconButton
              icon="send"
              size={24}
              iconColor={messageText.trim() ? '#FF4444' : '#666666'}
              onPress={sendMessage}
              disabled={!messageText.trim() || !isConnected}
            />
          ) : (
            <TouchableOpacity
              onPress={sendMessage}
              disabled={!messageText.trim() || !isConnected}
              style={{
                width: 40,
                height: 40,
                justifyContent: 'center',
                alignItems: 'center',
                opacity: messageText.trim() ? 1 : 0.5,
              }}
            >
              <Text style={{ fontSize: 20 }}>➤</Text>
            </TouchableOpacity>
          )}
        </View>

        {isEmojiPickerVisible && (
          <View style={styles.emojiPickerContainer}>
            {EMOJI_LIST.map((emoji) => (
              <TouchableOpacity
                key={emoji}
                style={styles.emojiItem}
                activeOpacity={0.6}
                onPress={() => handleEmojiSelect(emoji)}
                onLongPress={() => handleEmojiLongPress(emoji)}
              >
                <Text style={styles.emojiPickerEmoji}>{emoji}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {/* Yapay Zeka Sorusu Modal (UNO, Pişti, Batak ve OKEY için) */}
      {(isUno || isPisti || isBatak || isOkey) && (
        <Modal
          visible={showAiQuestionModal && (gameStateSnapshot?.status === 'WAITING' || !gameStateSnapshot?.status)}
          transparent={true}
          animationType="fade"
          onShow={() => {
            // Modal gerçekten gösterildiğinde ref'i true yap
            if (isOkey) {
              hasAskedOkeyAiQuestionRef.current = true;
              if (Platform.OS === 'web') {
                console.log('[OKEY] Modal onShow: hasAskedOkeyAiQuestionRef.current = true');
              }
            } else {
              setHasAskedAiQuestion(true);
            }
          }}
          onRequestClose={() => {
            setShowAiQuestionModal(false);
            if (isOkey) {
              hasAskedOkeyAiQuestionRef.current = true;
            } else {
              setHasAskedAiQuestion(true);
            }
            // Pişti için modal ref'ini temizle
            if (isPisti) {
              pistiModalOpenedRef.current = false;
            }
          }}
          onShow={() => {
            // Modal gerçekten gösterildiğinde ref'i true yap
            if (isOkey) {
              hasAskedOkeyAiQuestionRef.current = true;
              if (Platform.OS === 'web') {
                console.log('[OKEY] Modal onShow: hasAskedOkeyAiQuestionRef.current = true');
              }
            } else {
              setHasAskedAiQuestion(true);
            }
            // Pişti için modal ref'ini kontrol et (zaten true olmalı ama emin olmak için)
            if (isPisti) {
              pistiModalOpenedRef.current = true;
            }
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Yapay Zekaya Karşı Oynar mısın?</Text>
              <Text style={styles.modalText}>
                Yapay zeka oyuncusu eklenerek oyun başlatılacak.
              </Text>
              <View style={styles.modalButtons}>
                <Button
                  mode="contained"
                  onPress={handleAddAiPlayer}
                  disabled={isAddingAiPlayer || (isPisti && pistiAiPlayerAddedRef.current)}
                  style={[styles.modalButton, { backgroundColor: '#E94560' }]}
                >
                  {isAddingAiPlayer ? 'Ekleniyor...' : 'Evet'}
                </Button>
                <Button
                  mode="outlined"
                  onPress={() => {
                    setShowAiQuestionModal(false);
                    const gameType = room?.gameType || (radio._id?.startsWith('game-') ? radio._id.replace('game-', '') : null);
                    const isOkeyGame = gameType === 'okey' || radio._id === 'game-okey' || room?.roomId?.startsWith('okey-');
                    if (isOkeyGame) {
                      hasAskedOkeyAiQuestionRef.current = true;
                    } else {
                      setHasAskedAiQuestion(true);
                    }
                    // Pişti için modal ref'ini temizle
                    if (isPisti) {
                      pistiModalOpenedRef.current = false;
                    }
                  }}
                  style={styles.modalButton}
                >
                  Hayır
                </Button>
              </View>
            </View>
          </View>
        </Modal>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#121212',
    ...(Platform.OS === 'web' ? {
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      alignSelf: 'stretch',
      paddingHorizontal: 0,
    } : {}),
  },
  header: {
    backgroundColor: '#1E1E1E',
    paddingBottom: Platform.OS === 'web' ? 16 : 12,
    paddingTop: Platform.OS === 'web' ? 16 : 0,
    paddingHorizontal: Platform.OS === 'web' ? 24 : 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#2C2C2C',
    ...(Platform.OS === 'web' ? {
      borderRadius: 0,
      marginTop: 0,
      marginHorizontal: 0,
      marginBottom: 0,
      boxShadow: 'none',
    } : {}),
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    marginLeft: 8,
  },
  radioName: {
    color: '#FF4444',
    fontSize: Platform.OS === 'web' ? 22 : 18,
    fontWeight: 'bold',
  },
  roomName: {
    color: '#E6EAF2',
    fontSize: Platform.OS === 'web' ? 16 : 14,
    marginTop: 2,
  },
  headerSubInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  participantCount: {
    color: '#B0B0B0',
    fontSize: 12,
    opacity: 0.9,
    flex: 1,
  },
  messagesList: {
    flex: 1,
  },
  messagesContent: {
    paddingVertical: Platform.OS === 'web' ? 16 : 12,
    paddingHorizontal: Platform.OS === 'web' ? 24 : 12,
    paddingBottom: Platform.OS === 'web' ? 160 : 140,
  },
  messageRow: {
    flexDirection: 'row',
    marginBottom: Platform.OS === 'web' ? 12 : 8,
    alignItems: 'flex-end',
  },
  messageRowOwn: {
    justifyContent: 'flex-end',
  },
  messageRowOther: {
    justifyContent: 'flex-start',
  },
  messageBubble: {
    maxWidth: Platform.OS === 'web' ? '70%' : '80%',
    borderRadius: Platform.OS === 'web' ? 20 : 16,
    paddingHorizontal: Platform.OS === 'web' ? 18 : 14,
    paddingVertical: Platform.OS === 'web' ? 12 : 8,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  messageBubbleOwn: {
    backgroundColor: '#2C2C2C',
    borderTopRightRadius: 4,
  },
  messageBubbleOther: {
    backgroundColor: '#1E1E1E',
    borderTopLeftRadius: 4,
  },
  messageUser: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF4444',
    marginBottom: 2,
  },
  messageText: {
    fontSize: Platform.OS === 'web' ? 16 : 15,
    lineHeight: Platform.OS === 'web' ? 24 : 20,
  },
  messageTextOwn: {
    color: '#FFFFFF',
  },
  messageTextOther: {
    color: '#FFFFFF',
  },
  timestamp: {
    fontSize: 11,
    marginTop: 4,
    textAlign: 'right',
  },
  timestampOwn: {
    color: '#B0B0B0',
  },
  timestampOther: {
    color: '#808080',
  },
  reactionOverlay: {
    position: 'absolute',
    bottom: 120,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  reactionOverlayWithPicker: {
    bottom: 220,
  },
  reactionBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: 24,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginHorizontal: 6,
    borderWidth: 1,
    borderColor: '#FF4444',
  },
  reactionAvatar: {
    backgroundColor: '#FF4444',
    marginRight: 8,
  },
  reactionAvatarLabel: {
    fontSize: 16,
  },
  pistiNotificationOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
      pointerEvents: 'none',
  },
  pistiNotificationBubble: {
    backgroundColor: 'rgba(233, 69, 96, 0.95)',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 30,
    shadowColor: '#E94560',
    shadowOpacity: 0.8,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
    borderWidth: 3,
    borderColor: '#FFFFFF',
  },
  pistiNotificationBubbleMy: {
    backgroundColor: 'rgba(76, 175, 80, 0.95)',
    shadowColor: '#4CAF50',
  },
  pistiNotificationText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  reactionEmoji: {
    fontSize: 20,
    marginRight: 6,
  },
  reactionUser: {
    color: '#FF4444',
    fontSize: 12,
    fontWeight: '600',
  },

  gameHudContainer: {
    backgroundColor: '#1E1E1E',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#FF4444',
    position: 'relative',
  },
  gameHudContainerMobile: {
    padding: 2,
    borderRadius: 6,
    marginBottom: 0, // Chat alanının görünmesi için
    marginTop: -18, // Header'ın altında kalmaması için minicik aşağı kaydırıldı
  },
  gameHudRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  gameHudColumn: {
    flex: 1,
  },
  gameHudLabel: {
    color: '#8C92A4',
    fontSize: 12,
  },
  gameHudLabelMobile: {
    fontSize: 8,
  },
  gameHudValue: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 4,
  },
  gameHudValueMobile: {
    fontSize: 9,
    marginTop: 0,
  },
  gameHudCategory: {
    flexWrap: 'wrap',
    flexShrink: 1,
  },
  gamePhaseBadge: {
    marginTop: 12,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#FF4444',
  },
  gamePhaseText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  gameLeaderboard: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
    gap: 8,
  },
  gameLeaderboardMobile: {
    marginTop: 0,
    gap: 2,
  },
  gameLeaderboardItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#2C2C2C',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 120,
  },
  gameLeaderboardItemMobile: {
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    minWidth: 70,
  },
  gameLeaderboardName: {
    color: '#FFFFFF',
    fontSize: 13,
    flex: 1,
    marginRight: 8,
  },
  gameLeaderboardNameMobile: {
    fontSize: 9,
    marginRight: 4,
  },
  gameLeaderboardScore: {
    color: '#FFDD57',
    fontWeight: '700',
    fontSize: 14,
  },
  gameLeaderboardScoreMobile: {
    fontSize: 10,
  },
  gameUsedWords: {
    marginTop: 12,
  },
  gameUsedWordsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  gameUsedWordChip: {
    backgroundColor: '#2C2C2C',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  gameUsedWordText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  gameUsedWordPlayer: {
    color: '#8C92A4',
    fontSize: 11,
  },
  pistiHandContainer: {
    marginTop: 4,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#2C2C2C',
  },
  pistiHandRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 3,
    marginTop: 3,
    justifyContent: 'center',
  },
  pistiCard: {
    width: Platform.OS === 'web' ? 60 : 42,
    height: Platform.OS === 'web' ? 84 : 59,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#E94560',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 4,
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
      transition: 'all 0.2s',
    } : {}),
  },
  pistiCardPlayable: {
    borderColor: '#E94560',
    backgroundColor: '#FFFFFF',
    ...(Platform.OS === 'web' ? {
      ':hover': {
        transform: 'translateY(-4px)',
        shadowOpacity: 0.5,
      },
    } : {}),
  },
  pistiCardDisabled: {
    opacity: 0.5,
    borderColor: '#666',
    backgroundColor: '#CCCCCC',
  },
  pistiCardValue: {
    fontSize: Platform.OS === 'web' ? 18 : 13,
    fontWeight: 'bold',
    color: '#1E1E1E',
    marginBottom: 1,
  },
  pistiCardSuit: {
    fontSize: Platform.OS === 'web' ? 24 : 17,
  },
  centerCardContainer: {
    backgroundColor: '#2C2C2C',
    borderRadius: 8,
    padding: 8,
    marginTop: 4,
    alignItems: 'center',
    minWidth: 80,
  },
  centerCardText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  pistiYerContainer: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2C2C2C',
  },
  pistiYerCards: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
    gap: 4,
  },
  pistiYerCard: {
    backgroundColor: '#2C2C2C',
    borderRadius: 6,
    padding: 6,
    minWidth: 50,
    alignItems: 'center',
  },
  pistiYerCardText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  pistiYerEmptyText: {
    color: '#8C92A4',
    fontSize: 14,
    fontStyle: 'italic',
    marginTop: 4,
  },
  pistiWaitText: {
    color: '#8C92A4',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  unoInfoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 12,
  },
  unoInfoRowMobile: {
    gap: 8,
    marginTop: 8,
  },
  unoColorBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  unoColorChip: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  unoColorText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  unoInfoItem: {
    minWidth: 80,
  },
  unoDiscardSection: {
    marginTop: 16,
  },
  unoDiscardSectionMobile: {
    marginTop: 10,
  },
  unoDiscardCard: {
    marginTop: 8,
    padding: 24,
    borderRadius: 18,
    alignSelf: 'flex-start',
    minWidth: 120,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 12,
    borderWidth: 3,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  unoDiscardCardMobile: {
    padding: 10,
    borderRadius: 10,
    minWidth: 75,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 6,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  unoDiscardCardWild: {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  unoDiscardLabel: {
    color: '#FFFFFF',
    fontSize: 32,
    fontWeight: '700',
    textAlign: 'center',
  },
  unoDiscardLabelMobile: {
    fontSize: 20,
  },
  unoDiscardSubtext: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
    marginTop: 4,
    opacity: 0.9,
  },
  unoDiscardSubtextMobile: {
    fontSize: 9,
    marginTop: 1,
  },
  unoHandContainer: {
    marginTop: 16,
  },
  unoHandContainerMobile: {
    marginTop: 10,
  },
  unoHandRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  unoHandRowMobile: {
    gap: 5,
    marginTop: 6,
  },
  unoCard: {
    minWidth: 65,
    width: 65,
    height: 95,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
    borderWidth: 2.5,
    borderColor: 'rgba(255, 255, 255, 0.25)',
  },
  unoCardMobile: {
    minWidth: 45,
    width: 45,
    height: 65,
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderRadius: 10,
    borderWidth: 1.5,
  },
  unoCardPlayable: {
    borderColor: 'rgba(255, 255, 255, 0.6)',
    borderWidth: 3,
    shadowColor: '#E94560',
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 10,
  },
  unoCardWild: {
    borderColor: 'rgba(255, 255, 255, 0.5)',
    borderWidth: 3.5,
    shadowColor: '#9E9E9E',
    shadowOpacity: 0.5,
  },
  unoCardLabel: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 24,
    textAlign: 'center',
  },
  unoCardLabelMobile: {
    fontSize: 16,
  },
  unoCardSubtext: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 10,
    marginTop: 2,
    opacity: 0.9,
    textAlign: 'center',
  },
  unoCardSubtextMobile: {
    fontSize: 8,
    marginTop: 1,
  },
  unoCardDisabled: {
    opacity: 0.4,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  unoCardBlockedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unoCardBlockedText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 'bold',
  },
  unoCardBlockedTextMobile: {
    fontSize: 16,
  },
  unoCurrentTurn: {
    color: '#E94560',
    fontWeight: 'bold',
  },
  unoActionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  unoActionRowMobile: {
    gap: 8,
    marginTop: 10,
  },
  unoActionButton: {
    flex: 1,
  },
  unoColorPicker: {
    marginTop: 16,
  },
  unoColorPickerMobile: {
    marginTop: 10,
  },
  unoColorPickerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  unoColorPickerRowMobile: {
    gap: 6,
    marginTop: 6,
  },
  unoColorOption: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  unoColorOptionMobile: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    minWidth: 65,
    borderWidth: 1.5,
  },
  unoColorOptionLabel: {
    color: '#0B0E14',
    fontWeight: '700',
    fontSize: 14,
  },
  unoColorOptionLabelMobile: {
    fontSize: 12,
  },
  unoColorCancel: {
    backgroundColor: '#2C3240',
  },
  unoColorCancelLabel: {
    color: '#FFFFFF',
  },
  unoInfoText: {
    color: '#FFFFFF',
    marginTop: 12,
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1E1E1E',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: '#FF4444',
  },
  modalTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalText: {
    color: '#8C92A4',
    fontSize: 14,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'center',
  },
  modalButton: {
    flex: 1,
    minWidth: 100,
  },
  pistiWaitText: {
    color: '#8C92A4',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  batakActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginTop: 8,
  },
  batakBidInput: {
    flex: 1,
    marginRight: 12,
  },
  batakBidInputWeb: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    minHeight: 48,
  },
  batakHint: {
    marginTop: 8,
    color: '#B0B7C3',
    fontSize: 13,
    textAlign: 'center',
  },
  batakHintMobile: {
    marginTop: 1,
    fontSize: 9,
  },
  batakBidFloatingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 120,
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  batakBidFloatingContainer: {
    backgroundColor: '#1A1D29',
    borderRadius: 16,
    padding: 24,
    minWidth: 320,
    maxWidth: '90%',
    borderWidth: 3,
    borderColor: '#E94560',
    shadowColor: '#E94560',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.8,
    shadowRadius: 20,
    elevation: 25,
  },
  batakBidFloatingContainerMobile: {
    padding: 16,
    minWidth: 280,
    maxWidth: '95%',
    borderRadius: 12,
  },
  batakBidOverlayTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  batakBidOverlayTitleMobile: {
    fontSize: 16,
    marginBottom: 12,
  },
  batakTable: {
    marginTop: 0,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#1F1F28',
    borderWidth: 1,
    borderColor: '#2F3647',
    position: 'relative',
  },
  batakTableMobile: {
    marginTop: 0,
    padding: 2,
    borderRadius: 4,
  },
  batakTableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  batakTableTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  batakTableTitleMobile: {
    fontSize: 11,
  },
  batakTableStatus: {
    color: '#8C92A4',
    fontSize: 12,
  },
  batakTableStatusMobile: {
    fontSize: 10,
  },
  batakTableCards: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  batakTableCardsMobile: {
    gap: 4,
  },
  batakTableCard: {
    width: 72,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#2A2F3B',
    alignItems: 'center',
  },
  batakTableCardMobile: {
    width: 40,
    paddingVertical: 0,
    borderRadius: 4,
    paddingTop: 2, // Sadece üstten minimal padding
  },
  batakTablePlayer: {
    color: '#C7D0E0',
    fontSize: 10,
    marginBottom: 0, // Oyuncu ismi ile kart arasındaki aralığı kaldır
  },
  batakTablePlayerMobile: {
    fontSize: 7,
    marginBottom: -2, // Oyuncu ismi ile kart arasındaki aralığı negatif margin ile kaldır
    lineHeight: 8, // Satır yüksekliğini daha da azalt
    paddingBottom: 0,
  },
  batakTableCardValue: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 0, // Oyuncu ismi ile kart değeri arasındaki aralığı kaldır
  },
  batakTableCardValueMobile: {
    fontSize: 10,
    marginTop: -2, // Oyuncu ismi ile kart değeri arasındaki aralığı negatif margin ile kaldır
    lineHeight: 11, // Satır yüksekliğini daha da azalt
    paddingTop: 0,
  },
  batakTableCardSuit: {
    fontSize: 16,
  },
  batakTableCardSuitMobile: {
    fontSize: 8,
  },
  batakTrumpOptions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    width: '100%',
  },
  batakTrumpOptionsMobile: {
    marginTop: 6,
    gap: 4,
  },
  batakTrumpOption: {
    flex: 1,
    marginHorizontal: 4,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444B5E',
    alignItems: 'center',
  },
  batakTrumpOptionMobile: {
    marginHorizontal: 2,
    paddingVertical: 6,
    borderRadius: 6,
  },
  batakTrumpOptionActive: {
    borderColor: '#E94560',
    backgroundColor: '#2A1F24',
  },
  batakTrumpOptionText: {
    color: '#C7D0E0',
    fontWeight: '600',
  },
  batakTrumpOptionTextMobile: {
    fontSize: 12,
  },
  batakTrumpOptionTextActive: {
    color: '#FFFFFF',
  },
  gameAnnouncement: {
    marginTop: 12,
    backgroundColor: '#2A1F1F',
    borderRadius: 12,
    padding: 10,
    borderWidth: 1,
    borderColor: '#FF7B7B',
    alignItems: 'center',
  },
  gameAnnouncementMobile: {
    marginTop: 2,
    padding: 4,
    borderRadius: 6,
  },
  gameAnnouncementText: {
    color: '#FFB0B0',
    fontSize: 13,
  },
  gameAnnouncementTextMobile: {
    fontSize: 12,
  },
  startGameButton: {
    marginTop: 12,
    minWidth: 150,
  },
  startGameButtonMobile: {
    marginTop: 8,
  },
  mysteryWordValueWrapper: {
    backgroundColor: '#20252F',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    alignSelf: 'flex-start',
  },
  mysteryWordValue: {
    letterSpacing: 2,
  },
  mysteryQuestionCard: {
    backgroundColor: '#1C2A2F',
    borderColor: '#27E0C0',
  },
  mysteryQuestionText: {
    color: '#00C2A8',
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2C2C2C',
    borderRadius: 25,
    paddingHorizontal: 8,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  input: {
    flex: 1,
    marginHorizontal: 8,
    maxHeight: 120,
    minHeight: 36,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 15,
  },
  inputWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#1E1E1E',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#2C2C2C',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: Platform.select({ ios: 20, android: 0, default: 0 }),
  },
  emojiPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    paddingVertical: 8,
    backgroundColor: '#2C2C2C',
    borderRadius: 14,
    marginTop: 8,
    paddingHorizontal: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
  },
  emojiItem: {
    width: '12.5%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
  },
  emojiPickerEmoji: {
    fontSize: 24,
  },
  messageAvatar: {
    marginRight: 8,
    backgroundColor: '#075E54',
  },
  messageAvatarLabel: {
    fontSize: 16,
  },
  messageAvatarOwn: {
    marginLeft: 8,
    backgroundColor: '#128C7E',
  },
  // OKEY stilleri
  okeyScoreBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#2A1F24',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444B5E',
  },
  okeyScoreText: {
    color: '#FFD700',
    fontSize: 10,
    fontWeight: 'bold',
  },
  okeyTileDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444B5E',
    minWidth: 60,
  },
  okeyTileDisplayMobile: {
    padding: 4,
    minWidth: 50,
  },
  okeyTileNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    marginRight: 4,
  },
  okeyTileNumberMobile: {
    fontSize: 14,
  },
  okeyTileColorDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  okeyTileColorDotMobile: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  okeyStockTile: {
    width: 50,
    height: 70,
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#444B5E',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'dashed',
  },
  okeyStockTileMobile: {
    width: 40,
    height: 56,
  },
  okeyStockTileText: {
    color: '#C7D0E0',
    fontSize: 12,
    fontWeight: 'bold',
  },
  okeyStockTileTextMobile: {
    fontSize: 10,
  },
  okeyDiscardArea: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  okeyDiscardTile: {
    alignItems: 'center',
  },
  okeyDiscardLabel: {
    fontSize: 10,
    color: '#C7D0E0',
    marginBottom: 4,
  },
  okeyDiscardLabelMobile: {
    fontSize: 8,
  },
  okeyTileSmall: {
    width: 40,
    height: 56,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#444B5E',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    padding: 4,
  },
  okeyTileSmallMobile: {
    width: 32,
    height: 45,
  },
  okeyTileNumberSmall: {
    fontSize: 14,
    fontWeight: 'bold',
    marginRight: 2,
  },
  okeyTileNumberSmallMobile: {
    fontSize: 11,
  },
  okeyTileColorDotSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  okeyTileColorDotSmallMobile: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  okeyHandContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#444B5E',
  },
  okeyHandContainerMobile: {
    marginTop: 8,
    paddingTop: 8,
  },
  okeyHandRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    justifyContent: 'flex-start',
  },
  okeyHandRowMobile: {
    gap: 2,
  },
  okeyTile: {
    width: 50,
    height: 70,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#444B5E',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    padding: 4,
  },
  okeyTileMobile: {
    width: 40,
    height: 56,
  },
  okeyTilePlayable: {
    borderColor: '#0FBF61',
    borderWidth: 2,
  },
  okeyTileSelected: {
    borderColor: '#FFD700',
    borderWidth: 3,
    transform: [{ scale: 1.1 }],
  },
  okeyTileGrouped: {
    borderColor: '#9370DB',
    borderWidth: 2,
    opacity: 0.8,
  },
  okeyGroupButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 8,
    alignSelf: 'center',
  },
  okeyGroupButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  okeyActionButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
    justifyContent: 'center',
  },
  okeyActionButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  },
  okeyActionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  // OKEY gerçekçi masa stilleri
  okeyTableContainer: {
    flex: 1,
    backgroundColor: '#1a4d2e', // Koyu yeşil arka plan
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  okeyTableContainerMobile: {
    borderRadius: 8,
  },
  okeyTableFelt: {
    flex: 1,
    backgroundColor: '#2d5a3d', // Yeşil çuha rengi
    borderRadius: 12,
    padding: 8,
    position: 'relative',
    minHeight: 250,
  },
  okeyTableFeltMobile: {
    padding: 4,
    minHeight: 200,
  },
  // Oyuncu panelleri (Üst, Sol, Sağ)
  okeyPlayerTop: {
    position: 'absolute',
    top: 8,
    left: '50%',
    transform: [{ translateX: -50 }],
    alignItems: 'center',
  },
  okeyPlayerLeft: {
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: [{ translateY: -50 }],
    alignItems: 'center',
  },
  okeyPlayerRight: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: [{ translateY: -50 }],
    alignItems: 'center',
  },
  okeyPlayerPanel: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 4,
    alignItems: 'center',
  },
  okeyPlayerName: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  okeyPlayerScore: {
    color: '#FFD700',
    fontSize: 10,
    marginTop: 2,
  },
  okeyPlayerRackClosed: {
    width: 60,
    height: 40,
    backgroundColor: '#8B4513', // Ahşap rengi
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#654321',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  okeyPlayerRackCount: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  okeyTurnIndicator: {
    marginTop: 4,
  },
  okeyTurnArrow: {
    color: '#0FBF61',
    fontSize: 16,
  },
  // Merkez alan
  okeyCenterArea: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -50 }, { translateY: -50 }],
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  okeyIndicatorTile: {
    alignItems: 'center',
  },
  okeyIndicatorLabel: {
    color: '#FFFFFF',
    fontSize: 10,
    marginTop: 4,
    fontWeight: 'bold',
  },
  okeyStockPile: {
    alignItems: 'center',
  },
  okeyStockTileBack: {
    width: 42,
    height: 58,
    backgroundColor: '#1A1A1A',
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#444B5E',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 4,
    elevation: 5,
  },
  okeyStockTileBackMobile: {
    width: 35,
    height: 48,
  },
  okeyStockCount: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  okeyStockCountMobile: {
    fontSize: 12,
  },
  okeyStockLabelMobile: {
    fontSize: 9,
  },
  okeyStockLabel: {
    color: '#FFFFFF',
    fontSize: 10,
    marginTop: 4,
    fontWeight: 'bold',
  },
  okeyDiscardPile: {
    alignItems: 'center',
  },
  okeyMyDiscardTile: {
    alignItems: 'center',
  },
  // Gerçekçi taş stilleri (ıstaka için küçültülmüş)
  okeyRealisticTile: {
    width: 30,
    height: 42,
    backgroundColor: '#F5F5DC', // Krem/beyaz
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#D3D3D3',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.25,
    shadowRadius: 2,
    elevation: 3,
    marginHorizontal: 2, // Taşlar arası boşluk (gruplandırma için)
  },
  okeyRealisticTileMobile: {
    width: 26,
    height: 36,
    marginHorizontal: 1.5,
  },
  okeyRealisticTileSmall: {
    width: 32,
    height: 44,
    backgroundColor: '#F5F5DC',
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#D3D3D3',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.25,
    shadowRadius: 2,
    elevation: 3,
  },
  okeyRealisticTileSmallMobile: {
    width: 26,
    height: 36,
  },
  okeyTileTouchable: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  okeyRealisticTileNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  okeyRealisticTileNumberMobile: {
    fontSize: 11,
  },
  okeyRealisticTileNumberSmall: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  okeyRealisticTileNumberSmallMobile: {
    fontSize: 11,
  },
  okeyRealisticTileColorCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  okeyRealisticTileColorCircleMobile: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  okeyRealisticTileColorCircleSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  okeyRealisticTileColorCircleSmallMobile: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  okeyTileDragging: {
    opacity: 0.6,
    transform: [{ scale: 1.1 }],
    zIndex: 1000,
  },
  // Alt kısım - Aktif oyuncu
  okeyMyPlayerArea: {
    backgroundColor: '#1a4d2e',
    padding: 8,
    borderTopWidth: 2,
    borderTopColor: '#2d5a3d',
    maxHeight: '45%',
  },
  okeyMyPlayerAreaMobile: {
    padding: 6,
    maxHeight: '50%',
  },
  okeyMyPlayerPanel: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  okeyMyPlayerName: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  okeyMyPlayerScore: {
    color: '#FFD700',
    fontSize: 12,
    fontWeight: 'bold',
  },
  okeyMyTurnBadge: {
    backgroundColor: '#0FBF61',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  okeyMyTurnText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
  },
  // Ahşap dokulu ıstaka
  okeyRackContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  okeyRackContainerMobile: {
    flexDirection: 'row',
  },
  okeyRack: {
    backgroundColor: '#8B4513', // Ahşap rengi
    borderRadius: 8,
    padding: 6,
    borderWidth: 3,
    borderColor: '#654321',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 6,
    elevation: 6,
    flex: 1,
  },
  okeyRackDiscardLeft: {
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  okeyRackDiscardRight: {
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  okeyPlayerDiscardRight: {
    marginTop: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  okeyPlayerDiscardCorner: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  okeyRackMobile: {
    padding: 4,
    borderRadius: 6,
  },
  okeyRackRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 1, // Taşlar arası boşluk azaltıldı (gruplandırma için)
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  okeyRackRowMobile: {
    gap: 0.5,
  },
  okeyWaitingArea: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  okeyWaitingText: {
    color: '#FFFFFF',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
  },
  okeyStartGameButton: {
    backgroundColor: '#0FBF61',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  okeyStartGameButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
});
